/**
 * `TwinShell` — focused project-package viewer layout.
 *
 * Replaces `<AppShell />` for the `/t/:gtwinId` route. AppShell is the
 * full GeoViewer Phase 2 layout with 10+ floating panels (search, copilot,
 * asset clusters, plugins, point-cloud controls, 3D toolbar, drawers...)
 * and it re-bootstraps the catalog from `CatalogClient.search()` on mount,
 * which **overrides** any layers TwinRoute hydrated from the gTwin scene
 * with `SAMPLE_LAYERS` fallback when the catalog backend is unreachable.
 *
 * TwinShell is deliberately minimal:
 *   - Map canvas (MapLibre adapter only — sufficient for basemaps + raster
 *     + vector overlays; sets up Cesium adapter behind the engine pill if
 *     the user explicitly switches).
 *   - Layer panel showing only the gTwin's scene layers.
 *   - Header with project label + "Open another project" link.
 *   - Bottom-center scene preset switcher (mounted by TwinRoute).
 *
 * Token-only styling. No raw hex.
 */

import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { ViewerHost, type EngineId, type Layer, type ViewerCamera } from "@geosoft/viewer-sdk";
import type { SceneProfileId } from "@/sdk-stub/viewer-sdk/types";
import {
  isLayerAllowedForSceneProfile,
  normalizeSceneProfile,
} from "@/sdk-stub/viewer-sdk/policy/sceneProfilePolicy";
import { MapLibreAdapter } from "@/engines/maplibre/MapLibreAdapter";
import { CanvasMapAdapter } from "@/engines/openlayers/CanvasMapAdapter";
import {
  loadPreferredBasemapId,
} from "@/engines/maplibre/basemaps";
import { getMapLibreHostHandle } from "@/engines/maplibre/hostHandle";
import { getCanvasMapHostHandle } from "@/engines/openlayers/hostHandle";
import { cameraHeightForMercatorZoom } from "@/engines/maplibre/overlayZoom";
import { CesiumAdapter } from "@/engines/cesium/CesiumAdapter";
import { ThreeAdapter } from "@/engines/three/ThreeAdapter";
import { DeckGLAdapter } from "@/engines/deckgl/DeckGLAdapter";
import { Giro3DAdapter } from "@/engines/giro3d/Giro3DAdapter";
import { PLATEAU_FEATURES } from "@/engines/giro3d/plateauFeatures";
import {
  buildLoRState,
  microscopeTargetDistance,
  telescopeTargetDistance,
  type LoRScopeLabels,
} from "@/engines/giro3d/lorController";
import { startLoRCrossfade } from "@/engines/giro3d/lorCrossfade";
import { LorBreadcrumb } from "@/components/three-d/LorBreadcrumb";
import { getRampPreset } from "@/components/raster/rasterSymbology";
import { LoRScopeButtons } from "@/components/three-d/LoRScopeButtons";
import { FloorSelector } from "@/components/three-d/FloorSelector";
import { DisciplineToggle } from "@/components/three-d/DisciplineToggle";
import {
  buildScopeState,
  collectStoreys,
  findFirstDrillableLoR3,
  resolveChildScope,
  resolveParentScope,
  type DisciplineId,
  type LoRScopeState,
} from "@/engines/giro3d/lorScopeManager";
import {
  comparePlateauLayerMountOrder,
  getDetailLayerPriority,
  isPlateauBasemapLayer,
  isPlateauDeferrableDetailLayer,
  isPlateauStickyMountLayer,
  nextPlateauStreetDetailState,
  partitionPlateauMountPhases,
  partitionSceneMountStages,
} from "@/engines/giro3d/plateauMountPolicy";
import {
  evaluateOnDemandTrigger,
  listDeferredCandidates,
} from "@/sdk-stub/viewer-sdk/policy/activationPolicy";
import {
  ALTERNATE_SWITCH_EVENT,
  type AlternateSwitchEvent,
} from "@/components/layers/alternateGroupEvents";
import { crossfadeBetweenLayers } from "@/engines/giro3d/alternateCrossfade";
import { PotreeAdapter } from "@/engines/potree/PotreeAdapter";
import type { PotreeAdapter as PotreeAdapterType } from "@/engines/potree/PotreeAdapter";
import { useViewerStore } from "@/state/useViewerStore";
import { useSceneHierarchyStore } from "@/state/sceneHierarchyStore";
import { usePluginStore, type EngineType } from "@/state/usePluginStore";
import { Banner, Chip, Icon, tokens, type ChipVariant, type IconName } from "@/design";
import {
  useUnifiedKeyboardShortcuts,
  VIEWER_SHORTCUT_EVENT,
  type ViewerShortcutDetail,
} from "@/state/keyboardShortcuts";
import { BasemapSwitcher } from "@/components/map/BasemapSwitcher";
import { BookmarkLocationControl } from "@/components/map/BookmarkLocationControl";
import { SavedViewsControl } from "@/components/map/SavedViewsControl";
import { MeasurementOverlay } from "@/components/map/MeasurementOverlay";
import { PixelInspector } from "@/components/map/PixelInspector";
import { MapLayoutWorkspace } from "@/components/map-layout/MapLayoutWorkspace";
import type { LayoutWorkspaceView } from "@/components/map-layout/layoutTypes";
import { findTerrainAnalysisParent, resolveTerrainDerivatives } from "@/components/terrain/terrainDerivedLayers";
import { terrainToolCardPatchForLayerVisibility } from "@/components/terrain/terrainToolCardVisibilitySync";
import {
  applyContourIntervalFilter,
  scheduleContourIntervalFilter,
} from "@/components/terrain/terrainContourFilter";
import { sceneItemToLayer } from "@/app/sceneItemToLayer";
import { gtwinClient } from "@/app/gtwinClientSingleton";
import type { SceneItem, SceneSlot } from "@/sdk-stub/gtwin-sdk";
import type { ObjectIndex } from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";
import type { LayerHierarchyRef } from "@/components/scene-tree/collectSceneHierarchies";
import { AddLayerToGTwinDialog } from "@/components/gtwin/AddLayerToGTwinDialog";
import { ExportGtwinAction } from "@/components/gtwin/ExportGtwinAction";
import { RealBackendBanner } from "@/components/gtwin/RealBackendBanner";
import { SyncStatusBadge } from "@/components/gtwin/SyncStatusBadge";
import { RelinkLayerDialog, type UnreachableLayer } from "@/components/gtwin/RelinkLayerDialog";
import {
  createDebouncedBatcher,
  useSyncStatusStore,
} from "@/state/useSyncStatusStore";
import type { SceneItemPatch } from "@/sdk-stub/gtwin-sdk";
import { LayerHealthDot } from "@/components/layers/LayerHealthDot";
import { SceneTreePanel } from "./SceneTreePanel";
import { SceneTreeV2Container } from "@/components/scene-tree/SceneTreeV2Container";
import { SceneTreeDropArea } from "@/components/layer-drop/SceneTreeDropArea";
import {
  buildPickContext,
  resolveGeometryPick,
  resolveSpatialPickFromSurveyPoint,
  resolveTreeNodeToGeometry,
  type PickContext,
} from "@/components/scene-tree/nodePickResolver";
import { ScenePresetSwitcher } from "./ScenePresetSwitcher";
import { isSyntheticBasemapLayer } from "./defaultBasemap";
import { activeRenderQualityProfile } from "@/engines/giro3d/renderQualityProfile";
import { withRasterSourceCrsOverride } from "@/utils/titilerSourceHost";
import { isSyntheticEnvironmentLayer, isEnvironmentLayer, EnvironmentEffectsPanel } from "@/modules/weather";
import { ToolCardDock } from "@/components/toolcards/ToolCardDock";
import { MissingCrsDialog } from "@/components/georeference/MissingCrsDialog";
import {
  layerShouldPromptMissingCrs,
  withGeoreferenceStyle,
} from "@/components/georeference/needsGeoreference";
import { LayerPropertiesPanel } from "@/components/layer-properties";
import { buildLayerDiagnostics } from "@/modules/layer-diagnostics";
import {
  buildThreeDObjectNodes,
  isThreeDLayer,
  type ThreeDObjectNode,
  type ThreeDPerformanceSnapshot,
} from "@/components/three-d/model";
import { ThreeDToolbar } from "@/components/three-d/ThreeDToolbar";
import { ThreeDCompass } from "@/components/three-d/ThreeDCompass";
import { ThreeDObjectTree } from "@/components/three-d/ThreeDObjectTree";
import { ThreeDControlsPanel } from "@/components/three-d/ThreeDControlsPanel";
import { ThreeDPerformanceChip } from "@/components/three-d/ThreeDPerformanceChip";
import { MissingGeometryDiagnostics } from "@/components/three-d/MissingGeometryDiagnostics";
import { ThreeDSelectionDetail } from "@/components/three-d/ThreeDSelectionDetail";
import { SelectionPropertyPanel } from "@/components/three-d/SelectionPropertyPanel";
import { TileLoadChip } from "@/components/three-d/TileLoadChip";
import { PlateauDetailDeferredChip } from "@/components/three-d/PlateauDetailDeferredChip";
import { PlateauZoomGateChip } from "@/components/three-d/PlateauZoomGateChip";
import { AnalysisStrip } from "@/components/three-d/AnalysisStrip";
import { DragHandle } from "@/components/layout/DragHandle";
import { useDockablePanel } from "@/state/useDockablePanel";
import { DEFAULT_CLIPPING_BOX } from "@/engines/giro3d/clipController";
import { selectionBus } from "@/state/selectionBus";
import type {
  SceneReadinessNotice,
  ScenePackageDocument,
  SimulationReadinessReport,
  UnifiedSceneTreeNode,
} from "./scenePackage";
import {
  hasExplicitGeospatialBounds,
  isFlyableGeographicBbox,
  resolveLayerFrameBbox,
  unionFlyableGeographicBboxes,
} from "./catalogFrameBbox";
import { isPlaceholderCamera } from "./cameraPlaceholder";
import {
  selectEngineForScene,
  type PotreeBackendStatusInput,
} from "./engineRouting";
import { evaluateFallback, type FallbackProposal } from "./engineFallback";
import {
  availableModesFor,
  nextEngineForMode,
  preserveState,
  type SceneMode,
} from "./sceneModeController";
import { PointCloudModeSwitch } from "@/components/pointcloud/PointCloudModeSwitch";
import { TerrainViewSwitch, type TerrainViewMode } from "@/components/map/TerrainViewSwitch";
import { shouldShowTerrainViewSwitch } from "@/components/map/terrainViewGate";
import {
  savePreferredEngine,
} from "@/components/map/EngineSelector";
import { PluginDock } from "@/components/plugins/PluginDock";
import { Giro3DToolbar } from "@/engines/giro3d/Giro3DToolbar";
import type { Giro3DAdapter as Giro3DAdapterType } from "@/engines/giro3d/Giro3DAdapter";
import { EmptyScene3DState } from "@/components/three-d/EmptyScene3DState";
import { LayerCrsChip } from "@/components/crs/LayerCrsChip";
import { LayerAlignmentAlertBanner } from "@/components/crs/LayerAlignmentAlertBanner";
import {
  AnalysisToolbar,
  SemanticPropertyCard,
  SimulationReadinessPanel,
  TwinGlobalSearch,
  TwinSavedViewsPanel,
  ViewerSettingsPanel,
} from "./TwinProductUx";
import { buildCatalogGroups } from "./sceneExplorer";
import {
  dispatchPanoramaBubbleSelect,
  selectByIndexNodeId,
  selectByLayerId,
  selectFromTreeNode,
  selectPanoramaBubbleLayer,
  selectPanoramaBubbleStation,
} from "./sceneSelection";
// Panorama-tour module is plugin-gated. Heavy interactive components
// are lazy-loaded behind React.lazy so the main bundle stays slim when
// the feature is disabled; cheap pure helpers (manifest loader,
// continuity helpers) stay eager because they're tiny. The
// isPanoramaTourEnabled() check at the mount sites is what actually
// prevents the lazy chunks from being fetched at all.
import { isPanoramaTourEnabled } from "@/engines/panorama-bubble/manifest";
const PanoramaBubbleView = lazy(() =>
  import("@/engines/panorama-bubble/PanoramaBubbleView").then((m) => ({
    default: m.PanoramaBubbleView,
  })),
);
const StationMarkers = lazy(() =>
  import("@/engines/panorama-bubble/StationMarkers").then((m) => ({
    default: m.StationMarkers,
  })),
);
import { loadStations } from "@/engines/panorama-bubble/manifestLoader";
import {
  captureGiro3DCameraState,
  restoreGiro3DCameraState,
  type SavedGiro3DCameraState,
} from "@/engines/panorama-bubble/panoramaCameraContinuity";
import type { PanoramaBubbleStationsManifest } from "@geosoft/converter-viewer-handoff";
import {
  applyPanoramaBubbleTreeSelection,
  enrichSceneTreeWithPanoramaBubbles,
  isPanoramaBubbleLayer,
  readPanoramaBubbleStationsFromLayer,
  type PanoramaBubbleStationRef,
} from "./sceneTreePanoramaBubble";

/** Human labels for scene slots, used by the Layer Properties group readout. */
const SLOT_LABELS: Record<string, string> = {
  basemaps: "Basemaps",
  terrain: "Terrain",
  realityData: "Reality Data",
  engineeringModels: "Engineering Models",
  featureLayers: "Feature Layers",
  overlays: "Overlays",
  environment: "Environment",
};

function engineToPluginEngineType(engine: EngineId): EngineType {
  switch (engine) {
    case "maplibre":
    case "openlayers":
      return "maplibre";
    case "cesium":
      return "cesium";
    case "three":
      return "three";
    case "deckgl":
      return "deck";
    case "giro3d":
    case "potree":
    default:
      return null;
  }
}

function layerCoordinateMode(layer: Layer): string {
  return typeof layer.style?.coordinateMode === "string"
    ? layer.style.coordinateMode
    : "georeferenced";
}

function isLocalPointCloudModelLayer(layer: Layer, slot?: string): boolean {
  return (
    layer.kind === "pointcloud" &&
    slot === "realityData" &&
    layerCoordinateMode(layer) !== "georeferenced"
  );
}

function needsGeospatialMapAdapter(layers: readonly Layer[]): boolean {
  return layers.some((layer) => {
    if (layerCoordinateMode(layer) !== "georeferenced") return false;
    return layer.kind === "raster" || layer.kind === "vector" || layer.kind === "terrain";
  });
}

function estimateScaleDenominator(
  zoom: number,
  latitudeDeg: number,
): number | null {
  if (!Number.isFinite(zoom) || !Number.isFinite(latitudeDeg)) return null;
  const latitudeRad = (latitudeDeg * Math.PI) / 180;
  const metersPerPixel =
    (156543.03392804097 * Math.cos(latitudeRad)) / Math.pow(2, zoom);
  if (!Number.isFinite(metersPerPixel) || metersPerPixel <= 0) return null;
  return metersPerPixel / 0.0002645833333;
}

/** Fixed Web-Mercator zoom used by the scene-tree "Zoom to layer" action. */
const ZOOM_TO_LAYER_ZOOM = 14;

function geographicCenterFromBbox(
  bbox: readonly number[] | null | undefined,
): readonly [number, number] | null {
  if (!bbox || bbox.length < 4) return null;
  const [minX, minY, maxX, maxY] = bbox;
  if (![minX, minY, maxX, maxY].every((n) => Number.isFinite(n))) return null;
  if (maxX <= minX || maxY <= minY) return null;
  const looksGeographic =
    Math.abs(minX) <= 180 && Math.abs(maxX) <= 180 &&
    Math.abs(minY) <= 90 && Math.abs(maxY) <= 90;
  if (!looksGeographic) return null;
  const lonSpan = Math.abs(maxX - minX);
  const latSpan = Math.abs(maxY - minY);
  if (lonSpan >= 359 || latSpan >= 169) return null;
  return [(minX + maxX) / 2, (minY + maxY) / 2];
}

function cameraSignature(camera: ViewerCamera | null | undefined): string {
  if (!camera) return "none";
  const [lon = 0, lat = 0] = camera.lonLat ?? [0, 0];
  const zoom = camera.zoom ?? Number.NaN;
  const hpr = camera.hpr?.join(",") ?? "";
  return `${lon.toFixed(8)}|${lat.toFixed(8)}|${Number.isFinite(zoom) ? zoom.toFixed(4) : "nan"}|${hpr}`;
}

async function readPmtilesArchiveBounds(
  url: string,
): Promise<readonly [number, number, number, number] | null> {
  try {
    const { PMTiles } = await import("pmtiles");
    const header = await new PMTiles(url).getHeader();
    const bbox: [number, number, number, number] = [
      header.minLon,
      header.minLat,
      header.maxLon,
      header.maxLat,
    ];
    if (
      bbox.every((value) => Number.isFinite(value)) &&
      bbox[0] >= -180 &&
      bbox[2] <= 180 &&
      bbox[1] >= -90 &&
      bbox[3] <= 90 &&
      bbox[2] > bbox[0] &&
      bbox[3] > bbox[1]
    ) {
      return bbox;
    }
  } catch (err) {
    console.warn("[TwinShell] PMTiles bounds read failed", url, err);
  }
  return null;
}

/**
 * WP-12 — `?legacy-toolbar=1` flag. When present, TwinShell keeps the
 * legacy floating overlays (`BasemapSwitcher`, `SavedViewsControl`,
 * `MeasurementOverlay`, `PixelInspector`) visible alongside the new
 * right-rail `<ToolCardDock />`. By default the floating overlays
 * are hidden in TwinShell because the canonical surface is now the
 * dock; the developer-fallback `/demo/*` routes continue to render
 * them via their own components.
 */
/**
 * Responsive layout breakpoints. Returns a tier that the shell grid can use
 * to collapse or hide the left/right rails at narrow viewports.
 *
 *   'full'      — both rails visible at full width  (≥ 1280 px)
 *   'compact'   — left rail collapses to icon-only  (1024–1279 px)
 *   'narrow'    — left rail hidden, right rail stays (768–1023 px)
 *   'mobile'    — single column, rails hidden        (< 768 px)
 */
type LayoutTier = 'full' | 'compact' | 'narrow' | 'mobile';

function useLayoutTier(): LayoutTier {
  const [tier, setTier] = useState<LayoutTier>(() => classifyWidth(typeof window !== 'undefined' ? window.innerWidth : 1920));
 
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setTier(classifyWidth(window.innerWidth));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return tier;
}

function classifyWidth(w: number): LayoutTier {
  if (w >= 1280) return 'full';
  if (w >= 1024) return 'compact';
  if (w >= 768) return 'narrow';
  return 'mobile';
}

function useLegacyToolbarFlag(): boolean {
  const [enabled, setEnabled] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return new URLSearchParams(window.location.search).has("legacy-toolbar");
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onPopState = () => {
      try {
        setEnabled(
          new URLSearchParams(window.location.search).has("legacy-toolbar"),
        );
      } catch {
        setEnabled(false);
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);
  return enabled;
}

export interface TwinShellProps {
  gtwinId: string;
  projectLabel: string;
  shellMode?: 'standalone' | 'embedded-estate';
  sceneProfile?: SceneProfileId;
  sceneTree?: UnifiedSceneTreeNode;
  sceneNotices?: readonly SceneReadinessNotice[];
  readinessReport?: SimulationReadinessReport | null;
  /**
   * Wave-4 / A8 — the resolved scene package, when one is present in the
   * gTwin envelope. Used by the engine routing helper to choose between
   * Giro3D and Potree for point-cloud-only scenes, and by the fallback
   * supervisor to surface the one-click Potree switch when a COPC layer
   * reports `unsupported`. Optional / nullable so the legacy direct-slot
   * payload path (no scene package) preserves its pre-A8 behavior.
   */
  scenePackage?: ScenePackageDocument | null;
  /** True when the gTwin payload has external packageRef/indexRef that should be promoted to inline. */
  hasExternalRefs?: boolean;
  /** RFC-112 Phase E: project frame CRS (default EPSG:3857) */
  projectFrameCrs?: string;
  /** RFC-112 Phase E: project scale mode (default visual) */
  projectScaleMode?: 'visual' | 'engineering';
  /**
   * INIT-2026-Q3-SCENE-HIERARCHY Block 3 — embedded Object Index from the
   * loaded gTwin record (`payload.geospatial.objectIndex`).
   * When present, SceneTreeV2Container builds its manifest from this index
   * (real load path) instead of from the unified tree.
   * Optional — null/absent for all normal gTwins (backward compatible).
   */
  sceneObjectIndex?: ObjectIndex | null;
  /**
   * INIT-2026-Q3-SCENE-HIERARCHY close-out C2 — large-model delivery by reference.
   * A `SceneItem.hierarchyUrl` from the loaded gTwin's scene items. When present
   * and `sceneObjectIndex` is absent, SceneTreeV2Container fetches the
   * `object-index.json` from this URL instead of relying on an inline payload.
   * Optional — null/absent for inline/legacy gTwins (backward compatible).
   */
  sceneHierarchyUrl?: string | null;
  /**
   * G18 — initial camera from envelope.view. When present, the store camera is
   * set on first render and the default flyToBbox heuristic is suppressed.
   * URL-state deep links (RFC-102) win over this when explicitly present.
   */
  initialCamera?: ViewerCamera | null;
  /**
   * G18 — persisted saved views from envelope.view. Hydrated into the store
   * on first render so named views survive page reloads.
   */
  initialSavedViews?: Array<{
    id: string;
    name: string;
    camera: ViewerCamera | null;
    visibleLayerIds: string[];
    createdAt: string;
    kind?: 'bookmark' | 'view';
  }>;
  /**
   * INIT-2026-Q3-SCENE-HIERARCHY multi-layer V1 — all per-layer hierarchy refs.
   * One entry per CityGML theme layer that carries a `hierarchyUrl`. When present,
   * SceneTreeV2Container loads all N hierarchies and builds a grouped manifest.
   */
  sceneHierarchies?: LayerHierarchyRef[];
}

function effectiveLayerForSession(
  layer: Layer,
  override: { sourceCrs: string | null; displayCrs: string | null; displayCrsProj4: string | null; vectorSessionFeaturesUrl?: string | null } | null | undefined,
): Layer {
  if (!override) return layer;
  const next: Layer = {
    ...layer,
    ...(override.sourceCrs
      ? {
          crs: override.sourceCrs,
          sourceCrs: override.sourceCrs,
          sourceCrsOverride: override.sourceCrs,
        }
      : {}),
    ...(override.displayCrs
      ? {
          displayCrs: override.displayCrs,
          displayCrsProj4: override.displayCrsProj4 ?? undefined,
        }
      : {}),
  };
  if (next.kind === 'vector' && override.vectorSessionFeaturesUrl) {
    next.endpoints = {
      ...next.endpoints,
      features: override.vectorSessionFeaturesUrl,
      pmtiles: undefined,
    };
  }
  if (next.kind === 'raster' || next.kind === 'terrain') {
    next.endpoints = {
      ...next.endpoints,
      ...(next.endpoints.tiles
        ? { tiles: withRasterSourceCrsOverride(next.endpoints.tiles, override.sourceCrs) }
        : {}),
      ...(next.endpoints.terrain
        ? { terrain: withRasterSourceCrsOverride(next.endpoints.terrain, override.sourceCrs) }
        : {}),
    };
  }
  return next;
}

export function TwinShell({
  gtwinId,
  projectLabel,
  shellMode = 'standalone',
  sceneProfile = 'hybrid',
  sceneTree,
  sceneNotices = [],
  readinessReport = null,
  scenePackage = null,
  hasExternalRefs = false,
  projectFrameCrs = 'EPSG:3857',
  projectScaleMode = 'visual',
  sceneObjectIndex = null,
  sceneHierarchyUrl = null,
  sceneHierarchies = [],
  initialCamera = null,
  initialSavedViews,
}: TwinShellProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const hostRef = useRef<ViewerHost | null>(null);
  // Ref so the Giro3D pick handler (a stale closure) always reads the current
  // object-index without being recreated on every render.
  const sceneObjectIndexRef = useRef<import("@/sdk-stub/gtwin-sdk/scene-hierarchy-types").ObjectIndex | null>(sceneObjectIndex);
  // INIT-2026-Q3-SCENE-HIERARCHY Block 2, Task E — bidirectional 3D ⇄ tree.
  // `pickContextRef` holds the latest PickContext published by the
  // SceneTreeV2Container; `triggerPickRef` holds the container's geometry-pick
  // entry point. Both are populated via `onPickContextReady` and read from the
  // engine pick path / tree-selection effect without recreating closures.
  const pickContextRef = useRef<PickContext | null>(null);
  const [pickContext, setPickContext] = useState<PickContext | null>(null);
  const triggerPickRef = useRef<((hitName: string) => void) | null>(null);
  const layerSyncGenerationRef = useRef(0);
  const initialFlyDoneRef = useRef(false);
  /** Soft-VM / Canvas: coalesce camera store writes so pan does not re-render TwinShell. */
  const engineRef = useRef<EngineId>("maplibre");
  const canvasCameraTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingCanvasCameraRef = useRef<ViewerCamera | null>(null);

  /** Bumped when ViewerHost finishes mounting so layer sync re-runs if data arrived first. */
  const [mapHostGeneration, setMapHostGeneration] = useState(0);
  /** DOM host for PixelInspector — synced from the ref when the map remounts. */
  const [mapHostEl, setMapHostEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setMapHostEl(mapContainer.current);
  }, [mapHostGeneration]);
  const [engine, setEngine] = useState<EngineId>(() => "openlayers");
  engineRef.current = engine;
  const [engineMountError, setEngineMountError] = useState<string | null>(null);
  const activeBasemapId = useViewerStore((s) => s.basemapToolCard.activeBasemapId);
  const setBasemapToolCard = useViewerStore((s) => s.setBasemapToolCard);

  // Keep store in sync with preferred basemap (localStorage) on first paint.
  useEffect(() => {
    const preferred = loadPreferredBasemapId();
    if (preferred !== activeBasemapId) {
      setBasemapToolCard({ activeBasemapId: preferred });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once on mount
  }, []);

  const onEngineChange = useCallback(
    (next: EngineId) => {
      savePreferredEngine(gtwinId, next);
      if (typeof window !== "undefined") {
        try {
          if (next === "openlayers") {
            window.sessionStorage.setItem("geoviewer.canvasFallback", "1");
          } else {
            window.sessionStorage.removeItem("geoviewer.canvasFallback");
          }
        } catch {
          /* ignore */
        }
      }
      setEngine(next);
      setEngineMountError(null);
    },
    [gtwinId],
  );

  // Viewer store hooks — declared before any callback that closes over them so
  // useCallback dependency arrays compile cleanly.
  const setCamera = useViewerStore((s) => s.setCamera);
  const setSelection = useViewerStore((s) => s.setSelection);
  const setPluginEngineType = usePluginStore((s) => s.setEngineType);
  const setPluginEngineReady = usePluginStore((s) => s.setEngineReady);
  const measureVertices = usePluginStore(
    (s) => s.transient.measurement.vertices,
  );
  const camera = useViewerStore((s) => s.camera);
  const lastHostCameraSignatureRef = useRef<string | null>(null);
  const lastAppliedCameraSignatureRef = useRef<string | null>(null);
  /** Mount generation whose host already received the store camera. */
  const appliedCameraGenerationRef = useRef<number | null>(null);
  const [cursorCoords, setCursorCoords] = useState<[number, number] | null>(
    null,
  );
  const [mapBearingDeg, setMapBearingDeg] = useState<number | null>(null);
  const [mapScaleDenominator, setMapScaleDenominator] = useState<number | null>(
    null,
  );
  const [layoutWorkspaceView, setLayoutWorkspaceView] =
    useState<LayoutWorkspaceView>("map");

  // Track mouse position on the MapLibre canvas for live cursor coordinates.
  useEffect(() => {
    if (!mapContainer.current) return;
    const map = getMapLibreHostHandle(mapContainer.current);
    if (!map) return;
    const onMouseMove = (event: { lngLat: { lng: number; lat: number } }) => {
      setCursorCoords([event.lngLat.lng, event.lngLat.lat]);
    };
    const onMouseLeave = () => setCursorCoords(null);
    const updateMapReadouts = () => {
      try {
        const center = map.getCenter();
        setMapBearingDeg(map.getBearing());
        setMapScaleDenominator(
          estimateScaleDenominator(map.getZoom(), center.lat),
        );
      } catch {
        setMapBearingDeg(null);
        setMapScaleDenominator(null);
      }
    };
    map.on("mousemove", onMouseMove);
    map.on("mouseout", onMouseLeave);
    map.on("move", updateMapReadouts);
    updateMapReadouts();
    return () => {
      try {
        map.off("mousemove", onMouseMove);
        map.off("mouseout", onMouseLeave);
        map.off("move", updateMapReadouts);
      } catch {
        /* map may be torn down */
      }
    };
  }, [mapHostGeneration]);

  // Giro3D cursor coordinates effect is placed further down, after the
  // `giro3dAdapter` useState binding, to avoid a temporal dead zone error.
  // Subscribe to PRIMITIVE state — never to derived array selectors directly,
  // since each call returns a new array reference and Zustand's strict-equal
  // store-instance check would re-render every commit -> infinite loop.
  const catalog = useViewerStore((s) => s.catalog);
  const unsupportedFormatLayers = useMemo(
    () =>
      catalog.filter((layer) => {
        const reason = layer.style?.viewerUnsupportedReason;
        return typeof reason === "string" && reason.length > 0;
      }),
    [catalog],
  );
  const presets = useViewerStore((s) => s.presets);
  const activePreset = useViewerStore((s) => s.activePreset);
  const slotMap = useViewerStore((s) => s.slotMap);
  const layerCoordinateOverrides = useViewerStore((s) => s.layerCoordinateOverrides);
  const setLayerCoordinateOverride = useViewerStore((s) => s.setLayerCoordinateOverride);
  const clearLayerCoordinateOverride = useViewerStore((s) => s.clearLayerCoordinateOverride);
  const setVisible = useViewerStore((s) => s.setVisible);
  const setManyVisible = useViewerStore((s) => s.setManyVisible);
  const soloVisibleLayer = useViewerStore((s) => s.soloLayer);
  const removeLayer = useViewerStore((s) => s.removeLayer);
  const visibleIds = useViewerStore((s) => s.visibleLayerIds);
  const rasterToolCard = useViewerStore((s) => s.rasterToolCard);
  const setCatalogStore = useViewerStore((s) => s.setCatalog);
  const patchCatalogLayer = useViewerStore((s) => s.patchCatalogLayer);
  const setLayerSlot = useViewerStore((s) => s.setLayerSlot);
  const sceneSelection = useViewerStore((s) => s.sceneSelection);
  const setSceneSelection = useViewerStore((s) => s.setSceneSelection);
  const clearSceneSelection = useViewerStore((s) => s.clearSceneSelection);
  const pixelInspectorLayer = useMemo(() => {
    const selectedId = sceneSelection.layerId ?? sceneSelection.sceneItemId;
    const selected = selectedId
      ? catalog.find((layer) => layer.id === selectedId)
      : undefined;
    const layer =
      selected?.kind === "raster" || selected?.kind === "terrain"
        ? selected
        : catalog.find(
            (candidate) =>
              (candidate.kind === "raster" || candidate.kind === "terrain")
              && (
                typeof candidate.endpoints.cog === "string"
                || (
                  typeof candidate.endpoints.tiles === "string"
                  && candidate.endpoints.tiles.includes("/cog/")
                )
              ),
          );
    if (!layer) return null;
    const tileUrl = layer.endpoints.tiles;
    const match = tileUrl?.match(/[?&]url=([^&]+)/);
    const cogUrl =
      layer.endpoints.cog
      ?? (match ? decodeURIComponent(match[1]) : null);
    if (!cogUrl) return null;
    const label = layer.label ?? layer.id;
    return {
      cogUrl,
      label,
      elevationLike:
        layer.kind === "terrain"
        || /(^|[^a-z])(dem|dtm|dsm|elevation|terrain)([^a-z]|$)/i.test(label),
    };
  }, [catalog, sceneSelection.layerId, sceneSelection.sceneItemId]);
  const sceneHierarchyV2Enabled = useSceneHierarchyStore((s) => s.featureFlagEnabled);
  // Keep the object-index ref in sync with the prop so the pick handler
  // (stale closure inside the host.on subscription) always gets the current value.
  sceneObjectIndexRef.current = sceneObjectIndex ?? null;
  const panoramaBubbleView = useViewerStore((s) => s.panoramaBubbleView);
  const setPanoramaBubbleView = useViewerStore((s) => s.setPanoramaBubbleView);
  // Camera-continuity: snapshot the Giro3D camera (position + orbit target +
  // yaw) when the user enters the bubble overlay from the 3D scene, so
  // closing the overlay returns them to the exact same view they were
  // looking at. `panoramaInitialYawRad` is forwarded to the bubble view
  // once per entry to seed its look-around controller with the captured
  // 3D-scene yaw; `null` means "don't override" (e.g. for the auto-open
  // case where there is no parent 3D scene to inherit from).
  const savedCameraStateRef = useRef<SavedGiro3DCameraState | null>(null);
  const [panoramaInitialYawRad, setPanoramaInitialYawRad] = useState<number | null>(null);
  const selection = useViewerStore((s) => s.selection);
  const threeDCameraMode = useViewerStore((s) => s.threeDCameraMode);
  const setThreeDCameraMode = useViewerStore((s) => s.setThreeDCameraMode);
  const threeDCanvasTone = useViewerStore((s) => s.threeDCanvasTone);
  const setThreeDCanvasTone = useViewerStore((s) => s.setThreeDCanvasTone);
  const threeDExplodeFactor = useViewerStore((s) => s.threeDExplodeFactor);
  const setThreeDExplodeFactor = useViewerStore((s) => s.setThreeDExplodeFactor);
  const setPointCloudColorMode = useViewerStore((s) => s.setPointCloudColorMode);
  const setPointCloudPointSize = useViewerStore((s) => s.setPointCloudPointSize);
  const setPointCloudClassificationFilter = useViewerStore(
    (s) => s.setPointCloudClassificationFilter,
  );

  // WP-6 — 3D model tool state. Mirrors `AppShell` defaults so the same
  // toolset is available under the gTwin Twin route. Controls panel
  // owns: isolate / hide / ghost / section plane / clip box / exploded
  // view / lighting / shadows / sun position / material state / canvas
  // tone. Performance chip + missing-geometry diagnostics complete the
  // legacy WP-6 set.
  const [threeDControlState, setThreeDControlState] = useState({
    isolated: false,
    hidden: false,
    ghosted: false,
    sectionPlaneEnabled: false,
    clipBoxEnabled: false,
    lightingEnabled: true,
    shadowsEnabled: true,
    sunHour: 12,
    sectionOffset: 0,
    clipOffset: 0,
    materialState: "textured" as "textured" | "untextured" | "missing-texture",
  });
  /**
   * Live performance snapshot driven by a rAF loop. Without this effect the
   * status-bar chip stayed pinned at the initial "60 FPS / LOD balanced"
   * placeholder forever; the chip now reflects actual measured frame rate
   * and a coarse LOD label derived from camera distance.
   */
  const [threeDPerformance, setThreeDPerformance] = useState<ThreeDPerformanceSnapshot>({
    fps: 60,
    lod: "balanced",
    pointBudget: 0,
    droppedFrames: 0,
  });
  useEffect(() => {
    // 2D engines (MapLibre / Canvas OpenLayers) do not need a permanent rAF
    // sampler — it keeps the main thread warm and fights tile decode on
    // GPU-less VMs. Only sample while a real 3D engine is active.
    if (
      engine !== "giro3d" &&
      engine !== "three" &&
      engine !== "cesium" &&
      engine !== "potree" &&
      engine !== "deckgl"
    ) {
      return;
    }
    let frames = 0;
    let droppedFrames = 0;
    let lastSampleTs = performance.now();
    let lastFrameTs = lastSampleTs;
    let rafHandle = 0;
    const tick = (ts: number) => {
      frames += 1;
      const delta = ts - lastFrameTs;
      // A frame longer than ~33ms (~30fps) is treated as "dropped" so the
      // status chip can surface jank during heavy tile streaming.
      if (delta > 33) droppedFrames += 1;
      lastFrameTs = ts;
      if (ts - lastSampleTs >= 1000) {
        const fps = (frames * 1000) / (ts - lastSampleTs);
        setThreeDPerformance((prev) => ({
          ...prev,
          fps,
          droppedFrames,
        }));
        frames = 0;
        droppedFrames = 0;
        lastSampleTs = ts;
      }
      rafHandle = window.requestAnimationFrame(tick);
    };
    rafHandle = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(rafHandle);
  }, [engine]);

  // Wave-4 / A8 — Potree adapter handle. Held so the engine routing helper
  // can read the adapter's status snapshot when deciding whether to auto
  // route a point-cloud-only scene to Potree. The instance is owned by the
  // ViewerHost effect below; this slot is updated synchronously whenever the
  // host is mounted with the Potree adapter so the routing decision settles
  // before the first frame.
  const [potreeAdapter, setPotreeAdapter] = useState<PotreeAdapterType | null>(
    null,
  );
  const [potreeStatus, setPotreeStatus] = useState<PotreeBackendStatusInput>(
    "not-installed",
  );

  // Auto-select the best engine based on layer kinds + scene-package routing
  // hints when no user preference is saved. Runs once when catalog first
  // populates. Wave-4 / A8: when the scene package has point-cloud routing
  // hints, the giro3d-vs-potree decision is delegated to
  // `selectEngineForScene`. When the scene has no point-cloud items the
  // pre-A8 MapLibre / Giro3D heuristic is preserved verbatim (G8).
  const [engineAutoSelected, setEngineAutoSelected] = useState(false);
  useEffect(() => {
    if (engineAutoSelected || catalog.length === 0) return;
    // OpenLayers is the deliberate default — do not auto-switch to a WebGL
    // engine before the user explicitly asks for 3D.
    if (engine === "openlayers") {
      setEngineAutoSelected(true);
      return;
    }
    const saved =
      typeof window !== "undefined"
        ? window.localStorage.getItem(`geoviewer.engine.${gtwinId}`)
        : null;
    if (saved) {
      setEngineAutoSelected(true);
      return;
    }

    const kinds = new Set(catalog.map((l) => l.kind));
    const needs3D =
      kinds.has("3dtiles") ||
      kinds.has("bim") ||
      kinds.has("terrain") ||
      kinds.has("pointcloud");

    let best: EngineId = "maplibre";
    if (needs3D) {
      const routed = selectEngineForScene(scenePackage, { potreeStatus });
      best = routed.engine === "potree" ? "potree" : "giro3d";
    }

    if (best !== engine) {
      setEngine(best);
      savePreferredEngine(gtwinId, best);
    }
    setEngineAutoSelected(true);
  }, [catalog, engine, engineAutoSelected, gtwinId, potreeStatus, scenePackage]);

  const [layerOpacity, setLayerOpacity] = useState<Record<string, number>>({});
  const [rasterPaint, setRasterPaint] = useState<
    Record<string, Record<string, number>>
  >({});
  /**
   * Wave-1 A7 — coarse layer-health snapshot for the SceneTreePanel dots.
   * Filled by a low-rate poll against `giro3dAdapter.getLayerHealth`.
   * `'loading'` until the adapter resolves; updates flip to `'ready'`,
   * `'failed'`, or `'unsupported'`.
   */
  const [layerHealth, setLayerHealth] = useState<
    Record<string, 'ready' | 'loading' | 'failed' | 'unsupported' | 'deferred'>
  >({});

  const onSetRasterPaint = useCallback(
    (id: string, prop: string, value: number) => {
      setRasterPaint((prev) => ({
        ...prev,
        [id]: { ...(prev[id] ?? {}), [prop]: value },
      }));

      // Map the MapLibre paint prop names onto the Giro3D raster filter
      // surface. Both adapters silently no-op for layers they don't own,
      // so it's safe to fan to both engines.
      const giro3dFilterKey: 'brightness' | 'contrast' | 'saturation' | null =
        prop === 'raster-brightness-max'
          ? 'brightness'
          : prop === 'raster-contrast'
            ? 'contrast'
            : prop === 'raster-saturation'
              ? 'saturation'
              : null;
      if (giro3dFilterKey) {
        const host = hostRef.current;
        const adapters = host ? ((host as any)._adapters ?? []) : [];
        for (const adapter of adapters) {
          if (typeof adapter.setRasterFilter === 'function') {
            try {
              adapter.setRasterFilter(id, { [giro3dFilterKey]: value });
            } catch {
              /* best-effort */
            }
          }
        }
      }

      const map = mapContainer.current
        ? getMapLibreHostHandle(mapContainer.current)
        : null;
      if (!map) return;
      const layerId = `layer-${id}`;
      try {
        if (map.getLayer(layerId)) {
          map.setPaintProperty(layerId, prop, value);
        }
      } catch (err) {
        console.warn("[TwinShell] setRasterPaint failed", err);
      }
    },
    [],
  );

  // Wave-1 A3 + A7 callbacks/effects were originally declared here, but
  // they reference `giro3dAdapter` which is initialised further down via
  // `useState`. To avoid TDZ ("Cannot access 'giro3dAdapter' before
  // initialization") they now live just after that state declaration.

  // Stable refs that always point at the latest 3D adapter instance.
  // Required because `onSetOpacity` (and similar callbacks) are declared
  // BEFORE the `giro3dAdapter` / `potreeAdapter` useState bindings below.
  // Putting the state directly in `useCallback` deps would throw a TDZ
  // ReferenceError; using refs sidesteps that while still letting the
  // callback read the current adapter at call time.
  const giro3dAdapterRef = useRef<Giro3DAdapterType | null>(null);
  const potreeAdapterRef = useRef<PotreeAdapterType | null>(null);
  const maplibreAdapterRef = useRef<MapLibreAdapter | null>(null);

  const onSetOpacity = useCallback((id: string, opacity: number) => {
    setLayerOpacity((prev) => ({ ...prev, [id]: opacity }));
    // Persist via the debounced batcher — but skip layers that exist only
    // client-side (synthetic OSM basemap injected by maybeInjectDefaultBasemap).
    // Sending those to the gtwin API yields a 400 because the server has no
    // matching scene item id. Use `useViewerStore.getState()` to read the
    // current catalog without taking a hook dependency.
    const layer = useViewerStore.getState().catalog.find((l) => l.id === id);
    const isLocalOnly = layer ? (isSyntheticBasemapLayer(layer) || isSyntheticEnvironmentLayer(layer)) : false;
    if (!isLocalOnly) {
      patchBatcherRef.current.enqueue({ id, opacity });
    }

    // Apply locally on the active 3D adapter. Read from refs — NOT from
    // the captured state value — so the slider works after the adapter
    // mounts. Previously this closure had empty deps and captured the
    // initial `null` adapter, making the slider a silent no-op.
    const active3dAdapter = giro3dAdapterRef.current ?? potreeAdapterRef.current;
    if (typeof (active3dAdapter as any)?.setLayerOpacity === "function") {
      try {
        (active3dAdapter as any).setLayerOpacity(id, opacity);
      } catch (err) {
        console.warn("[TwinShell] adapter setLayerOpacity failed", err);
      }
    } else {
      console.warn("[TwinShell] setLayerOpacity skipped: no 3D adapter ready");
    }

    const map = mapContainer.current
      ? getMapLibreHostHandle(mapContainer.current)
      : null;
    if (!map) return;
    const layerId = `layer-${id}`;
    try {
      if (map.getLayer(layerId)) {
        map.setPaintProperty(layerId, "raster-opacity", opacity);
      }
      const fillId = `${layerId}-fill`;
      if (map.getLayer(fillId)) {
        map.setPaintProperty(fillId, "fill-opacity", Math.min(0.6, opacity));
      }
      const lineId = `${layerId}-line`;
      if (map.getLayer(lineId)) {
        map.setPaintProperty(lineId, "line-opacity", opacity);
      }
    } catch (err) {
      console.warn("[TwinShell] setOpacity failed", err);
    }
  }, []);

  const onMoveLayer = useCallback((id: string, direction: -1 | 1) => {
    const host = hostRef.current;
    const adapters = host ? ((host as any)._adapters ?? []) : [];
    for (const adapter of adapters) {
      if (typeof adapter.setLayerOrder !== "function") continue;
      try {
        adapter.setLayerOrder(id, direction);
      } catch (err) {
        console.warn("[TwinShell] adapter setLayerOrder failed", err);
      }
    }

    const map = mapContainer.current
      ? getMapLibreHostHandle(mapContainer.current)
      : null;
    if (map) {
      const layerId = `layer-${id}`;
      try {
        if (map.getLayer(layerId)) {
          const style = map.getStyle();
          const userLayerIds = (style.layers ?? [])
            .filter((l) => l.id.startsWith("layer-"))
            .map((l) => l.id);
          const idx = userLayerIds.indexOf(layerId);
          const target = idx + direction;
          if (idx >= 0 && target >= 0 && target < userLayerIds.length) {
            const beforeId =
              direction === -1 ? userLayerIds[target] : userLayerIds[target + 1];
            map.moveLayer(layerId, beforeId);
          }
        }
      } catch (err) {
        console.warn("[TwinShell] moveLayer failed", err);
      }
    }

    const newOrder = catalog.map((l) => l.id);
    const from = newOrder.indexOf(id);
    const to = from + direction;
    if (from >= 0 && to >= 0 && to < newOrder.length) {
      [newOrder[from], newOrder[to]] = [newOrder[to], newOrder[from]];
      void gtwinClient.scene.items.reorder(gtwinId, newOrder).catch((err: unknown) => {
        console.warn("[TwinShell] Failed to persist layer order:", err);
      });
    }
  }, [catalog, gtwinId]);

  const [giro3dAdapter, setGiro3dAdapter] = useState<Giro3DAdapterType | null>(
    null,
  );

  // Mirror the adapter state into the refs declared above onSetOpacity so
  // pre-declared callbacks can always read the latest instance.
  useEffect(() => {
    giro3dAdapterRef.current = giro3dAdapter;
  }, [giro3dAdapter]);
  useEffect(() => {
    potreeAdapterRef.current = potreeAdapter;
  }, [potreeAdapter]);

  // Terrain tool card → Giro3D adapter bridge: forward every terrain
  // state change to the engine so hillshade, slope, contour, and
  // vertical-exaggeration toggles actually render.
  const terrainToolCard = useViewerStore((s) => s.terrainToolCard);
  useEffect(() => {
    if (!giro3dAdapter || engine !== 'giro3d') return;
    try {
      giro3dAdapter.applyTerrainToolCardState(terrainToolCard);
    } catch (err) {
      console.warn('[TwinShell] terrain state sync failed:', err);
    }
  }, [giro3dAdapter, engine, terrainToolCard]);

  // Terrain tool card state → companion layer visibility. The card's direct
  // onChange handler handles normal clicks, but this effect also covers reloads,
  // HMR, and cases where the card state was restored before derivative layers
  // were discovered.
  useEffect(() => {
    const terrainLayer = findTerrainAnalysisParent(catalog);
    if (!terrainLayer) return;
    const derivatives = resolveTerrainDerivatives(terrainLayer, catalog);
    const desired: Array<[Layer | null, boolean]> = [
      [derivatives.hillshade, terrainToolCard.hillshade],
      [derivatives.slope, terrainToolCard.slopeOn],
      [derivatives.aspect, terrainToolCard.aspectOn],
      [derivatives.contours, terrainToolCard.contoursOn],
    ];
    const contourInterval = terrainToolCard.contourInterval;
    const contoursOn = terrainToolCard.contoursOn;

    for (const [layer, shouldBeVisible] of desired) {
      if (!layer) continue;
      if (visibleIds.has(layer.id) !== shouldBeVisible) {
        setVisible(layer.id, shouldBeVisible);
      }

      const host = hostRef.current;
      if (!host) continue;
      void (async () => {
        try {
          const mounted = typeof host.listLayers === "function"
            ? host.listLayers().some((mountedLayer) => mountedLayer.id === layer.id)
            : true;
          if (shouldBeVisible && !mounted) {
            await host.addLayer(layer);
          }
          if (typeof host.setLayerVisible === "function") {
            await host.setLayerVisible(layer.id, shouldBeVisible);
          }

          const styleOpacity =
            layer.style && typeof layer.style === "object"
              ? (layer.style as Record<string, unknown>).opacity
              : undefined;
          const opacity =
            typeof styleOpacity === "number" && Number.isFinite(styleOpacity)
              ? styleOpacity
              : undefined;
          if (shouldBeVisible && typeof opacity === "number" && layer.kind === "raster") {
            const adapter = giro3dAdapterRef.current;
            if (adapter && typeof adapter.setLayerOpacity === "function") {
              adapter.setLayerOpacity(layer.id, opacity);
            }
          }

          if (
            shouldBeVisible
            && contoursOn
            && derivatives.contours
            && layer.id === derivatives.contours.id
          ) {
            const map = mapContainer.current
              ? getMapLibreHostHandle(mapContainer.current)
              : null;
            scheduleContourIntervalFilter(map, layer.id, contourInterval);
          }
        } catch (err) {
          console.warn("[TwinShell] terrain companion sync failed", layer.id, err);
        }
      })();
    }
  }, [
    catalog,
    setVisible,
    terrainToolCard.aspectOn,
    terrainToolCard.contourInterval,
    terrainToolCard.contoursOn,
    terrainToolCard.hillshade,
    terrainToolCard.slopeOn,
    visibleIds,
  ]);

  // Phase 1b — contour-interval slider → MapLibre filter bridge.
  // Contour PMTiles are rendered by MapLibre (vector layers), not Giro3D.
  // When the user moves the interval slider, apply an elevation-modulo
  // filter so only contours at multiples of the chosen interval remain.
  useEffect(() => {
    if (!terrainToolCard.contoursOn) return;

    const map = mapContainer.current
      ? getMapLibreHostHandle(mapContainer.current)
      : null;
    if (!map) return;

    const terrainLayer = findTerrainAnalysisParent(catalog);
    if (!terrainLayer) return;
    const derivatives = resolveTerrainDerivatives(terrainLayer, catalog);
    const contourLayer = derivatives.contours;
    if (!contourLayer) return;
    if (!visibleIds.has(contourLayer.id)) return;

    const interval = terrainToolCard.contourInterval;
    if (!applyContourIntervalFilter(map, contourLayer.id, interval)) {
      scheduleContourIntervalFilter(map, contourLayer.id, interval);
    }
  }, [
    catalog,
    terrainToolCard.contourInterval,
    terrainToolCard.contoursOn,
    visibleIds,
  ]);

  // Giro3D cursor coordinates: when the engine is giro3d, MapLibre's mouse
  // events are suppressed (the Giro3D canvas captures pointer). Listen on the
  // container DOM element instead and back-project the mouse position from
  // EPSG:3857 scene-space to lon/lat using the Giro3D raycaster.
  useEffect(() => {
    const container = mapContainer.current;
    if (!container || engine !== "giro3d") return;

    const mercatorToLonLat = (x: number, y: number): [number, number] => {
      const lon = (x / 20037508.342789244) * 180;
      const latRad = Math.atan(Math.exp((y / 20037508.342789244) * Math.PI));
      const lat = (2 * latRad - Math.PI / 2) * (180 / Math.PI);
      return [lon, lat];
    };

    const onMouseMove = (e: MouseEvent) => {
      const adapter = giro3dAdapterRef.current as any;
      const inst = adapter?.instance;
      const cam = inst?.view?.camera;
      const renderer = inst?.renderer;
      if (!cam || !renderer) return;

      const rect = (renderer.domElement as HTMLCanvasElement).getBoundingClientRect();
      const ndcX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const ndcY = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      const THREE = adapter.THREE ?? (typeof window !== 'undefined' ? (window as any).THREE : null);
      if (!THREE) return;
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), cam);

      const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
      const target = new THREE.Vector3();
      const hit = raycaster.ray.intersectPlane(plane, target);
      if (!hit) { setCursorCoords(null); return; }

      const [lon, lat] = mercatorToLonLat(hit.x, hit.y);
      if (Math.abs(lon) <= 180 && Math.abs(lat) <= 90) {
        setCursorCoords([lon, lat]);
      }
    };
    const onMouseLeave = () => setCursorCoords(null);

    container.addEventListener("mousemove", onMouseMove);
    container.addEventListener("mouseleave", onMouseLeave);
    return () => {
      container.removeEventListener("mousemove", onMouseMove);
      container.removeEventListener("mouseleave", onMouseLeave);
    };
  }, [engine, mapHostGeneration]);

  // Q4+Q5+M10: Selection property panel state  
  const [selectionData, setSelectionData] = useState<{ featureId: string; properties: Record<string, unknown> } | null>(null);
  const [selectionCount, setSelectionCount] = useState(0);

  // Q4+Q5+M10: Handle closing selection property panel. Declared here, after
  // `giro3dAdapter` and selection state, so the closure binds to the live values
  // rather than referencing block-scoped variables before their declaration.
  const handleCloseSelectionPanel = useCallback(() => {
    if (giro3dAdapter) {
      giro3dAdapter.clearSelection();
    }
    setSelectionData(null);
    setSelectionCount(0);
    clearSceneSelection("keyboard");
  }, [clearSceneSelection, giro3dAdapter]);
  
  // RFC-112 Phase F: Track CRS correction data per layer
  const [layerCrs, setLayerCrs] = useState<Record<string, Extract<import('@geosoft/viewer-sdk').ViewerEvent, { type: 'crsCorrectionApplied' }>>>({});
  // Wave-1 A3 (`onZoomToLayer`) and A7 (`layerHealth` polling) are defined
  // further down, after `onFlyToLayer`, so we don't hit a temporal dead zone
  // on the legacy fallback dep.
  const [selectedSceneNode, setSelectedSceneNode] =
    useState<UnifiedSceneTreeNode | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const nodeByLayerId = useMemo(() => {
    const byLayer = new Map<string, UnifiedSceneTreeNode>();
    const visit = (node: UnifiedSceneTreeNode) => {
      for (const layerId of node.sceneItemIds) {
        byLayer.set(layerId, node);
      }
      for (const ref of node.geometryRefs) {
        if (ref.sceneItemId) byLayer.set(ref.sceneItemId, node);
      }
      for (const child of node.children) visit(child);
    };
    if (sceneTree) visit(sceneTree);
    return byLayer;
  }, [sceneTree]);
  const nodeById = useMemo(() => {
    const byId = new Map<string, UnifiedSceneTreeNode>();
    const visit = (node: UnifiedSceneTreeNode) => {
      byId.set(node.id, node);
      for (const child of node.children) visit(child);
    };
    if (sceneTree) visit(sceneTree);
    return byId;
  }, [sceneTree]);
  const nodeByFeatureId = useMemo(() => {
    const byFeatureId = new Map<string, UnifiedSceneTreeNode>();
    const visit = (node: UnifiedSceneTreeNode) => {
      for (const ref of node.geometryRefs) {
        if (ref.featureId != null) {
          byFeatureId.set(String(ref.featureId), node);
        }
        if (ref.sourceFeatureId != null) {
          byFeatureId.set(String(ref.sourceFeatureId), node);
        }
      }
      for (const child of node.children) visit(child);
    };
    if (sceneTree) visit(sceneTree);
    return byFeatureId;
  }, [sceneTree]);

  const selectSceneNodeForTools = useCallback(
    (node: UnifiedSceneTreeNode) => {
      setSelectedSceneNode(node);
      const layer =
        node.sceneItemIds
          .map((id) => catalog.find((candidate) => candidate.id === id))
          .find((candidate): candidate is Layer => Boolean(candidate)) ??
        node.geometryRefs
          .map((ref) =>
            ref.sceneItemId
              ? catalog.find((candidate) => candidate.id === ref.sceneItemId)
              : undefined,
          )
          .find((candidate): candidate is Layer => Boolean(candidate)) ??
        null;

      const panoramaSelection = applyPanoramaBubbleTreeSelection(
        node,
        layer,
        "scene-tree",
      );
      if (panoramaSelection) {
        // Camera continuity: when the user transitions from the 3D scene
        // INTO the bubble overlay (i.e. no bubble layer was previously
        // active), snapshot the Giro3D camera and read its yaw so the
        // bubble can open facing the same direction. We only seed yaw on
        // the *first* such entry — re-selecting a sibling station inside
        // the same bubble session must not re-snap to the (potentially
        // stale) Giro3D yaw, since the user has been steering the bubble
        // controller in the meantime.
        const isFreshEntry = !panoramaBubbleView.layerId;
        if (isFreshEntry) {
          const instance = giro3dAdapter?.getInstance() ?? null;
          const captured = captureGiro3DCameraState(instance);
          savedCameraStateRef.current = captured;
          setPanoramaInitialYawRad(captured ? captured.yawRad : null);
        }
        setSceneSelection(panoramaSelection);
        setPanoramaBubbleView({
          layerId: panoramaSelection.layerId ?? null,
          activeStationId: panoramaSelection.panoramaStationId ?? null,
        });
        return;
      }

      setSceneSelection(selectFromTreeNode(node, layer, "scene-tree"));
    },
    [
      catalog,
      giro3dAdapter,
      panoramaBubbleView.layerId,
      setPanoramaBubbleView,
      setSceneSelection,
    ],
  );

  // Phase 6.5 — V2 tree selection handler: forwards the pre-built SceneSelection
  // from SceneTreeV2Container into the viewer store unchanged.
  const onOpenPanoramaRef = useRef<(layer: Layer) => void>(() => {});

  const handleV2Selection = useCallback(
    (selection: Parameters<typeof setSceneSelection>[0]) => {
      setSceneSelection(selection);
      const layerId = selection.sceneItemId ?? selection.layerId;
      if (!layerId) return;
      const layer = catalog.find((candidate) => candidate.id === layerId);
      // ADR-017: panorama-bubble activation trigger is tree-selection — selecting
      // the layer/station in V2 opens the bubble without a prior eye toggle.
      if (layer && isPanoramaBubbleLayer(layer) && isPanoramaTourEnabled()) {
        if (!visibleIds.has(layer.id)) {
          setVisible(layer.id, true);
        }
        onOpenPanoramaRef.current(layer);
      }
    },
    [catalog, visibleIds, setSceneSelection, setVisible],
  );

  // Block 2, Task E — tree → engine highlight. When a selection originates
  // from the scene tree (a tree-row or relationship-chip click), resolve the
  // selected node to its glTF node name via the unified pick resolver and ask
  // the 3D engine to highlight the matching mesh. This is the inverse of the
  // engine-pick path in the host 'select' handler below.
  useEffect(() => {
    if (sceneSelection.source !== "scene-tree") return;
    const ctx = pickContextRef.current;
    const objectId = sceneSelection.indexNodeId ?? sceneSelection.nodeId;
     
    console.log("[TreeToEngine] fire", { objectId, hasCtx: !!ctx, ctxSize: ctx?.nodeById.size });
    if (!ctx || !objectId) return;
    const node = ctx.nodeById.get(objectId);
     
    console.log("[TreeToEngine] node", { found: !!node, featureId: node?.featureId });
    if (!node) return;
    const gltfName = resolveTreeNodeToGeometry(node);
    
    const adapter = giro3dAdapterRef.current as unknown as {
      highlightByObjectName?: (name: string) => boolean;
      highlightByBatchId?: (layerId: string, batchId: string, gmlId?: string | null) => boolean;
      flyToProjectView?: () => boolean;
      flyToLayer?: (layerId: string) => boolean;
    } | null;

    const gmlId = node.standardRef?.nativeId ?? null;

    if (node.featureId != null && adapter?.highlightByBatchId) {
      // Prioritise batch-id pick for tilesets. highlightByBatchId scans all
      // tile meshes for the matching _BATCHID, so the layerId is only a hint —
      // a by-URL hierarchy node carries no layerId and must still resolve.
      // On success the adapter also flies the camera to the highlighted
      // geometry (world-space bounds) so off-screen selections become visible.
      const layerId =
        sceneSelection.layerId ?? node.layerId ?? sceneSelection.sceneItemId ?? "";
      const hit = adapter.highlightByBatchId(
        layerId,
        String(node.featureId),
        gmlId,
      );
      // Clear the previous tree-highlight before setting the new one, so the
      // scene tree list doesn't accumulate blue/highlighted rows.
      const store = useSceneHierarchyStore.getState();
      const prevHighlighted = Object.entries(store.controlOverlay.byObject)
        .filter(([, e]) => e.highlighted)
        .map(([id]) => id);
      for (const prevId of prevHighlighted) {
        if (prevId !== node.objectId) {
          store.setControlOverlayEntry(prevId, { highlighted: false });
        }
      }
      store.setControlOverlayEntry(node.objectId, {
        highlighted: true,
      });
      if (hit) {
        return;
      }

      // If the user is currently looking away from the tileset, Giro unloads
      // most tile meshes and a specific batch id may not be present in the
      // live scene. Fit back to the project, then retry with escalating delays
      // as tiles stream in.
      const flyOk = adapter?.flyToProjectView?.();
      let cameraMoved = flyOk;
      if (!flyOk) {
        // Fallback: fly to the layer bounds. This uses the entity's current
        // world bounds (which may be partial) and will trigger Giro3D to
        // stream more tiles, including the target block.
        const layerFly = adapter?.flyToLayer?.(layerId);
        cameraMoved = !!layerFly;
      }
      if (cameraMoved) {
        const retryOnce = (delay: number) => {
          window.setTimeout(() => {
            adapter.highlightByBatchId?.(layerId, String(node.featureId), gmlId);
          }, delay);
        };
        retryOnce(1500);
        retryOnce(4000);
        return;
      }
    }

    if (gltfName) {
      adapter?.highlightByObjectName?.(gltfName);
    }
  }, [sceneSelection]);

  useEffect(() => {
    if (catalog.length !== 1) return;
    if (sceneSelection.kind !== "cleared") return;
    const [onlyLayer] = catalog;
    if (!onlyLayer) return;
    const node = nodeByLayerId.get(onlyLayer.id) ?? null;
    if (node) {
      setSelectedSceneNode(node);
      setSceneSelection(selectFromTreeNode(node, onlyLayer, "scene-tree"));
      return;
    }
    setSceneSelection(
      selectByLayerId(onlyLayer.id, {
        layerKind: onlyLayer.kind,
        sceneItemId: onlyLayer.id,
        source: "scene-tree",
      }),
    );
  }, [catalog, nodeByLayerId, sceneSelection.kind, setSceneSelection]);

  useEffect(() => {
    if (sceneSelection.kind === "cleared") {
      setSelectedSceneNode(null);
      return;
    }
    const byNodeId =
      (sceneSelection.indexNodeId && nodeById.get(sceneSelection.indexNodeId)) ||
      (sceneSelection.nodeId && nodeById.get(sceneSelection.nodeId)) ||
      null;
    if (byNodeId) {
      setSelectedSceneNode(byNodeId);
      return;
    }
    if (sceneSelection.featureId != null) {
      const byFeature = nodeByFeatureId.get(String(sceneSelection.featureId));
      if (byFeature) {
        setSelectedSceneNode(byFeature);
        return;
      }
    }
    const byLayer = sceneSelection.layerId
      ? (nodeByLayerId.get(sceneSelection.layerId) ?? null)
      : null;
    setSelectedSceneNode(byLayer);
  }, [sceneSelection, nodeByFeatureId, nodeById, nodeByLayerId]);

  // V2 hierarchy nodes (object-index / hierarchyUrl) live in the pick context,
  // not the legacy UnifiedSceneTreeNode tree. Resolve the selected building
  // from the manifest so the property panel shows metadata instead of a stale
  // 3D mesh pick (e.g. mesh_0_595).
  const selectedManifestNode = useMemo(() => {
    if (sceneSelection.kind === "cleared") return null;
    const objectId = sceneSelection.indexNodeId ?? sceneSelection.nodeId;
    if (!objectId || !pickContext) return null;
    return pickContext.nodeById.get(objectId) ?? null;
  }, [sceneSelection, pickContext]);

  const propertyPanelSelection = useMemo(() => {
    if (selectedManifestNode) {
      const meta = { ...(selectedManifestNode.metadata ?? {}) } as Record<string, unknown>;
      const nativeId = selectedManifestNode.standardRef?.nativeId;
      if (nativeId && meta.gml_id == null) {
        meta.gml_id = nativeId;
      }
      // Merge authoritative attributes from the b3dm batch table (height,
      // usage, class, storeys, gen_* …). These match the geometry exactly and
      // override stale/empty object-index metadata for this run.
      const adapterAttrs =
        (giro3dAdapterRef.current as unknown as {
          getAttributesForGmlId?: (id: string | null | undefined) => Record<string, unknown> | null;
        } | null)?.getAttributesForGmlId?.(nativeId) ?? null;
      if (adapterAttrs) {
        for (const [k, v] of Object.entries(adapterAttrs)) {
          meta[k] = v;
        }
      }
      if (Array.isArray(meta.lodLevelsPresent)) {
        meta.lodLevelsPresent = meta.lodLevelsPresent.join(", ");
      }
      return {
        featureId: selectedManifestNode.label ?? selectedManifestNode.objectId,
        properties: meta,
      };
    }
    if (
      sceneSelection.featureId != null
      && sceneSelection.layerId
      && (sceneSelection.layerKind === "vector" || sceneSelection.toolCardKind === "vector")
      && !String(sceneSelection.featureId).startsWith("layer:")
    ) {
      const wanted = String(sceneSelection.featureId);
      if (sceneSelection.featureProperties && Object.keys(sceneSelection.featureProperties).length > 0) {
        return {
          featureId: wanted,
          properties: { ...sceneSelection.featureProperties },
        };
      }
      const adapter = maplibreAdapterRef.current;
      const adapterProps = adapter?.getFeatureProperties(sceneSelection.layerId, wanted);
      const renderedProps = adapter?.getLastVectorPickProperties?.() ?? {};
      const cached = adapter?.getVectorFeatures(sceneSelection.layerId).find((feature) => {
        const id = feature.id ?? feature.properties?.id ?? feature.properties?.gml_id;
        return id != null && String(id) === wanted;
      });
      const properties = {
        ...(adapterProps ?? {}),
        ...(cached?.properties ?? {}),
        ...renderedProps,
      };
      if (Object.keys(properties).length > 0 || cached || adapterProps) {
        return {
          featureId: wanted,
          properties,
        };
      }
    }
    if (selectedSceneNode) {
      const legacyProps =
        (selectedSceneNode as { metadata?: Record<string, unknown> }).metadata ??
        selectedSceneNode.attributes ??
        {};
      return {
        featureId: String(
          (selectedSceneNode as { featureId?: string | number }).featureId ??
            selectedSceneNode.id,
        ),
        properties: legacyProps as Record<string, unknown>,
      };
    }
    return selectionData;
  }, [selectedManifestNode, selectedSceneNode, sceneSelection, selectionData]);

  const layerPropertiesLayer = useMemo(() => {
    if (sceneSelection.detailsPanel !== "layer-properties") return null;
    const layerId = sceneSelection.layerId ?? sceneSelection.sceneItemId;
    if (!layerId) return null;
    return catalog.find((candidate) => candidate.id === layerId) ?? null;
  }, [catalog, sceneSelection.detailsPanel, sceneSelection.layerId, sceneSelection.sceneItemId]);

  const closeLayerProperties = useCallback(() => {
    const nextSelection = { ...sceneSelection };
    delete (nextSelection as { detailsPanel?: unknown }).detailsPanel;
    setSceneSelection(nextSelection);
  }, [sceneSelection, setSceneSelection]);

  // Active session coordinate override for the layer-properties target (if any).
  const layerPropertiesOverride = useMemo(() => {
    if (!layerPropertiesLayer) return null;
    const o = layerCoordinateOverrides[layerPropertiesLayer.id];
    return o
      ? {
          sourceCrs: o.sourceCrs,
          displayCrs: o.displayCrs,
          displayCrsProj4: o.displayCrsProj4,
          vectorSessionFeaturesUrl: o.vectorSessionFeaturesUrl,
          scope: o.scope,
        }
      : null;
  }, [layerPropertiesLayer, layerCoordinateOverrides]);

  // Build the global Layer Properties view model. Layer-KIND aware: it fuses
  // the viewer Layer with store visibility, the scene slot, the polled engine
  // health, live render diagnostics from the owning adapter, and any session
  // override. Recomputes as health ticks so live mount/visibility stays fresh.
  const layerPropertiesDiagnostics = useMemo(() => {
    if (!layerPropertiesLayer) return null;
    const layerId = layerPropertiesLayer.id;
    const live = hostRef.current?.getLayerDiagnostics?.(layerId) ?? null;
    const hostInternals = hostRef.current as unknown as {
      _adapters?: Array<{ id?: EngineId }>;
      opts?: { adapters?: Array<{ id?: EngineId }> };
    } | null;
    const adapters = hostInternals?._adapters ?? hostInternals?.opts?.adapters ?? [];
    const availableEngines = [
      ...new Set(adapters.map((a) => a.id).filter((id): id is EngineId => Boolean(id))),
    ];
    // Viewer-level render frame (the CRS the whole scene is drawn in) and the
    // layer's group/slot siblings — feed the three-level coordinate readout.
    const slot = slotMap[layerId] ?? null;
    const viewerFrameCrs = hostRef.current?.getActiveFrameCrs?.() ?? null;
    const groupMembers = slot
      ? catalog
          .filter((l) => (slotMap[l.id] ?? null) === slot)
          .map((l) => ({
            id: l.id,
            sourceCrs: l.sourceCrs ?? l.crs ?? null,
            displayCrs: l.displayCrs ?? null,
          }))
      : undefined;
    return buildLayerDiagnostics(layerPropertiesLayer, {
      slot,
      storeVisible: visibleIds.has(layerId),
      health: layerHealth[layerId] ?? null,
      live,
      engineHealthJson: live,
      primaryEngine: engine,
      availableEngines: availableEngines.length > 0 ? availableEngines : undefined,
      viewerFrameCrs,
      groupLabel: slot ? (SLOT_LABELS[slot] ?? slot) : null,
      groupMembers,
      override: layerPropertiesOverride
        ? {
        sourceCrs: layerPropertiesOverride.sourceCrs,
            displayCrs: layerPropertiesOverride.displayCrs,
            displayCrsProj4: layerPropertiesOverride.displayCrsProj4,
          }
        : null,
    });
  }, [
    layerPropertiesLayer,
    slotMap,
    catalog,
    visibleIds,
    layerHealth,
    engine,
    layerPropertiesOverride,
  ]);

  const [addLayerOpen, setAddLayerOpen] = useState(false);
  const [missingCrsLayerId, setMissingCrsLayerId] = useState<string | null>(null);
  const dismissedMissingCrsRef = useRef(new Set<string>());

  useEffect(() => {
    if (missingCrsLayerId) return;
    const next = catalog.find(
      (layer) =>
        layerShouldPromptMissingCrs(layer) && !dismissedMissingCrsRef.current.has(layer.id),
    );
    setMissingCrsLayerId(next?.id ?? null);
  }, [catalog, missingCrsLayerId]);
  const [panoramaStationsByLayerId, setPanoramaStationsByLayerId] = useState<
    Record<string, readonly PanoramaBubbleStationRef[]>
  >({});
  // Full station entries (position, quaternion, …) keyed by layer id. Drives
  // the in-3D-scene `<StationMarkers>` so the user can click a dot in the
  // Giro3D scene to enter the bubble at that station; the lightweight
  // `panoramaStationsByLayerId` map above continues to feed the scene tree.
  const [panoramaStationManifestsByLayerId, setPanoramaStationManifestsByLayerId] = useState<
    Record<string, PanoramaBubbleStationsManifest>
  >({});

  useEffect(() => {
    const bubbleLayers = catalog.filter((layer) => isPanoramaBubbleLayer(layer));
    if (bubbleLayers.length === 0) {
      setPanoramaStationsByLayerId({});
      setPanoramaStationManifestsByLayerId({});
      return;
    }

    let cancelled = false;
    void (async () => {
      const next: Record<string, readonly PanoramaBubbleStationRef[]> = {};
      const nextFull: Record<string, PanoramaBubbleStationsManifest> = {};
      await Promise.all(
        bubbleLayers.map(async (layer) => {
          const stationsUrl = layer.endpoints.stations;
          if (typeof stationsUrl !== "string" || stationsUrl.length === 0) return;
          try {
            const manifest = await loadStations(stationsUrl);
            next[layer.id] = manifest.stations.map((station) => ({
              id: station.id,
              name: station.name,
            }));
            nextFull[layer.id] = manifest;
          } catch {
            /* keep tree parent-only when stations.json is unreachable */
          }
        }),
      );
      if (!cancelled) {
        setPanoramaStationsByLayerId(next);
        setPanoramaStationManifestsByLayerId(nextFull);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [catalog]);

  const enrichedSceneTree = useMemo(() => {
    if (!sceneTree) return undefined;
    return enrichSceneTreeWithPanoramaBubbles(
      sceneTree,
      catalog,
      panoramaStationsByLayerId,
    );
  }, [sceneTree, catalog, panoramaStationsByLayerId]);

  const activePanoramaLayer = useMemo(
    () =>
      panoramaBubbleView.layerId
        ? catalog.find((layer) => layer.id === panoramaBubbleView.layerId) ?? null
        : null,
    [catalog, panoramaBubbleView.layerId],
  );

  // Auto-open the panorama bubble view when the scene contains only
  // panorama-bubble layers (plus possibly a synthetic basemap). Avoids
  // forcing the user to discover the layer-name click or the new
  // "360° View" button on first visit. Skipped for ADR-017 on-demand layers —
  // those stay registered in the tree until the operator toggles them on.
  const panoramaAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (panoramaAutoOpenedRef.current) return;
    if (catalog.length === 0) return;
    if (panoramaBubbleView.layerId) return;
    const nonBasemap = catalog.filter((l) => !isSyntheticBasemapLayer(l));
    if (nonBasemap.length !== 1) return;
    const layer = nonBasemap[0];
    if (!layer || layer.kind !== "panorama-bubble") return;
    if (layer.activationTier === "on-demand") return;
    if (!layer.endpoints.stations) return;
    panoramaAutoOpenedRef.current = true;
    const stations = readPanoramaBubbleStationsFromLayer(layer);
    const firstStationId = stations[0]?.id ?? null;
    setPanoramaBubbleView({
      layerId: layer.id,
      activeStationId: firstStationId,
    });
    if (firstStationId) {
      setSceneSelection(
        selectPanoramaBubbleLayer(layer.id, {
          stationId: firstStationId,
          source: "scene-tree",
        }),
      );
    }
  }, [catalog, panoramaBubbleView.layerId, setPanoramaBubbleView, setSceneSelection]);

  // Endpoint health check — detect unreachable layer data
  const [unreachableLayers, setUnreachableLayers] = useState<UnreachableLayer[]>([]);
  const [relinkDismissed, setRelinkDismissed] = useState(false);

  useEffect(() => {
    if (catalog.length === 0) return;
    const checkEndpoints = async () => {
      const unreachable: UnreachableLayer[] = [];
      for (const layer of catalog) {
        const ep = layer.endpoints;
        const primaryUrl =
          ep.tiles || ep.pmtiles || ep.features || (ep as Record<string, string>).copc || (ep as Record<string, string>).tileset3d;
        if (!primaryUrl || typeof primaryUrl !== "string") continue;
        if (primaryUrl.startsWith("mock://") || primaryUrl.startsWith("data:")) continue;
        // TiTiler XYZ templates do not support HEAD on templated URLs. The
        // renderer resolves their tilejson/bounds separately, so avoid noisy
        // 405 preflight errors here.
        if (ep.tiles && primaryUrl.includes("{z}") && primaryUrl.includes("{x}") && primaryUrl.includes("{y}")) {
          continue;
        }
        // Private S3 HTTPS URLs are rewritten to `/s3-assets/…` at layer build
        // time. If a raw amazonaws.com URL still appears, skip HEAD — the
        // browser cannot CORS-check private buckets (Option B).
        if (/^https?:\/\/[^/]*\.amazonaws\.com\//i.test(primaryUrl)) {
          continue;
        }
        try {
          const resp = await fetch(primaryUrl, { method: "HEAD", signal: AbortSignal.timeout(5000) });
          if (!resp.ok && resp.status !== 206) {
            const key = ep.tiles ? "tiles" : ep.pmtiles ? "pmtiles" : ep.features ? "features" : "copc";
            unreachable.push({
              layerId: layer.id,
              layerLabel: layer.label ?? layer.id,
              endpointKey: key,
              currentUrl: primaryUrl,
              error: `HTTP ${resp.status}`,
            });
          }
        } catch (err) {
          const key = ep.tiles ? "tiles" : ep.pmtiles ? "pmtiles" : ep.features ? "features" : "copc";
          unreachable.push({
            layerId: layer.id,
            layerLabel: layer.label ?? layer.id,
            endpointKey: key,
            currentUrl: primaryUrl,
            error: err instanceof Error ? err.message : "Network error",
          });
        }
      }
      setUnreachableLayers(unreachable);
    };
    void checkEndpoints();
  }, [catalog]);

  const handleRelink = useCallback(
    (layerId: string, endpointKey: string, newUrl: string) => {
      const layer = catalog.find((l) => l.id === layerId);
      if (!layer) return;
      const updatedEndpoints = { ...layer.endpoints, [endpointKey]: newUrl };
      const updatedLayer = { ...layer, endpoints: updatedEndpoints };
      setCatalogStore(catalog.map((l) => l.id === layerId ? updatedLayer : l));
      // Persist the re-linked endpoint to the gTwin
      void gtwinClient.scene.items.patch(gtwinId, [
        { id: layerId, extensions: { endpoints: { [endpointKey]: newUrl } } },
      ]).catch((err: unknown) => {
        console.warn(`[TwinShell] Failed to persist re-linked endpoint:`, err);
      });
      setUnreachableLayers((prev) => prev.filter((u) => u.layerId !== layerId));
    },
    [gtwinId, catalog, setCatalogStore],
  );

  // Persistent remove: remove from store AND from gTwin via platform API
  const persistentRemoveLayer = useCallback(
    (_node: import("./scenePackage").UnifiedSceneTreeNode, layer: import("@geosoft/viewer-sdk").Layer | null) => {
      if (!layer) return;
      removeLayer(layer.id);
      void (async () => {
        try {
          await gtwinClient.scene.items.remove(gtwinId, layer.id);
          console.log(`[TwinShell] Removed layer ${layer.id} from gTwin ${gtwinId}`);
        } catch (err) {
          console.warn(`[TwinShell] Failed to remove layer ${layer.id} from platform:`, err);
        }
      })();
    },
    [gtwinId, removeLayer],
  );


  // WP-1: the add-layer flow now writes through `gtwinClient.scene.items.add`
  // (mock by default; real-platform stub once the Track A SDK lands). The
  // legacy in-memory `devPreview` path was removed; durable project state is
  // the only path supported on the Twin route.
  const markDirty = useSyncStatusStore((s) => s.markDirty);
  const markSaving = useSyncStatusStore((s) => s.markSaving);
  const markClean = useSyncStatusStore((s) => s.markClean);
  const markFailed = useSyncStatusStore((s) => s.markFailed);
  const resetSync = useSyncStatusStore((s) => s.resetSync);
  // WP-14 — real-backend error surfacing. The batcher already records
  // its own failures (see `useSyncStatusStore.createDebouncedBatcher`);
  // we also call this from the direct mutation sites below (AddLayer,
  // export, preset upsert) so every failure mode flows through one
  // banner and one error catalog.
  const recordRealBackendError = useSyncStatusStore(
    (s) => s.recordRealBackendError,
  );

  // WP-1: debounced batcher per gtwinId. Visibility/opacity edits enqueue a
  // SceneItemPatch; the batcher flushes via `gtwinClient.scene.items.patch`
  // (mock by default). Sync state transitions are handled inside the batcher.
  const patchBatcherRef = useRef(
    createDebouncedBatcher<SceneItemPatch>({
      waitMs: 250,
      flush: async (batch) => {
        // Coalesce by id so the last patch wins for a given layer in one flush.
        const merged = new Map<string, SceneItemPatch>();
        for (const patch of batch) {
          const prev = merged.get(patch.id) ?? { id: patch.id };
          merged.set(patch.id, { ...prev, ...patch });
        }
        await gtwinClient.scene.items.patch(gtwinId, Array.from(merged.values()));
      },
    }),
  );

  const persistGeoreferenceLayer = useCallback((updated: Layer) => {
    patchBatcherRef.current.enqueue({
      id: updated.id,
      style: (updated.style ?? {}) as Record<string, unknown>,
    });
  }, []);

  const missingCrsLayer = catalog.find((layer) => layer.id === missingCrsLayerId) ?? null;

  const handleMissingCrsRectify = useCallback(() => {
    if (!missingCrsLayer) return;
    const updated = withGeoreferenceStyle(missingCrsLayer, { status: 'rectifying' });
    patchCatalogLayer(missingCrsLayer.id, () => updated);
    persistGeoreferenceLayer(updated);
    dismissedMissingCrsRef.current.add(missingCrsLayer.id);
    setSceneSelection(
      selectByLayerId(missingCrsLayer.id, {
        layerKind: missingCrsLayer.kind,
        sceneItemId: missingCrsLayer.id,
        source: 'scene-tree',
      }),
    );
    setMissingCrsLayerId(null);
  }, [missingCrsLayer, patchCatalogLayer, persistGeoreferenceLayer, setSceneSelection]);

  const handleMissingCrsFakeCoordinates = useCallback(() => {
    if (!missingCrsLayer) return;
    const updated = withGeoreferenceStyle(missingCrsLayer, { status: 'fake-coordinates' });
    patchCatalogLayer(missingCrsLayer.id, () => updated);
    persistGeoreferenceLayer(updated);
    dismissedMissingCrsRef.current.add(missingCrsLayer.id);
    setMissingCrsLayerId(null);
  }, [missingCrsLayer, patchCatalogLayer, persistGeoreferenceLayer]);

  const handleMissingCrsDismiss = useCallback(() => {
    if (missingCrsLayerId) dismissedMissingCrsRef.current.add(missingCrsLayerId);
    setMissingCrsLayerId(null);
  }, [missingCrsLayerId]);

  useEffect(() => {
    // Reset sync state when the active gTwin changes; flush any pending edits
    // first so the previous project keeps its last state.
    // G18: hydrate envelope.view camera here (not in a separate effect) so we
    // never reset initialFlyDoneRef after hydration — that was letting flyToBbox
    // override a persisted pose on every reload.
    if (initialCamera && !isPlaceholderCamera(initialCamera)) {
      useViewerStore.getState().setCamera(initialCamera);
      initialFlyDoneRef.current = true;
    } else {
      initialFlyDoneRef.current = false;
      if (isPlaceholderCamera(useViewerStore.getState().camera)) {
        useViewerStore.setState({ camera: null });
      }
    }
    if (initialSavedViews?.length) {
      useViewerStore.setState({
        savedViews: initialSavedViews.map((sv) => ({
          ...sv,
          kind: sv.kind === 'bookmark' ? 'bookmark' : sv.kind === 'view' ? 'view' : undefined,
          time: null,
          sceneSelection: undefined,
        })),
      });
    }
    const batcher = patchBatcherRef.current;
    dismissedMissingCrsRef.current = new Set();
    setMissingCrsLayerId(null);
    return () => {
      batcher.cancel();
      resetSync();
    };
  }, [gtwinId, resetSync, initialCamera, initialSavedViews]);

  // G18 fix — debounced camera auto-save. Persists the current camera +
  // savedViews to envelope.view on every camera change (2s debounce to avoid
  // thrashing during animation/orbit).
  const viewSaverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const viewSaverInflightRef = useRef(false);

  useEffect(() => {
    if (viewSaverTimerRef.current) clearTimeout(viewSaverTimerRef.current);
    const unsub = useViewerStore.subscribe((state, prevState) => {
      if (state.camera === prevState.camera && state.savedViews === prevState.savedViews) return;
      if (!initialFlyDoneRef.current) return;
      if (viewSaverTimerRef.current) clearTimeout(viewSaverTimerRef.current);
      viewSaverTimerRef.current = setTimeout(() => {
        viewSaverTimerRef.current = null;
        if (viewSaverInflightRef.current) return;
        if (!initialFlyDoneRef.current) return;
        const { camera, savedViews } = useViewerStore.getState();
        if (!camera) return;
        if (isPlaceholderCamera(camera)) return;
        viewSaverInflightRef.current = true;
        markDirty();
        gtwinClient.view.save(gtwinId, {
          camera,
          savedViews: savedViews.map((sv) => ({
            id: sv.id,
            name: sv.name,
            camera: sv.camera,
            visibleLayerIds: [...sv.visibleLayerIds],
            createdAt: sv.createdAt,
            ...(sv.kind ? { kind: sv.kind } : {}),
          })),
        }).then(() => {
          markClean();
        }).catch((err: unknown) => {
          console.warn("[G18] view auto-save failed", err);
        }).finally(() => {
          viewSaverInflightRef.current = false;
        });
      }, 2000);
    });
    return () => {
      unsub();
      if (viewSaverTimerRef.current) clearTimeout(viewSaverTimerRef.current);
    };
  }, [gtwinId, markDirty, markClean]);

  const onSceneItemAdded = useCallback(
    (item: SceneItem, slot: SceneSlot) => {
      const layer = sceneItemToLayer(item, slot);
      const next = [...catalog.filter((l) => l.id !== layer.id), layer];
      setCatalogStore(next);
      setLayerSlot(layer.id, slot);
      setAddLayerOpen(false);
      markClean();
    },
    [catalog, setCatalogStore, setLayerSlot, markClean],
  );

  /**
   * Layers that exist only in the viewer (e.g. the synthetic OSM basemap
   * injected by `maybeInjectDefaultBasemap`) are never registered on the
   * gTwin server. Sending a visibility patch for them would return 400
   * `scene item id ... not found`. Detect via the `isSyntheticBasemap` flag
   * the injector stamps onto `layer.style`.
   */
  const isLocalOnlyLayer = useCallback(
    (id: string): boolean => {
      const layer = catalog.find((l) => l.id === id);
      return layer ? (isSyntheticBasemapLayer(layer) || isSyntheticEnvironmentLayer(layer)) : false;
    },
    [catalog],
  );

  /**
   * Tracks the visibility snapshot taken the moment we isolated a layer, so
   * that clicking the Isolate button a second time on the same card restores
   * the prior visible set instead of leaving the user with a single layer.
   * Cleared whenever the user manually changes visibility or isolates a
   * different layer.
   */
  const priorVisibleBeforeSoloRef = useRef<{
    soloedId: string;
    visible: ReadonlySet<string>;
  } | null>(null);

  /** Push environment-layer visibility through ViewerHost (mount + setLayerVisible). */
  const syncEnvironmentLayerVisible = useCallback(
    (id: string, visible: boolean) => {
      if (!id.startsWith("layer:environment:")) return;
      console.warn(`[TwinShell] syncEnvironmentLayerVisible: ${id} → ${visible}`);
      const host = hostRef.current;
      if (!host) {
        console.warn('[TwinShell] syncEnvironmentLayerVisible: hostRef is null!');
        return;
      }
      const layer = catalog.find((l) => l.id === id);
      if (!layer) {
        console.warn(`[TwinShell] syncEnvironmentLayerVisible: layer ${id} not in catalog`);
        return;
      }
      void (async () => {
        const mounted = new Set(host.listLayers().map((l) => l.id));
        if (!mounted.has(id)) {
          console.info(`[TwinShell] syncEnv: mounting ${id} first`);
          await host.addLayer(layer);
        }
        if (typeof host.setLayerVisible === "function") {
          console.warn(`[TwinShell] syncEnv: calling setLayerVisible(${id}, ${visible})`);
          await host.setLayerVisible(id, visible);
        }
      })();
    },
    [catalog],
  );

  /** Visibility wrapper: updates the local store and enqueues a patch. */
  const onSetVisibleAndQueue = useCallback(
    (id: string, visible: boolean) => {
      priorVisibleBeforeSoloRef.current = null;
      const layer = catalog.find((candidate) => candidate.id === id);
      // Keep Terrain tool-card toggles aligned with Scene-tree eyes so the
      // companion sync effect does not immediately force derivatives back off.
      const toolCardPatch = terrainToolCardPatchForLayerVisibility(layer, visible);
      if (toolCardPatch) {
        useViewerStore.getState().setTerrainToolCard(toolCardPatch);
      }
      setVisible(id, visible);
      if (id.startsWith("layer:environment:")) {
        syncEnvironmentLayerVisible(id, visible);
        return;
      }
      // Push directly to the active ViewerHost so the engine reflects the
      // toggle immediately (store update alone is not forwarded to the engine).
      const host = hostRef.current;
      console.log(`[TwinShell] onSetVisibleAndQueue ${id} → ${visible} (host=${host ? 'ready' : 'null'})`);
      if (host && typeof host.setLayerVisible === "function") {
        void (async () => {
          try {
            // Stamp desired visibility FIRST so an in-flight addLayer cannot
            // ignore a subsequent hide (hide→show→hide).
            await host.setLayerVisible(id, visible);
            if (visible && layer && typeof host.listLayers === "function") {
              const mounted = host.listLayers().some((mountedLayer) => mountedLayer.id === id);
              if (!mounted) {
                await host.addLayer(layer);
                if (layer.kind === "vector" && host.getLayerEngineId?.(layer.id) === "maplibre") {
                  const currentCamera = useViewerStore.getState().camera;
                  if (currentCamera) maplibreAdapterRef.current?.syncOverlayCamera?.(currentCamera);
                }
                // Re-apply after mount — addLayer may default the entity on.
                await host.setLayerVisible(id, visible);
              }
            }
          } catch (err: unknown) {
            console.warn("[TwinShell] setLayerVisible failed", id, err);
          }
        })();
      } else {
        console.warn(`[TwinShell] setLayerVisible: host not ready or missing setLayerVisible for ${id}`);
      }
      if (isLocalOnlyLayer(id)) return;
      patchBatcherRef.current.enqueue({ id, visible });
    },
    [catalog, setVisible, isLocalOnlyLayer, syncEnvironmentLayerVisible],
  );

  const onSetManyVisibleAndQueue = useCallback(
    (ids: ReadonlyArray<string>, visible: boolean) => {
      if (ids.length === 0) return;
      priorVisibleBeforeSoloRef.current = null;
      setManyVisible(ids, visible);
      for (const id of ids) {
        if (id.startsWith("layer:environment:")) {
          syncEnvironmentLayerVisible(id, visible);
          continue;
        }
        if (isLocalOnlyLayer(id)) continue;
        patchBatcherRef.current.enqueue({ id, visible });
      }
    },
    [setManyVisible, isLocalOnlyLayer, syncEnvironmentLayerVisible],
  );

  const onSoloLayerAndQueue = useCallback(
    (id: string) => {
      if (!catalog.some((layer) => layer.id === id)) return;

      const snapshot = priorVisibleBeforeSoloRef.current;
      const currentlyIsolatedToThis =
        snapshot?.soloedId === id
        && visibleIds.size === 1
        && visibleIds.has(id);

      // Helper: enqueue a patch only when the new visibility differs from
      // the current one. Avoids the previous wasteful pattern of firing one
      // patch per catalog layer on every solo toggle, which was contributing
      // to multi-100ms click-handler violations.
      const applyVisibilityChange = (layerId: string, nextVisible: boolean) => {
        if (layerId.startsWith("layer:environment:")) {
          if (visibleIds.has(layerId) !== nextVisible) {
            syncEnvironmentLayerVisible(layerId, nextVisible);
          }
          return;
        }
        if (isLocalOnlyLayer(layerId)) return;
        if (visibleIds.has(layerId) === nextVisible) return;
        patchBatcherRef.current.enqueue({ id: layerId, visible: nextVisible });
      };

      if (currentlyIsolatedToThis && snapshot) {
        const restored = Array.from(snapshot.visible);
        const hiddenForCard = catalog
          .filter((layer) => !snapshot.visible.has(layer.id))
          .map((layer) => layer.id);
        setManyVisible(restored, true);
        setManyVisible(hiddenForCard, false);
        for (const layer of catalog) {
          applyVisibilityChange(layer.id, snapshot.visible.has(layer.id));
        }
        priorVisibleBeforeSoloRef.current = null;
        return;
      }

      priorVisibleBeforeSoloRef.current = {
        soloedId: id,
        visible: new Set(visibleIds),
      };
      soloVisibleLayer(id);
      for (const layer of catalog) {
        applyVisibilityChange(layer.id, layer.id === id);
      }
    },
    [catalog, soloVisibleLayer, setManyVisible, visibleIds, isLocalOnlyLayer, syncEnvironmentLayerVisible],
  );

  const onSetPointCloudAppearance = useCallback(
    (
      _layerId: string,
      patch: {
        pointBudget?: number;
        pointSize?: number;
        colorMode?: "auto" | "rgb" | "intensity" | "classification" | "elevation";
      },
    ) => {
      if (patch.pointSize !== undefined) {
        setPointCloudPointSize(patch.pointSize);
      }
      if (patch.colorMode) {
        setPointCloudColorMode(
          patch.colorMode === "auto" ? "rgb" : patch.colorMode,
        );
      }
      const host = hostRef.current;
      const adapters = host ? ((host as any)._adapters ?? []) : [];
      for (const adapter of adapters) {
        if (typeof adapter.setPointCloudQuality !== "function") continue;
        try {
          adapter.setPointCloudQuality(patch);
        } catch (err) {
          console.warn("[TwinShell] setPointCloudQuality failed", err);
        }
      }
    },
    [setPointCloudColorMode, setPointCloudPointSize],
  );

  const onSetPointCloudClassificationForLayer = useCallback(
    (_layerId: string, classes: ReadonlySet<number>) => {
      const next = new Set(classes);
      setPointCloudClassificationFilter(next);
      const host = hostRef.current;
      const adapters = host ? ((host as any)._adapters ?? []) : [];
      for (const adapter of adapters) {
        if (typeof adapter.setPointCloudClassificationFilter !== "function") continue;
        try {
          adapter.setPointCloudClassificationFilter(next);
        } catch (err) {
          console.warn("[TwinShell] setPointCloudClassificationFilter failed", err);
        }
      }
    },
    [setPointCloudClassificationFilter],
  );

  const onSetVectorStyle = useCallback(
    (
      layerId: string,
      delta: {
        fillColor?: string;
        fillOpacity?: number;
        lineColor?: string;
        lineWidth?: number;
      },
    ) => {
      const host = hostRef.current;
      const adapters = host ? ((host as any)._adapters ?? []) : [];
      for (const adapter of adapters) {
        if (typeof adapter.setVectorPaint !== "function") continue;
        try {
          adapter.setVectorPaint(layerId, delta);
        } catch (err) {
          console.warn("[TwinShell] setVectorPaint failed", err);
        }
      }
    },
    [],
  );

  const onSetVectorLabelsForLayer = useCallback(
    (layerId: string, options: { enabled: boolean; field: string | null }) => {
      const host = hostRef.current;
      const adapters = host ? ((host as any)._adapters ?? []) : [];
      for (const adapter of adapters) {
        if (typeof adapter.setVectorLabels !== "function") continue;
        try {
          adapter.setVectorLabels(layerId, options);
        } catch (err) {
          console.warn("[TwinShell] setVectorLabels failed", err);
        }
      }
    },
    [],
  );

  const onFlyToLayer = useCallback(
    (layer: Layer) => {
      if (!hasExplicitGeospatialBounds(layer)) {
        console.warn(
          "[TwinShell] flyTo skipped: layer has local/unknown coordinates or missing bounds",
          layer.id,
        );
        return;
      }
      const is3DLayer =
        layer.kind === "3dtiles" ||
        layer.kind === "bim" ||
        layer.kind === "terrain" ||
        layer.kind === "pointcloud";
      const is3DEngine =
        engine === "cesium" || engine === "three" || engine === "giro3d";

      if (is3DLayer && is3DEngine && hostRef.current) {
        const bbox = layer.bbox;
        if (bbox && bbox.length === 4) {
          const [minX, minY, maxX, maxY] = bbox;
          // Prefer the adapter's bbox flight: it computes a proper framing
          // distance (e.g. ~813 m for the 2.7 km Minato DEM), keeping small
          // high-resolution COGs inside their TiTiler min/max zoom range. The
          // legacy `spanDeg * 111000 * 2` altitude heuristic flew ~4-6x too
          // high (5550 m for the same DEM → camera below minzoom → no tiles).
          const looksGeographic =
            Math.abs(minX) <= 180 && Math.abs(maxX) <= 180 &&
            Math.abs(minY) <= 90 && Math.abs(maxY) <= 90;
          const adapter = giro3dAdapter as
            | (typeof giro3dAdapter & {
                flyToBbox?: (bbox: readonly number[], crs?: string) => boolean;
              })
            | null;
          if (
            looksGeographic &&
            adapter &&
            typeof adapter.flyToBbox === "function" &&
            adapter.flyToBbox(bbox, "EPSG:4326")
          ) {
            return;
          }
          const centerLon = (minX + maxX) / 2;
          const centerLat = (minY + maxY) / 2;
          const spanDeg = Math.max(
            Math.abs(maxX - minX),
            Math.abs(maxY - minY),
          );
          // Fallback framing: ~0.7x the ground span keeps the bbox in view at
          // the default -45° pitch without overshooting small layers' zoom range.
          const height = Math.min(Math.max(300, spanDeg * 111_000 * 0.7), 60_000);
          hostRef.current.flyTo?.({ lonLat: [centerLon, centerLat], height });
        }
        return;
      }

      const canvasAdapter = mapContainer.current
        ? getCanvasMapHostHandle(mapContainer.current)
        : null;
      const frame = resolveLayerFrameBbox(
        layer,
        canvasAdapter?.getVectorBounds?.(layer.id) ?? null,
      );
      if (frame && canvasAdapter?.flyToBbox?.(frame, "EPSG:4326")) {
        return;
      }
      const map = mapContainer.current
        ? getMapLibreHostHandle(mapContainer.current)
        : null;
      if (!map || !frame) return;
      const [minX, minY, maxX, maxY] = frame;
      try {
        map.fitBounds(
          [
            [minX, minY],
            [maxX, maxY],
          ],
          {
            padding: tokens.space[8],
            maxZoom: 18,
            duration: tokens.motion.duration.slow,
          },
        );
      } catch (err) {
        console.warn("[TwinShell] flyTo failed", err);
      }
    },
    [engine, giro3dAdapter],
  );

  // LoR Viewer Bridge: when opened from the estate admin with a resolved launch
  // focus, frame the first focused layer once (after the engine has had a beat
  // to mount it). `catalog` is a dependency so this retries when hydration
  // populates the layer.
  const launchFocus = useViewerStore((s) => s.launchFocus);
  const launchFlyKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!launchFocus?.resolved || launchFocus.matchedLayerIds.length === 0) return;
    const key = launchFocus.matchedLayerIds.join("|");
    if (launchFlyKeyRef.current === key) return;
    const targetId = launchFocus.matchedLayerIds[0];
    const layer = catalog.find((candidate) => candidate.id === targetId);
    if (!layer) return; // catalog not hydrated yet — re-runs when it updates
    launchFlyKeyRef.current = key;
    const timer = window.setTimeout(() => onFlyToLayer(layer), 600);
    return () => window.clearTimeout(timer);
  }, [launchFocus, catalog, onFlyToLayer]);

  /**
   * Wave-1 A3 — "Zoom to layer". Centres the camera on the layer footprint and
   * lands at a fixed Web-Mercator zoom of 14 so the action is predictable from
   * anywhere on the map (the old fit-bounds path left zoom dependent on extent
   * size). Falls back to fit-bounds / project-view only when no geographic
   * centre can be resolved.
   */
  const onZoomToLayer = useCallback(
    (layerId: string) => {
      void (async () => {
        const layer = catalog.find((l) => l.id === layerId);
        if (!layer) return;
        const canvasAdapter = mapContainer.current
          ? getCanvasMapHostHandle(mapContainer.current)
          : null;
        const adapter = (giro3dAdapter ?? canvasAdapter) as
          | (typeof giro3dAdapter & {
              flyToLayer?: (id: string) => boolean;
              flyToBbox?: (bbox: readonly number[], crs?: string) => boolean;
              flyToProjectView?: () => boolean;
              flyTo?: (camera: ViewerCamera) => void;
            })
          | null;

        const flyToLayerZoom = (lon: number, lat: number): boolean => {
          const viewportHeightPx = Math.max(
            1,
            mapContainer.current?.clientHeight ?? window.innerHeight ?? 800,
          );
          const height =
            cameraHeightForMercatorZoom({
              zoom: ZOOM_TO_LAYER_ZOOM,
              latDeg: lat,
              viewportHeightPx,
            }) ?? 3000;
          const camera: ViewerCamera = {
            lonLat: [lon, lat],
            zoom: ZOOM_TO_LAYER_ZOOM,
            height,
          };

          if (adapter && typeof adapter.flyTo === "function") {
            adapter.flyTo(camera);
            return true;
          }
          if (hostRef.current && typeof hostRef.current.flyTo === "function") {
            hostRef.current.flyTo(camera);
            return true;
          }

          const map = mapContainer.current
            ? getMapLibreHostHandle(mapContainer.current)
            : null;
          if (map && typeof map.flyTo === "function") {
            map.flyTo({
              center: [lon, lat],
              zoom: ZOOM_TO_LAYER_ZOOM,
              duration: tokens.motion.duration.slow,
            });
            return true;
          }
          return false;
        };

        // Prefer PMTiles archive bounds for vectors — producers can stamp broad
        // project extents even when the archive itself is a small footprint.
        if (layer.kind === "vector" && layer.endpoints.pmtiles) {
          const pmtilesBbox = await readPmtilesArchiveBounds(layer.endpoints.pmtiles);
          const center = geographicCenterFromBbox(pmtilesBbox);
          if (center && flyToLayerZoom(center[0], center[1])) return;
          if (
            pmtilesBbox &&
            adapter &&
            typeof adapter.flyToBbox === "function" &&
            adapter.flyToBbox(pmtilesBbox, "EPSG:4326")
          ) {
            return;
          }
        }

        // Catalog bbox centre — works from anywhere on the map because we aim
        // at the layer's own lon/lat, not at the current camera pose.
        const center = geographicCenterFromBbox(layer.bbox);
        if (center && flyToLayerZoom(center[0], center[1])) return;

        // Fallbacks when the layer has no usable geographic centre (local CRS,
        // unloaded entity, global extent). Keep prior behaviour so 3D Tiles /
        // BIM still have a path.
        if (adapter && typeof adapter.flyToLayer === "function") {
          if (adapter.flyToLayer(layerId)) return;
        }

        const bbox = layer.bbox;
        const bboxCrs = (layer.crs ?? "EPSG:4326").toUpperCase();
        const safeBboxCrs = bboxCrs === "EPSG:4326" || bboxCrs === "EPSG:3857" || bboxCrs === "OGC:CRS84";
        const looksGeographic = !!bbox && bbox.length >= 4 &&
          Math.abs(bbox[0]) <= 180 && Math.abs(bbox[2]) <= 180 &&
          Math.abs(bbox[1]) <= 90 && Math.abs(bbox[3]) <= 90;
        if (bbox && bbox.length >= 4 && (safeBboxCrs || looksGeographic)) {
          const [minX, minY, maxX, maxY] = bbox;
          const lonSpan = Math.abs(maxX - minX);
          const latSpan = Math.abs(maxY - minY);
          const isGlobal = lonSpan >= 359 || latSpan >= 169;
          if (!isGlobal && adapter && typeof adapter.flyToBbox === "function") {
            if (adapter.flyToBbox(bbox, safeBboxCrs ? bboxCrs : "EPSG:4326")) return;
          }
        }

        const hasProjectFrameScene = catalog.some((candidate) => isThreeDLayer(candidate));
        const shouldUseProjectFrameFallback =
          hasProjectFrameScene &&
          (layer.kind === "terrain" || layer.kind === "vector" || layer.kind === "raster");
        if (
          shouldUseProjectFrameFallback &&
          adapter &&
          typeof adapter.flyToProjectView === "function" &&
          adapter.flyToProjectView()
        ) {
          console.info(
            `[TwinShell] onZoomToLayer used project-frame view for ${layerId} ` +
              `(layer kind '${layer.kind}' has no flyable per-layer centre)`,
          );
          return;
        }

        if (adapter && typeof adapter.flyToProjectView === "function") {
          if (adapter.flyToProjectView()) {
            console.info(
              `[TwinShell] onZoomToLayer fell back to project view for ${layerId}` +
                ` (entity not loaded${safeBboxCrs ? "" : `, bbox CRS '${bboxCrs}' not flyable`})`,
            );
            return;
          }
        }

        onFlyToLayer(layer);
      })();
    },
    [catalog, giro3dAdapter, onFlyToLayer],
  );

  const handleResetView = useCallback(() => {
    const adapter = giro3dAdapter as
      | (typeof giro3dAdapter & {
          flyToBbox?: (bbox: readonly number[], crs?: string) => boolean;
        })
      | null;

    const visibleUnion = unionFlyableGeographicBboxes(
      catalog.filter(
        (layer) =>
          visibleIds.has(layer.id) && hasExplicitGeospatialBounds(layer),
      ),
    );
    if (visibleUnion && adapter?.flyToBbox?.(visibleUnion, "EPSG:4326")) {
      return;
    }

    const catalogUnion = unionFlyableGeographicBboxes(catalog);
    if (catalogUnion && adapter?.flyToBbox?.(catalogUnion, "EPSG:4326")) {
      return;
    }

    const visibleGeoreferenced = catalog.filter(
      (layer) =>
        visibleIds.has(layer.id) && hasExplicitGeospatialBounds(layer),
    );
    const target =
      visibleGeoreferenced[0]
      ?? catalog.find((layer) => hasExplicitGeospatialBounds(layer))
      ?? null;
    if (target) onFlyToLayer(target);
  }, [catalog, giro3dAdapter, onFlyToLayer, visibleIds]);

  /**
   * Zoom-to for group / container nodes (e.g. the "Scene slots" root) that
   * carry no `layerId` of their own. We walk the subtree, collect the
   * descendant scene-item ids, and frame the combined bbox so "Zoom to" on a
   * parent fits everything beneath it instead of being a silent no-op.
   * Returns true when it took action.
   */
  const onZoomToNode = useCallback(
    (nodeId: string): boolean => {
      const root = nodeById.get(nodeId);
      if (!root) return false;

      const layerIds = new Set<string>();
      const visit = (node: UnifiedSceneTreeNode) => {
        for (const id of node.sceneItemIds ?? []) layerIds.add(id);
        for (const ref of node.geometryRefs ?? []) {
          if (ref.sceneItemId) layerIds.add(ref.sceneItemId);
        }
        for (const child of node.children ?? []) visit(child);
      };
      visit(root);

      const targets = [...layerIds]
        .map((id) => catalog.find((l) => l.id === id))
        .filter((l): l is NonNullable<typeof l> => Boolean(l));
      if (targets.length === 0) return false;
      if (targets.length === 1) {
        onZoomToLayer(targets[0].id);
        return true;
      }

      const hasProjectFrameScene = catalog.some((candidate) => isThreeDLayer(candidate));
      const adapter = giro3dAdapter as
        | (typeof giro3dAdapter & {
            flyToBbox?: (bbox: readonly number[], crs?: string) => boolean;
            flyToProjectView?: () => boolean;
          })
        | null;
      if (
        hasProjectFrameScene &&
        targets.some((layer) => layer.kind === "terrain" || layer.kind === "vector" || layer.kind === "raster") &&
        adapter?.flyToProjectView?.()
      ) {
        return true;
      }

      // Combined bbox in lon/lat degrees. EPSG:4326, EPSG:6697 (JGD2011
      // geographic) and OGC:CRS84 all express bbox as degrees; union any
      // layer whose bbox sits inside the lon/lat envelope.
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      let count = 0;
      for (const layer of targets) {
        const bbox = layer.bbox;
        if (!bbox || bbox.length < 4) continue;
        const [x0, y0, x1, y1] = bbox;
        const looksGeographic =
          Math.abs(x0) <= 180 && Math.abs(x1) <= 180 &&
          Math.abs(y0) <= 90 && Math.abs(y1) <= 90;
        if (!looksGeographic) continue;
        if (Math.abs(x1 - x0) >= 359 || Math.abs(y1 - y0) >= 169) continue;
        minX = Math.min(minX, x0, x1);
        minY = Math.min(minY, y0, y1);
        maxX = Math.max(maxX, x0, x1);
        maxY = Math.max(maxY, y0, y1);
        count += 1;
      }

      if (count > 0 && adapter && typeof adapter.flyToBbox === "function") {
        if (adapter.flyToBbox([minX, minY, maxX, maxY], "EPSG:4326")) return true;
      }

      // Fallback: frame the first usable descendant layer.
      onZoomToLayer(targets[0].id);
      return true;
    },
    [nodeById, catalog, giro3dAdapter, onZoomToLayer],
  );

  // G5 fix — wire the scene-tree:zoom-to custom event to onZoomToLayer.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ nodeId: string; layerId?: string | null }>).detail;
      const { nodeId } = detail;
      if (!nodeId) return;
      console.log('[G5] scene-tree:zoom-to nodeId=', nodeId, 'layerId=', detail.layerId);

      // Preferred: the container resolved the layerId for us (keyed by node.id).
      if (detail.layerId) {
        onZoomToLayer(detail.layerId);
        return;
      }

      // V2 hierarchy nodes (PickContext) carry layerId directly.
      const v2Node = pickContextRef.current?.nodeById.get(nodeId);
      if (v2Node?.layerId) {
        onZoomToLayer(v2Node.layerId);
        return;
      }

      // Layer-group synthetic nodes use the pattern "layer-group:<layerId>".
      if (nodeId.startsWith("layer-group:")) {
        onZoomToLayer(nodeId.slice("layer-group:".length));
        return;
      }

      // Legacy UnifiedSceneTreeNode path.
      const legacyNode = nodeById.get(nodeId);
      const legacyLayerId = legacyNode?.sceneItemIds?.[0]
        ?? legacyNode?.geometryRefs?.[0]?.sceneItemId
        ?? null;
      if (legacyLayerId) {
        onZoomToLayer(legacyLayerId);
        return;
      }

      // Group / container node with no direct layer (e.g. the root
      // "Scene slots" node) — fit the combined bbox of its descendants.
      if (onZoomToNode(nodeId)) return;
    };
    window.addEventListener("scene-tree:zoom-to", handler);
    return () => window.removeEventListener("scene-tree:zoom-to", handler);
  }, [nodeById, onZoomToLayer, onZoomToNode]);

  // Phase 2 / RFC-108-G — alternate-group crossfade listener. Fires a 400
  // ms opacity crossfade between the outgoing and incoming alternates,
  // then asks the Giro3D adapter to hide (= unmount, for 3D Tiles) the
  // outgoing layer. The store has already mutated `visibleLayerIds`
  // (mutually-exclusive within a group) by the time we get here.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as AlternateSwitchEvent).detail;
      const adapter = giro3dAdapterRef.current;
      if (!adapter) return;
      console.log('[Phase2] alternate-switch', detail);
      void crossfadeBetweenLayers(
        {
          getLayerObject3D: (id) => adapter.getLayerObject3D(id),
          setLayerVisible: (id, visible) => adapter.setLayerVisible(id, visible),
          notifyChange: () => adapter.notifyChange(),
        },
        detail.fromLayerId,
        detail.toLayerId,
      ).catch((err) => {
        console.warn('[Phase2] alternate crossfade failed', err);
      });
    };
    window.addEventListener(ALTERNATE_SWITCH_EVENT, handler);
    return () => window.removeEventListener(ALTERNATE_SWITCH_EVENT, handler);
  }, []);

  // Dev-mode debug helpers exposed on window.__dev
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    (window as unknown as Record<string, unknown>)["__dev"] = {
      hideFog:   () => syncEnvironmentLayerVisible("layer:environment:fog", false),
      showFog:   () => syncEnvironmentLayerVisible("layer:environment:fog", true),
      camera:    () => useViewerStore.getState().camera,
      catalog:   () => useViewerStore.getState().catalog,
      selectionBus,
    };
    return () => { delete (window as unknown as Record<string, unknown>)["__dev"]; };
  }, [syncEnvironmentLayerVisible]);

  const handleResetNorth = useCallback(() => {
    const map = mapContainer.current
      ? getMapLibreHostHandle(mapContainer.current)
      : null;
    if (!map) return;
    try {
      map.easeTo({ bearing: 0, duration: tokens.motion.duration.normal });
    } catch {
      /* best effort */
    }
  }, []);

  const handleShareView = useCallback(() => {
    if (typeof window === "undefined") return;
    try {
      void navigator.clipboard.writeText(window.location.href);
    } catch {
      /* clipboard may be blocked; ignore */
    }
  }, []);

  const handleOpenGeocoder = useCallback(() => {
    if (typeof window === "undefined") return;
    const map = mapContainer.current
      ? getMapLibreHostHandle(mapContainer.current)
      : null;
    if (!map) return;
    try {
      const center = map.getCenter();
      const query = `${center.lat.toFixed(6)},${center.lng.toFixed(6)}`;
      const url = `https://www.openstreetmap.org/search?query=${encodeURIComponent(query)}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      /* best effort; geocoder launcher should never block other actions */
    }
  }, []);

  const onIsolateLayer = useCallback(
    (layer: Layer) => {
      onSoloLayerAndQueue(layer.id);
      onFlyToLayer(layer);
    },
    [onFlyToLayer, onSoloLayerAndQueue],
  );

  /**
   * Giro3D scene-marker click handler. The user clicked a panorama-bubble
   * station dot rendered in the 3D scene; transition into that station's
   * bubble while preserving the 3D camera yaw for the bubble's initial
   * look direction. Mirrors `onOpenPanorama` and the scene-tree path so
   * all three entry points produce identical state.
   */
  const onPanoramaStationMarkerClick = useCallback(
    (layerId: string, stationId: string) => {
      const layer = catalog.find((l) => l.id === layerId);
      if (!layer || layer.kind !== "panorama-bubble") return;
      const isFreshEntry = !panoramaBubbleView.layerId;
      if (isFreshEntry) {
        const instance = giro3dAdapter?.getInstance() ?? null;
        const captured = captureGiro3DCameraState(instance);
        savedCameraStateRef.current = captured;
        setPanoramaInitialYawRad(captured ? captured.yawRad : null);
      }
      dispatchPanoramaBubbleSelect({
        layerId,
        layerKind: "panorama-bubble",
        stationId,
        source: "engine-pick",
      });
      setPanoramaBubbleView({ layerId, activeStationId: stationId });
      setSceneSelection(
        selectPanoramaBubbleStation(layerId, stationId, { source: "engine-pick" }),
      );
    },
    [
      catalog,
      giro3dAdapter,
      panoramaBubbleView.layerId,
      setPanoramaBubbleView,
      setSceneSelection,
    ],
  );

  /** G8 — bubble depth-pick → scene-tree spatial resolve (survey frame). */
  const onPanoramaSurfacePick = useCallback(
    (worldSurvey: { x: number; y: number; z: number }) => {
      const ctx =
        pickContextRef.current ??
        (sceneObjectIndexRef.current
          ? buildPickContext(sceneObjectIndexRef.current)
          : null);
      if (!ctx) return;

      const resolved = resolveSpatialPickFromSurveyPoint(worldSurvey, ctx);
      if (!resolved) return;

      setSceneSelection(resolved.selection);
      useSceneHierarchyStore.getState().setControlOverlayEntry(
        resolved.node.objectId,
        { highlighted: true },
      );
      selectionBus.publish({
        source: "host",
        ts: Date.now(),
        features: [{
          layerId: resolved.node.layerId ?? panoramaBubbleView.layerId ?? "",
          featureId: String(
            resolved.node.featureId ?? resolved.node.objectId,
          ),
        }],
      });
    },
    [panoramaBubbleView.layerId, setSceneSelection],
  );

  const onOpenPanorama = useCallback(
    (layer: Layer) => {
      if (layer.kind !== "panorama-bubble") return;
      const stations = readPanoramaBubbleStationsFromLayer(layer);
      const firstStationId = stations[0]?.id ?? null;
      // Capture the live Giro3D camera so closing the overlay restores the
      // user's exact 3D view. We only do this on the FIRST entry — i.e.
      // when no bubble layer is currently active. See `selectSceneNodeForTools`
      // for the matching scene-tree-driven path.
      const isFreshEntry = !panoramaBubbleView.layerId;
      if (isFreshEntry) {
        const instance = giro3dAdapter?.getInstance() ?? null;
        const captured = captureGiro3DCameraState(instance);
        savedCameraStateRef.current = captured;
        setPanoramaInitialYawRad(captured ? captured.yawRad : null);
      }
      dispatchPanoramaBubbleSelect({
        layerId: layer.id,
        layerKind: "panorama-bubble",
        stationId: firstStationId ?? undefined,
        source: "scene-tree",
      });
      setPanoramaBubbleView({
        layerId: layer.id,
        activeStationId: firstStationId,
      });
      setSceneSelection(
        selectPanoramaBubbleLayer(layer.id, {
          stationId: firstStationId ?? undefined,
          source: "scene-tree",
        }),
      );
    },
    [
      giro3dAdapter,
      panoramaBubbleView.layerId,
      setPanoramaBubbleView,
      setSceneSelection,
    ],
  );
  onOpenPanoramaRef.current = onOpenPanorama;

  const presetFilteredLayers = useMemo(() => {
    const active = presets.find((p) => p.id === activePreset);
    if (!active) return catalog;
    return catalog.filter((layer) => {
      if (isSyntheticBasemapLayer(layer)) return true;
      const slot = slotMap[layer.id];
      if (!slot) return true;
      if (active.id === "scn-twin-only" && isLocalPointCloudModelLayer(layer, slot)) {
        return true;
      }
      if (active.id === "scn-twin-only" && slot === "realityData" && layer.kind === "3dtiles") {
        return true;
      }
      if (slot === "environment") return true;
      return active.show.includes(slot);
    });
  }, [catalog, presets, activePreset, slotMap]);

  const [plateauStreetDetail, setPlateauStreetDetail] = useState(false);
  const [plateauCameraDistanceM, setPlateauCameraDistanceM] = useState<number | null>(null);

  // Map the live camera distance to a coarse LOD label for the status chip.
  // Thresholds chosen to match the LoR ladder semantics used elsewhere in the
  // viewer: street-level → "high", neighborhood → "balanced", city → "overview",
  // earth-scale → "overview" (clamped).
  useEffect(() => {
    if (plateauCameraDistanceM == null) return;
    let nextLod: ThreeDPerformanceSnapshot['lod'];
    if (plateauCameraDistanceM < 200) nextLod = 'high';
    else if (plateauCameraDistanceM < 1500) nextLod = 'balanced';
    else nextLod = 'reduced';
    setThreeDPerformance((prev) => (prev.lod === nextLod ? prev : { ...prev, lod: nextLod }));
  }, [plateauCameraDistanceM]);

  // Block 1.5 / audit-finding D4 — data-driven Tier-1 scope labels.
  // Region comes from the scene package title (or the project label); site/asset
  // fall back to neutral generics until a scope manifest supplies them. This
  // replaces the previously hardcoded "Tokyo / Shinjuku" breadcrumb so every
  // gTwin shows its OWN locality.
  const lorScope = useMemo<LoRScopeLabels>(
    () => ({
      region: (typeof scenePackage?.title === "string" ? scenePackage.title : undefined) ?? projectLabel ?? undefined,
    }),
    [scenePackage?.title, projectLabel],
  );
  const activeSceneProfile = normalizeSceneProfile(sceneProfile);

  // RFC-114 Phase 1 — derive LoR state from camera distance + current selection + scope
  const lorState = useMemo(
    () => buildLoRState(
      plateauCameraDistanceM,
      selectedSceneNode?.label ?? undefined,
      lorScope,
      activeSceneProfile,
    ),
    [plateauCameraDistanceM, selectedSceneNode?.label, lorScope, activeSceneProfile],
  );

  // RFC-114 LoR4-6 — interior scope state (active layer + hierarchy)
  const [activeScopeState, setActiveScopeState] = useState<LoRScopeState | null>(null);
  const [activeStorey, setActiveStorey] = useState<string>('GF');
  const [activeDisciplines, setActiveDisciplines] = useState<Set<DisciplineId>>(new Set());

  // Effective LoR: use scope state lor when in an explicit interior scope, else camera-based
  const effectiveLoR = activeScopeState?.activeLoR ?? lorState.level;
  const profileFilteredLayers = useMemo(
    () => presetFilteredLayers.filter((layer) =>
      isLayerAllowedForSceneProfile(layer, activeSceneProfile, effectiveLoR),
    ),
    [activeSceneProfile, effectiveLoR, presetFilteredLayers],
  );
  /** Layers actually rendered: intersection of scene preset, profile policy, and user Show/Hide. */
  const mapLayers = useMemo(
    () => profileFilteredLayers.filter((layer) => visibleIds.has(layer.id)),
    [profileFilteredLayers, visibleIds],
  );
  const layoutLegendLayers = useMemo(
    () =>
      mapLayers.map((layer) => {
        const isRaster = layer.kind === "raster" || layer.kind === "terrain";
        const min = rasterToolCard.stretchMin
          ?? rasterToolCard.symbologyDataMin
          ?? 0;
        const max = rasterToolCard.stretchMax
          ?? rasterToolCard.symbologyDataMax
          ?? 255;
        return {
          id: layer.id,
          label: layer.label ?? layer.id,
          kind: layer.kind,
          ...(isRaster
            ? {
                rasterRamp: {
                  cssGradient: getRampPreset(rasterToolCard.colorRamp).cssGradient,
                  minLabel: Number.isInteger(min) ? String(min) : min.toFixed(2),
                  maxLabel: Number.isInteger(max) ? String(max) : max.toFixed(2),
                },
              }
            : {}),
        };
      }),
    [
      mapLayers,
      rasterToolCard.colorRamp,
      rasterToolCard.stretchMin,
      rasterToolCard.stretchMax,
      rasterToolCard.symbologyDataMin,
      rasterToolCard.symbologyDataMax,
    ],
  );
  /**
   * The layout page always composes in 2D: a Canvas frame when the map tab
   * is on a 3D or software-rendering engine, MapLibre when it is already 2D.
   */
  const layoutFrameEngine = useMemo<"maplibre" | "openlayers">(
    () => (engine === "maplibre" || engine === "deckgl" ? "maplibre" : "openlayers"),
    [engine],
  );
  const layoutSeedCamera = useMemo(
    () => ({
      center: (camera?.lonLat ?? [0, 0]) as [number, number],
      zoom: camera?.zoom ?? 2,
      bearing: mapBearingDeg ?? 0,
      scaleDenominator: mapScaleDenominator,
    }),
    [camera?.lonLat, camera?.zoom, mapBearingDeg, mapScaleDenominator],
  );

  // Storeys available in the active scope's child layers
  const scopeChildLayers = useMemo(() => {
    if (!activeScopeState?.activeLayerId) return [];
    return resolveChildScope(catalog, activeScopeState.activeLayerId);
  }, [activeScopeState?.activeLayerId, catalog]);

  const scopeStoreys = useMemo(() => collectStoreys(scopeChildLayers), [scopeChildLayers]);

  // Breadcrumb entries for LoR4+ scope (appended to camera-based crumbs)
  const extendedBreadcrumb = useMemo(() => {
    if (!activeScopeState?.activeLayerId) return lorState.breadcrumb;
    const extra = activeScopeState.label ? [activeScopeState.label] : [];
    return [...lorState.breadcrumb, ...extra];
  }, [activeScopeState, lorState.breadcrumb]);

  // RFC-114 Phase 2+3 — Microscope / Telescope scope navigation + crossfade
  // LoR crossfade (RFC-114 Phase 3) — disabled by default. When enabled, scope
  // transitions fade parent-context tiles to 30% opacity as you drill in.
  // Users can enable it via a future "LoR crossfade" toggle in the viewer
  // settings. For now it stays off so the scene doesn't fog out on zoom.
  const lorCrossfadeEnabled = false;

  const handleMicroscope = useCallback(() => {
    if (!giro3dAdapter) return;

    const drillSourceId = activeScopeState?.activeLayerId
      ?? findFirstDrillableLoR3(catalog)?.id
      ?? null;

    if (drillSourceId) {
      const children = resolveChildScope(catalog, drillSourceId);
      if (children.length > 0) {
        const firstChild = children[0];
        const newState = buildScopeState(catalog, firstChild.id, 4);
        setActiveScopeState(newState);
        if (lorCrossfadeEnabled) startLoRCrossfade(giro3dAdapter.getInstance(), 'microscope');
        return;
      }
    }

    const target = microscopeTargetDistance(lorState.level);
    if (target !== null) {
      if (lorCrossfadeEnabled) startLoRCrossfade(giro3dAdapter.getInstance(), 'microscope');
      giro3dAdapter.animateCameraDistance(target);
    }
  }, [giro3dAdapter, lorState.level, activeScopeState, catalog, lorCrossfadeEnabled]);

  const handleTelescope = useCallback(() => {
    if (!giro3dAdapter) return;

    if (activeScopeState?.activeLayerId) {
      const parent = resolveParentScope(catalog, activeScopeState.activeLayerId);
      if (parent) {
        const parentState = buildScopeState(catalog, parent.id, lorState.level);
        setActiveScopeState(parentState);
        if (lorCrossfadeEnabled) startLoRCrossfade(giro3dAdapter.getInstance(), 'telescope');
        return;
      }
      setActiveScopeState(null);
      setActiveStorey('GF');
      setActiveDisciplines(new Set());
      if (lorCrossfadeEnabled) startLoRCrossfade(giro3dAdapter.getInstance(), 'telescope');
      return;
    }

    const target = telescopeTargetDistance(lorState.level);
    if (target !== null) {
      if (lorCrossfadeEnabled) startLoRCrossfade(giro3dAdapter.getInstance(), 'telescope');
      giro3dAdapter.animateCameraDistance(target);
    }
  }, [giro3dAdapter, lorState.level, activeScopeState, catalog, lorCrossfadeEnabled]);

  const handleDisciplineToggle = useCallback((disc: DisciplineId) => {
    setActiveDisciplines((prev) => {
      const next = new Set(prev);
      if (next.has(disc)) {
        next.delete(disc);
      } else {
        next.add(disc);
      }
      return next;
    });
  }, []);
  const [plateauLayerTileProgress, setPlateauLayerTileProgress] = useState<
    Map<string, { loaded: number; total: number; inFlight: number }>
  >(new Map());
  const lastMountedLayerKeyRef = useRef("");

  useEffect(() => {
    if (!PLATEAU_FEATURES.stableMountPolicy || !giro3dAdapter || engine !== "giro3d") {
      setPlateauStreetDetail(false);
      setPlateauCameraDistanceM(null);
      return;
    }
    const sample = () => {
      const distanceM = giro3dAdapter.getCameraTargetDistanceM();
      setPlateauCameraDistanceM(distanceM);
      setPlateauStreetDetail((previous) =>
        nextPlateauStreetDetailState(previous, distanceM),
      );
      setPlateauLayerTileProgress(giro3dAdapter.getTileProgressByLayer());
    };
    sample();
    const handle = window.setInterval(sample, 500);
    return () => window.clearInterval(handle);
  }, [engine, giro3dAdapter]);

  // Delay between successive detail layer priority groups when entering street mode.
  const STAGGER_DELAY_MS = 4000;

  // Tracks how many detail-layer priority levels are currently allowed to mount.
  // 0 = none (city zoom), 1 = roads, 2 = +furniture, 3 = +ground, 4 = +veg, 5 = +brid, 99 = all.
  // Once unlocked, tiers stay mounted until the user toggles them off (sticky policy).
  const [allowedDetailPriorityThreshold, setAllowedDetailPriorityThreshold] = useState<number>(0);
  const prevStreetDetailRef = useRef(false);
  const staggerTimersRef = useRef<number[]>([]);

  // Staggered detail layer mounting: when street detail activates, mount detail
  // layers one priority group at a time (roads → furniture → veg → brid → rest).
  // On zoom-out, immediately drop all detail layers (no stagger on unmount).
  useEffect(() => {
    const prev = prevStreetDetailRef.current;
    prevStreetDetailRef.current = plateauStreetDetail;

    // Clear any pending stagger timers from the previous run.
    for (const t of staggerTimersRef.current) window.clearTimeout(t);
    staggerTimersRef.current = [];

    if (!plateauStreetDetail) {
      // Sticky policy: keep already-unlocked detail tiers mounted when zooming out.
      return;
    }

    if (!prev) {
      // Transition false → true: stagger detail layers in by priority group.
      const orderedPriorities = [1, 2, 3, 4, 5, 99];
      setAllowedDetailPriorityThreshold(orderedPriorities[0]); // roads mount immediately
      staggerTimersRef.current = orderedPriorities.slice(1).map((priority, index) =>
        window.setTimeout(() => {
          setAllowedDetailPriorityThreshold(priority);
        }, (index + 1) * STAGGER_DELAY_MS),
      );
    }
    // If plateauStreetDetail was already true, don't restart stagger — leave threshold as-is.

    return () => {
      for (const t of staggerTimersRef.current) window.clearTimeout(t);
      staggerTimersRef.current = [];
    };
  }, [plateauStreetDetail]);  

  // Mount superset for the engine. Eye-hide must NOT unmount default-tier
  // layers — remount on every show races an in-flight addLayer and makes the
  // next hide a no-op (layer pops back). Visibility is flipped in-place via
  // `setLayerVisible`. On-demand tiers mount only while visible.
  const userVisibleLayers = useMemo(
    () =>
      catalog.filter((layer) => {
        const visible = visibleIds.has(layer.id);
        const onDemand = evaluateOnDemandTrigger(layer, { visible });
        if (!onDemand.shouldMount) return false;
        if (layer.activationTier !== "on-demand") return true;
        if (visible) return true;
        // Keep basemap layers mounted as a stable background resource. Presets
        // and the layer eye toggle still control actual visibility below, but
        // the layer must be mounted for Map only / Combined to work instantly.
        return slotMap[layer.id] === "basemaps" || isSyntheticBasemapLayer(layer) || isEnvironmentLayer(layer);
      }),
    [catalog, slotMap, visibleIds],
  );

  const layerAlignmentRejections = useMemo(
    () =>
      catalog
        .map((layer) => {
          const event = layerCrs[layer.id];
          if (!event?.rejected) return null;
          return {
            layerId: layer.id,
            label: layer.label ?? layer.id,
            event,
          };
        })
        .filter((entry): entry is NonNullable<typeof entry> => entry !== null),
    [catalog, layerCrs],
  );

  // RFC-108-G Phase 2: an alternate that has been EXPLICITLY chosen by the
  // operator (via the alternate dropdown or the eye toggle on a group
  // member) is the active member of its group and represents user intent —
  // it must mount regardless of the PLATEAU deferred-detail / LOD-1/3/4
  // gate that would otherwise filter it out. Without this, switching from
  // `bldg-lod2` → `bldg-lod1` at altitude would unmount LOD2, never mount
  // LOD1, and leave the operator with no buildings at all.
  const activeAlternateByGroup = useViewerStore((s) => s.activeAlternateByGroup);
  const activeAlternateIds = useMemo(
    () => new Set(activeAlternateByGroup.values()),
    [activeAlternateByGroup],
  );

  // Buildings + Bridges (primary layers) always stay mounted; detail layers mount
  // incrementally based on allowedDetailPriorityThreshold to protect cache.
  const mountedMapLayers = useMemo(() => {
    if (!PLATEAU_FEATURES.stableMountPolicy) return [...userVisibleLayers];
    if (allowedDetailPriorityThreshold === 0) {
      return userVisibleLayers.filter((layer) => {
        if (isPlateauBasemapLayer(layer)) return true;
        if (activeAlternateIds.has(layer.id)) return true;
        return !isPlateauDeferrableDetailLayer(layer.id, layer.label, layer.style, layer.kind);
      });
    }
    return userVisibleLayers.filter((layer) => {
      if (isPlateauBasemapLayer(layer)) return true;
      if (activeAlternateIds.has(layer.id)) return true;
      if (!isPlateauDeferrableDetailLayer(layer.id, layer.label, layer.style, layer.kind)) return true;
      return getDetailLayerPriority(layer.id, layer.label) <= allowedDetailPriorityThreshold;
    });
  }, [userVisibleLayers, allowedDetailPriorityThreshold, activeAlternateIds]);

  const mountedLayerKey = useMemo(
    () => mountedMapLayers.map((layer) => layer.id).sort().join("|"),
    [mountedMapLayers],
  );

  const orderedMountedMapLayers = useMemo(
    () =>
      [...mountedMapLayers]
        // panorama-bubble layers are rendered by the React <PanoramaBubbleView>
        // overlay, not by a Giro3D/MapLibre engine adapter. Excluding them here
        // prevents "No adapter supports layer kind 'panorama-bubble'" errors
        // from the engine layer-sync loop.
        .filter((layer) => layer.kind !== "panorama-bubble")
        .sort(comparePlateauLayerMountOrder),
    [mountedMapLayers],
  );

  const presetVisibleLayerIds = useMemo(
    () => new Set(mapLayers.map((layer) => layer.id)),
    [mapLayers],
  );

  // Session-constant: a GPU-less host renders 3D in a reduced-quality profile
  // that leaves out the environment effects, so their panel is hidden too
  // rather than offering toggles that intentionally do nothing.
  const environmentEffectsAllowed = activeRenderQualityProfile().environmentEffects;

  // Mount set only — do NOT fold eye-visibility into this key. Including
  // visible layer ids remounted layers on every hide/show and raced
  // in-flight addLayer against the next hide (second hide appeared broken).
  const layerSyncKey = `${mapHostGeneration}:${mountedLayerKey}`;

  // ADR-017 / G3 — `listDeferredCandidates` now drives this list from the
  // viewer activation-policy module; the PLATEAU street-detail rule is one
  // branch of that policy (see `policy/activationPolicy.ts`). Behaviour is
  // identical to the old `listPlateauDeferredDetailLayers` for PLATEAU
  // scenes; the policy module is the new source of truth for any other
  // deferral that gets added.
  const deferredDetailLayers = useMemo(
    () => listDeferredCandidates(mapLayers, mountedMapLayers),
    [mapLayers, mountedMapLayers],
  );
  const deferredDetailLayerIds = useMemo(
    () => new Set(deferredDetailLayers.map((layer) => layer.id)),
    [deferredDetailLayers],
  );
  const mountedDetailLayerCount = useMemo(
    () =>
      mapLayers.filter(
        (layer) =>
          isPlateauDeferrableDetailLayer(layer.id, layer.label, layer.style, layer.kind)
          && mountedMapLayers.some((mounted) => mounted.id === layer.id),
      ).length,
    [mapLayers, mountedMapLayers],
  );

  // Wave-1 A7 — poll adapter-reported per-layer health every 500 ms and
  // rebroadcast as a record the SceneTreePanel can consume. In mixed mode
  // (Giro3D + MapLibre overlay), each layer should be read from the adapter
  // that actually owns that layer kind.
  useEffect(() => {
    type AdapterEntry = {
      supports?: (kind: Layer['kind']) => boolean;
      getLayerHealth?: (id: string) => 'ready' | 'loading' | 'failed' | 'unsupported';
    };
    const host = hostRef.current as unknown as {
      _adapters?: AdapterEntry[];
      opts?: { adapters?: AdapterEntry[] };
    } | null;
    const adapters = host?._adapters ?? host?.opts?.adapters ?? [];
    if (adapters.length === 0) return;
    const tick = () => {
      setLayerHealth((prev) => {
        let changed = false;
        const next: Record<string, 'ready' | 'loading' | 'failed' | 'unsupported' | 'deferred'> = {
          ...prev,
        };
        for (const layer of catalog) {
          if (layerCrs[layer.id]?.rejected) {
            if (next[layer.id] !== 'failed') {
              next[layer.id] = 'failed';
              changed = true;
            }
            continue;
          }
          if (deferredDetailLayerIds.has(layer.id)) {
            if (next[layer.id] !== 'deferred') {
              next[layer.id] = 'deferred';
              changed = true;
            }
            continue;
          }
          const reporter = adapters.find(
            (adapter: AdapterEntry) =>
              typeof adapter.getLayerHealth === 'function'
              && typeof adapter.supports === 'function'
              && adapter.supports(layer.kind),
          );
          if (!reporter || typeof reporter.getLayerHealth !== 'function') continue;
          const h = reporter.getLayerHealth(layer.id);
          if (next[layer.id] !== h) {
            next[layer.id] = h;
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    };
    tick();
    const handle = window.setInterval(tick, 500);
    return () => window.clearInterval(handle);
  }, [giro3dAdapter, catalog, engine, mapHostGeneration, deferredDetailLayerIds, layerCrs]);

  // `needsGeospatialMapAdapter` is no longer used to decide adapter mounting
  // (the giro3d unified-camera mode always mounts the map entity lazily),
  // but the helper is kept exported for future routing logic. Reference it
  // here so the import doesn't get flagged as unused.
  void needsGeospatialMapAdapter;

  // WP-6 wiring — surface the existing 3D object tree + controls panel +
  // performance/diagnostics chips whenever the project carries a
  // renderable BIM / 3D Tiles / glTF layer. Replaces the legacy
  // `engine === 'cesium'` gate used by `AppShell` so the same toolset
  // becomes available under Giro3D-backed Twin scenes.
  const isThreeDPresent = useMemo(
    () => catalog.some((layer) => isThreeDLayer(layer)),
    [catalog],
  );
  const visibleThreeDModelLayers = useMemo(
    () => catalog.filter((layer) => isThreeDLayer(layer) && visibleIds.has(layer.id)),
    [catalog, visibleIds],
  );
  const threeDNodes = useMemo<ThreeDObjectNode[]>(
    () => buildThreeDObjectNodes(catalog, visibleIds),
    [catalog, visibleIds],
  );
  const missingGeometryCount = useMemo(
    () => threeDNodes.filter((node) => node.hasMissingGeometry).length,
    [threeDNodes],
  );
  const selectedThreeDLayer = useMemo(() => {
    if (!selection?.layerId) return null;
    const layer = catalog.find((candidate) => candidate.id === selection.layerId);
    return layer && isThreeDLayer(layer) ? layer : null;
  }, [catalog, selection?.layerId]);
  const toneAvailable = Boolean(
    typeof tokens.color.canvas === "object"
    && "threeDNeutral" in (tokens.color.canvas as Record<string, unknown>),
  );
  const effectiveThreeDCanvasTone =
    isThreeDPresent && toneAvailable ? threeDCanvasTone : "light";

  const handleSelectThreeDNode = useCallback(
    (node: ThreeDObjectNode) => {
      setSelection({ layerId: node.layerId, featureId: node.id });
      selectionBus.publish({
        source: "host",
        ts: Date.now(),
        features: [{ layerId: node.layerId, featureId: node.id }],
      });
      setSceneSelection(
        selectByLayerId(node.layerId, {
          layerKind: catalog.find((l) => l.id === node.layerId)?.kind,
          sceneItemId: node.layerId,
          featureId: node.id,
          source: "tool-card",
        }),
      );
    },
    [catalog, setSceneSelection, setSelection],
  );
  const handleToggleThreeDNodeVisibility = useCallback(
    (node: ThreeDObjectNode) => {
      setVisible(node.layerId, !node.visible);
      setThreeDControlState((previous) => ({ ...previous, hidden: node.visible }));
    },
    [setVisible],
  );

  // ─── Live ControlsPanel ↔ Giro3D adapter bridges ──────────────────────────
  // Translates the local toggle state into actual engine mutations on
  // the active Giro3D adapter. Section/clip use the canonical
  // `setSectionPlane` / `setClippingBox` surfaces; isolate/showAll
  // route through the existing tree mutators with a synthesised node.
  // Where the adapter doesn't yet expose a primitive (exploded view,
  // lighting/shadows/sun), the local state is still updated so the
  // panel reflects the user's intent — a future WP wires those into
  // the renderer.
  const sectionOffsetToPosition = useCallback((value: number) => {
    // ControlsPanel ships an offset slider in −100..100 (UI units).
    // The engine's section-plane spec expects a 0..1 position along
    // the chosen axis. Map linearly with 0 → 0.5 (centred).
    return Math.max(0, Math.min(1, (value + 100) / 200));
  }, []);

  const applyLiveSectionPlane = useCallback(
    (enabled: boolean, offset: number) => {
      const adapter = giro3dAdapter;
      if (!adapter?.setSectionPlane) return;
      if (!enabled) {
        adapter.setSectionPlane(null);
        return;
      }
      adapter.setSectionPlane({
        axis: "z",
        position: sectionOffsetToPosition(offset),
        flip: false,
      });
    },
    [giro3dAdapter, sectionOffsetToPosition],
  );

  const applyLiveClippingBox = useCallback(
    (enabled: boolean, offset: number) => {
      const adapter = giro3dAdapter;
      if (!adapter?.setClippingBox) return;
      if (!enabled) {
        adapter.setClippingBox(null);
        return;
      }
      const inset = Math.max(0, Math.min(0.49, Math.abs(offset) / 200));
      adapter.setClippingBox({
        ...DEFAULT_CLIPPING_BOX,
        xMin: inset,
        xMax: 1 - inset,
        yMin: inset,
        yMax: 1 - inset,
        zMin: inset,
        zMax: 1 - inset,
      });
    },
    [giro3dAdapter],
  );

  const handleControlsToggleSection = useCallback(() => {
    setThreeDControlState((previous) => {
      const next = !previous.sectionPlaneEnabled;
      applyLiveSectionPlane(next, previous.sectionOffset);
      return { ...previous, sectionPlaneEnabled: next };
    });
  }, [applyLiveSectionPlane]);

  const handleControlsChangeSectionOffset = useCallback(
    (value: number) => {
      setThreeDControlState((previous) => {
        if (previous.sectionPlaneEnabled) {
          applyLiveSectionPlane(true, value);
        }
        return { ...previous, sectionOffset: value };
      });
    },
    [applyLiveSectionPlane],
  );

  const handleControlsToggleClipBox = useCallback(() => {
    setThreeDControlState((previous) => {
      const next = !previous.clipBoxEnabled;
      applyLiveClippingBox(next, previous.clipOffset);
      return { ...previous, clipBoxEnabled: next };
    });
  }, [applyLiveClippingBox]);

  const handleControlsChangeClipOffset = useCallback(
    (value: number) => {
      setThreeDControlState((previous) => {
        if (previous.clipBoxEnabled) {
          applyLiveClippingBox(true, value);
        }
        return { ...previous, clipOffset: value };
      });
    },
    [applyLiveClippingBox],
  );

  const handleControlsToggleIsolate = useCallback(() => {
    const adapter = giro3dAdapter;
    setThreeDControlState((previous) => {
      const next = !previous.isolated;
      if (next && selection?.layerId) {
        adapter?.isolateNode?.({
          id: selection.layerId,
          name: selection.layerId,
          visible: true,
          featureId: selection.featureId ? String(selection.featureId) : undefined,
        });
      } else {
        adapter?.showAllNodes?.();
      }
      return { ...previous, isolated: next };
    });
  }, [giro3dAdapter, selection?.layerId, selection?.featureId]);

  const handleControlsToggleHidden = useCallback(() => {
    setThreeDControlState((previous) => {
      const next = !previous.hidden;
      if (selection?.layerId) {
        setVisible(selection.layerId, !next);
      }
      return { ...previous, hidden: next };
    });
  }, [selection?.layerId, setVisible]);

  const handleControlsToggleGhosted = useCallback(() => {
    setThreeDControlState((previous) => {
      const next = !previous.ghosted;
      const adapter = giro3dAdapter;
      adapter?.setRenderMode?.(next ? "xray" : "default");
      return { ...previous, ghosted: next };
    });
  }, [giro3dAdapter]);

  // AnalysisStrip uses the adapter directly through its narrow contract.
  // Wrap the live adapter so the strip never closes over Three.js types.
  const analysisStripAdapter = useMemo(
    () => giro3dAdapter ?? null,
    [giro3dAdapter],
  );

  // Mount / re-mount the viewer host whenever the active engine changes.
  useEffect(() => {
    let cancelled = false;
    if (!mapContainer.current) return;
    // Both 2D (MapLibre) and 3D (Giro3D) adapters mount canvases as absolute
    // children of this host container; make sure it is a positioned ancestor
    // so their z-indexes are honoured.
    mapContainer.current.style.position = "relative";

    void (async () => {
      const adapters = (() => {
        switch (engine) {
          case "cesium": {
            return [new CesiumAdapter(), new ThreeAdapter()];
          }
          case "three": {
            return [new ThreeAdapter(), new CesiumAdapter()];
          }
          case "giro3d": {
            const g3d = new Giro3DAdapter();
            setGiro3dAdapter(g3d);
            setPotreeAdapter(null);
            const ml = new MapLibreAdapter({ vectorOverlayMode: true });
            maplibreAdapterRef.current = ml;
            // Unified single-camera: Giro3D renders BOTH the 2D imagery
            // basemap (as a ColorLayer on its Map entity) and the 3D model /
            // point cloud / 3D Tiles in the same scene. One camera, one
            // canvas — rotate/zoom moves everything together (Google Earth
            // style).
            //
            // MapLibre remains mounted in `vectorOverlayMode` only for 2D /
            // preview / fallback vector paths. PMTiles vectors route to
            // Giro3D's native PMTiles/MVT path so they share the same render
            // frame as terrain, basemaps, and 3D Tiles.
            return [g3d, ml];
          }
          case "potree": {
            const potree = new PotreeAdapter();
            setPotreeAdapter(potree);
            setGiro3dAdapter(null);
            maplibreAdapterRef.current = null;
            return [potree, new CesiumAdapter()];
          }
          case "openlayers": {
            setGiro3dAdapter(null);
            setPotreeAdapter(null);
            maplibreAdapterRef.current = null;
            return [new CanvasMapAdapter()];
          }
          case "deckgl":
          case "maplibre":
          default: {
            setGiro3dAdapter(null);
            setPotreeAdapter(null);
            const ml = new MapLibreAdapter();
            maplibreAdapterRef.current = ml;
            return [ml, new DeckGLAdapter()];
          }
        }
      })();
      const host = new ViewerHost({
        container: mapContainer.current!,
        primaryEngine: engine,
        adapters,
      });
      try {
        await host.mount();
        if (!cancelled) setEngineMountError(null);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        const isWebGlFailure =
          /webgl|WebGL|BindToCurrentSequence|graphics card|software-gl|SwiftShader|llvmpipe|major-performance|unsuitable for MapLibre/i.test(
            message,
          );
        console.error("[TwinShell] engine mount failed", err);
        console.error("[TwinShell] mount error detail", message);
        // GPU-less VMs (Mesa/llvmpipe / SwiftShader): fall through to Canvas 2D
        // OpenLayers so basemap + COG tiles still render without freezing.
        if (isWebGlFailure && engine !== "openlayers") {
          console.warn(
            "[TwinShell] WebGL unsuitable — switching to Canvas 2D (openlayers) fallback",
          );
          try {
            window.sessionStorage.setItem("geoviewer.canvasFallback", "1");
          } catch {
            /* ignore */
          }
          setEngineMountError(null);
          setEngine("openlayers");
          return;
        }
        if (engine !== "maplibre" && engine !== "openlayers") {
          setEngine("maplibre");
          setEngineMountError(null);
          return;
        }
        setEngineMountError(
          isWebGlFailure
            ? "Map could not start: this VM has no usable hardware WebGL. Using Canvas 2D mode instead (refresh if the map is blank)."
            : `Map engine failed to start: ${message}`,
        );
        return;
      }
      if (cancelled) {
        await host.unmount();
        return;
      }
      hostRef.current = host;
      host.on((event) => {
        if (event.type === "cameraChange") {
          if (isPlaceholderCamera(event.camera)) return;
          lastHostCameraSignatureRef.current = cameraSignature(event.camera);
          // Canvas 2D path: throttle Zustand camera writes. TwinShell is huge;
          // writing camera on every moveend forces expensive React work that
          // feels like "the map is lagging" even when tiles are streaming fine.
          if (engineRef.current === "openlayers") {
            pendingCanvasCameraRef.current = event.camera;
            if (canvasCameraTimerRef.current == null) {
              canvasCameraTimerRef.current = setTimeout(() => {
                canvasCameraTimerRef.current = null;
                const pending = pendingCanvasCameraRef.current;
                if (pending) setCamera(pending);
              }, 500);
            }
            return;
          }
          setCamera(event.camera);
          // Keep the MapLibre vector overlay aligned to the primary 3D camera so
          // toggled-on PMTiles/GeoJSON vectors render over the area in view. The
          // adapter self-gates to overlay mode, so this is a no-op when MapLibre
          // is the primary engine.
          maplibreAdapterRef.current?.syncOverlayCamera?.(event.camera);
        }
        if (event.type === "select") {
          setSelection(event.selection);
          const picked = event.selection;

          if (picked?.layerId && picked.layerId !== "__map__" && picked.featureId != null) {
            const pickedLayer = useViewerStore
              .getState()
              .catalog.find((layer) => layer.id === picked.layerId);
            const canvasAdapter = mapContainer.current
              ? getCanvasMapHostHandle(mapContainer.current)
              : null;
            const maplibreFeatures =
              maplibreAdapterRef.current?.getVectorFeatures(picked.layerId) ?? [];
            const canvasFeatures = canvasAdapter?.getVectorFeatures(picked.layerId) ?? [];
            if (
              pickedLayer?.kind === "vector"
              || maplibreFeatures.length > 0
              || canvasFeatures.length > 0
            ) {
              setSelectedSceneNode(null);
              const wanted = String(picked.featureId);
              const cached = [...maplibreFeatures, ...canvasFeatures].find((feature) => {
                const id =
                  feature.id
                  ?? feature.properties?.id
                  ?? feature.properties?.OBJECTID_1
                  ?? feature.properties?.OBJECTID
                  ?? feature.properties?.gml_id;
                return id != null && String(id) === wanted;
              });
              const renderedProps =
                maplibreAdapterRef.current?.getLastVectorPickProperties?.()
                ?? canvasAdapter?.getLastVectorPickProperties?.()
                ?? {};
              const props = {
                ...(maplibreAdapterRef.current?.getFeatureProperties(picked.layerId, wanted) ?? {}),
                ...(cached?.properties ?? {}),
                ...renderedProps,
              };
              const pickPoint =
                picked.point
                && picked.point.length >= 2
                && Number.isFinite(picked.point[0])
                && Number.isFinite(picked.point[1])
                  ? ([picked.point[0], picked.point[1]] as const)
                  : undefined;
              const vectorSelection = selectByLayerId(picked.layerId, {
                featureId: picked.featureId,
                layerKind: "vector",
                featureProperties: props,
                pickPoint,
                source: "engine-pick",
              });
              setSceneSelection(vectorSelection);
              setSelectionData({
                featureId: wanted,
                properties: props,
              });
              setSelectionCount(1);
              selectionBus.publish({
                source: "maplibre",
                ts: Date.now(),
                features: [{
                  layerId: picked.layerId,
                  featureId: wanted,
                  metadata: Object.keys(props).length > 0 ? props : undefined,
                }],
              });
              return;
            }
          }

          // INIT-2026-Q3-SCENE-HIERARCHY geometry binding: when a 3D pick
          // fires and the gTwin has an embedded object-index, try to resolve
          // the hit glTF node name → TreeNode → SceneSelection so the tree
          // highlights the picked element. Uses the adapter's last-picked-
          // object-name captured during the pick event.
          const activeObjectIndex = sceneObjectIndexRef.current;
          const adapterForPick = giro3dAdapterRef.current as unknown as {
            getLastPickedObjectName?: () => string | null;
            getGmlIdForBatchId?: (batchId: string | null | undefined) => string | null;
          } | null;
          // Use the giro3dAdapterRef (stable ref to the Giro3D adapter) to
          // read the last-picked glTF node name without re-creating the closure.
          const lastPickedName = adapterForPick?.getLastPickedObjectName?.() ?? null;
          const hitFeatureId = picked?.featureId != null ? String(picked.featureId) : null;
          // CityGML 3D→tree: the engine returns a batch id (e.g. "feature:570").
          // The object-index featureId for this run can be stale, so reverse the
          // batch id to its gml:id via the tile batch table and use that as the
          // match key (resolveGeometryPick checks nodeByNativeId === gml:id).
          const gmlIdFromPick =
            adapterForPick?.getGmlIdForBatchId?.(hitFeatureId) ?? null;
          const hitName = gmlIdFromPick ?? lastPickedName;
          // Pass the raw batch id through; resolveGeometryPick tries the gmlId /
          // nativeId path first (via hitName) and falls back to featureId lookup
          // if the nativeId is not present in the current PickContext. This keeps
          // gml authoritative when available while still allowing cross-block
          // resolution when only the batch id is known.
          const resolveFeatureId = hitFeatureId;
          // Prefer the container-published PickContext (populated from the
          // combined manifest — works for by-URL hierarchies). Fall back to
          // building one from the inline object index for inline-index gTwins.
          const ctx =
            pickContextRef.current ??
            (activeObjectIndex ? buildPickContext(activeObjectIndex) : null);
          const hitLayerId = picked?.layerId ?? null;
          if ((hitName || hitFeatureId) && ctx) {
            let resolved = resolveGeometryPick(hitName, resolveFeatureId, ctx, hitLayerId);
            // If layer-scoped lookup failed (common for multi-block 3D Tiles where
            // hitLayerId may not exactly match node.layerId), retry without layer
            // scoping so the global nodeByFeatureId fallback can resolve the pick.
            if (!resolved && hitFeatureId) {
              resolved = resolveGeometryPick(hitName, resolveFeatureId, ctx, null);
            }
            if (resolved) {
              setSceneSelection(resolved.selection);
              useSceneHierarchyStore.getState().setControlOverlayEntry(
                resolved.node.objectId,
                { highlighted: true },
              );
              const busPayload = {
                source: "host" as const,
                ts: Date.now(),
                features: [{
                  layerId: resolved.node.layerId ?? picked?.layerId ?? "",
                  featureId: String(resolved.node.featureId ?? picked?.featureId ?? resolved.node.objectId),
                }],
              };
              console.log('[G6] selectionBus.publish (resolved pick) →', busPayload);
              selectionBus.publish(busPayload);
              return;
            }
          }

          if (!picked?.layerId) return;
          const pickedLayer = useViewerStore
            .getState()
            .catalog.find((layer) => layer.id === picked.layerId);
          if (!pickedLayer) return;
          const pickedNode = nodeByLayerId.get(picked.layerId) ?? null;
          if (pickedNode) {
            setSelectedSceneNode(pickedNode);
            setSceneSelection(
              selectByIndexNodeId(pickedNode.id, {
                layerId: picked.layerId,
                layerKind: pickedLayer.kind,
                sceneItemId: picked.layerId,
                featureId: picked.featureId,
                nodeKind: pickedNode.kind,
                source: "engine-pick",
              }),
            );
            selectionBus.publish({
              source: "host",
              ts: Date.now(),
              features: [{
                layerId: picked.layerId,
                featureId: String(picked.featureId ?? pickedNode.id),
              }],
            });
            return;
          }
          setSelectedSceneNode(null);
          setSceneSelection(
            selectByLayerId(picked.layerId, {
              layerKind: pickedLayer.kind,
              sceneItemId: picked.layerId,
              featureId: picked.featureId,
              source: "engine-pick",
            }),
          );
          selectionBus.publish({
            source: "host",
            ts: Date.now(),
            features: [{
              layerId: picked.layerId,
              featureId: String(picked.featureId ?? ""),
            }],
          });
        }
        if (event.type === "crsCorrectionApplied") {
          // RFC-112 Phase F: Track CRS alignment data per layer
          setLayerCrs(prev => ({
            ...prev,
            [event.layerId]: event
          }));
        }
      });
      // Publish engine availability to the plugin store so the plugin
      // panels (measurement / profile / viewshed / annotation / export)
      // know they have an engine to talk to.
      setPluginEngineType(engineToPluginEngineType(engine));
      setPluginEngineReady(true);
      setMapHostGeneration((n) => n + 1);
    })();

    return () => {
      cancelled = true;
      if (canvasCameraTimerRef.current != null) {
        clearTimeout(canvasCameraTimerRef.current);
        canvasCameraTimerRef.current = null;
      }
      setPluginEngineReady(false);
      setPluginEngineType(null);
      maplibreAdapterRef.current = null;
      void hostRef.current?.unmount();
      hostRef.current = null;
    };
  }, [
    engine,
    setCamera,
    setSelection,
    setSceneSelection,
    nodeByLayerId,
    setPluginEngineReady,
    setPluginEngineType,
  ]);

  // Q4+Q5+M10: Monitor selection state from Giro3DAdapter
  useEffect(() => {
    if (!giro3dAdapter) {
      return;
    }

    let mounted = true;
    const pollInterval = 100; // Poll every 100ms for responsive UI

    function updateSelectionState() {
      if (!mounted || !giro3dAdapter) return;
      
      const newSelectionData = giro3dAdapter.getSelectionData();
      const newSelectionCount = giro3dAdapter.getSelectionCount();
      
      // Update state only if changed to avoid unnecessary rerenders
      setSelectionData(current => {
        const hasChanged = 
          (!current && newSelectionData) ||
          (current && !newSelectionData) ||
          (current && newSelectionData && current.featureId !== newSelectionData.featureId);
        return hasChanged ? newSelectionData : current;
      });
      
      setSelectionCount(current => current !== newSelectionCount ? newSelectionCount : current);
      
      if (mounted) {
        setTimeout(updateSelectionState, pollInterval);
      }
    }

    updateSelectionState();

    return () => {
      mounted = false;
    };
  }, [giro3dAdapter]);

  // RFC-112 Phase E: Set default scale mode on Giro3DAdapter when mounted
  useEffect(() => {
    if (!giro3dAdapter) return;

    // Configure the default scale mode (engineering for block-tiled CityGML when
    // the gTwin anchor does not override it).
    giro3dAdapter.setDefaultScaleMode(projectScaleMode).catch((err: unknown) => {
      console.warn('[TwinShell] Failed to set default scale mode:', err);
    });
  }, [giro3dAdapter, projectFrameCrs, projectScaleMode]);

  // Keep host camera in sync with store-driven restores (saved views, deep links).
  // Guard against feedback loops by skipping camera signatures that just came from
  // the host itself.
  //
  // Both signature refs describe the *previous* host's canvas, so after an
  // engine swap they would suppress the one flyTo the fresh host needs: Giro3D
  // then sat at the OrbitControls default (null island) and rendered black
  // while the DTM loaded correctly off-screen. Re-apply the pose once per mount
  // generation, before the de-duplication applies.
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !camera) return;
    if (isPlaceholderCamera(camera)) return;
    const signature = cameraSignature(camera);
    const remounted = appliedCameraGenerationRef.current !== mapHostGeneration;
    if (!remounted) {
      if (lastHostCameraSignatureRef.current === signature) return;
      if (lastAppliedCameraSignatureRef.current === signature) return;
    }
    appliedCameraGenerationRef.current = mapHostGeneration;
    host.flyTo?.(camera);
    lastAppliedCameraSignatureRef.current = signature;
  }, [camera, mapHostGeneration]);

  // Wave-4 / A8 — keep `potreeStatus` in sync with the adapter snapshot so
  // the routing helper and the fallback banner stay accurate without
  // reaching into the adapter from the render path. Cheap poll (200 ms)
  // while the adapter is alive; replaced by an event channel once the
  // adapter exposes a status-listener (A12).
  useEffect(() => {
    if (!potreeAdapter) {
      setPotreeStatus("not-installed");
      return;
    }
    setPotreeStatus(potreeAdapter.getBackendStatus());
    const handle = window.setInterval(() => {
      setPotreeStatus(potreeAdapter.getBackendStatus());
    }, 200);
    return () => window.clearInterval(handle);
  }, [potreeAdapter]);

  // Wave-4 / A8 — Giro3D fallback supervisor. Polls the adapter's per-item
  // health snapshot at 500 ms (sufficient resolution for the typed
  // CV_E_COPC_UNSUPPORTED path which surfaces synchronously on `addLayer`)
  // and exposes a `FallbackProposal` whenever an unsupported COPC has a
  // Potree alternate in the same sourceGroupId. Side-effect free —
  // `evaluateFallback` is a pure function and the proposal is rendered via
  // an existing `<Banner severity="warning" />` primitive.
  const [fallbackProposal, setFallbackProposal] =
    useState<FallbackProposal | null>(null);
  useEffect(() => {
    if (!giro3dAdapter || engine !== "giro3d") {
      setFallbackProposal(null);
      return;
    }
    const compute = () => {
      const health = giro3dAdapter.getCopcSceneItems().map((item) => ({
        layerId: item.layerId,
        health: item.health,
        sourceGroupId: item.sourceGroupId,
      }));
      setFallbackProposal(
        evaluateFallback("giro3d", health, scenePackage ?? null),
      );
    };
    compute();
    const handle = window.setInterval(compute, 500);
    return () => window.clearInterval(handle);
  }, [giro3dAdapter, engine, scenePackage]);

  // Sync map layers.
  //
  // Stability rule: hidden 3D Tiles must not be mounted. Mounting hidden
  // PLATEAU Tier-2/flood layers still runs addLayer side effects (CRS align,
  // camera fit, clipping retune), which breaks the original building view.
  // Keep the mounted set to the visible intersection until alternates/overlay
  // switching is side-effect free.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    if (layerSyncKey === lastMountedLayerKeyRef.current) return;
    lastMountedLayerKeyRef.current = layerSyncKey;

    const generation = ++layerSyncGenerationRef.current;
    void (async () => {
      try {
        if (typeof host.listLayers !== "function") return;
        const present = new Set(host.listLayers().map((l) => l.id));
        const mountSet = new Set(orderedMountedMapLayers.map((l) => l.id));
        console.info(
          "[TwinShell] layer sync",
          {
            present: [...present],
            mounted: orderedMountedMapLayers.map((l) => `${l.kind}:${l.id}`),
            presetVisible: [...presetVisibleLayerIds],
          },
        );
        // 1. Drop anything currently mounted that should not be mounted at all
        // (user-hidden or still deferred). Sticky layers (basemap, roads,
        // furniture, ground) stay until the user toggles them off in the panel.
        for (const id of present) {
          if (generation !== layerSyncGenerationRef.current) return;
          if (mountSet.has(id)) continue;
          const meta = catalog.find((layer) => layer.id === id);
          // Terrain stability guard: avoid destructive unmount/remount churn.
          // During hydration/preset reconciliation we can transiently compute a
          // mount set that omits terrain even though the operator did not turn
          // it off, which causes the DEM to flash then disappear. Keep terrain
          // mounted and let visibility reconciliation (or direct setVisible
          // actions) control whether it is shown.
          if (meta?.kind === "terrain") continue;
          if (meta && isPlateauStickyMountLayer(meta)) continue;
          await host.removeLayer(id);
        }
        // 2. Mount layers in priority phases: basemap first, then scene content.
        const { basemap: basemapLayers, scene: sceneLayers } =
          partitionPlateauMountPhases(orderedMountedMapLayers);
        const mountLayer = async (layer: Layer) => {
          if (generation !== layerSyncGenerationRef.current) return;
          const environmentLayer =
            isEnvironmentLayer(layer) || layer.id.startsWith("layer:environment:");
          // Sky, clouds, rain and stars are full-screen shader work that adds no
          // data. On a host with no GPU that is the difference between a usable
          // orbit and a slideshow, so the reduced profile drops them entirely.
          if (environmentLayer && !environmentEffectsAllowed) return;
          // Canvas engine already paints its own basemap — skip duplicate
          // synthetic OSM/Esri catalog layers (extra tile storms on soft VMs).
          // Environment sky/sun layers are Giro3D-only and waste work on Canvas.
          if (
            engineRef.current === "openlayers"
            && (isSyntheticBasemapLayer(layer) || environmentLayer)
          ) {
            return;
          }
          const current = new Set(host.listLayers().map((l) => l.id));
          if (!current.has(layer.id)) {
            try {
              const coordinateOverride = useViewerStore.getState().layerCoordinateOverrides[layer.id];
              const effectiveLayer = effectiveLayerForSession(layer, coordinateOverride);
              await host.addLayer(effectiveLayer);
              if (layer.kind === "vector" && host.getLayerEngineId?.(layer.id) === "maplibre") {
                const currentCamera = useViewerStore.getState().camera;
                if (currentCamera) maplibreAdapterRef.current?.syncOverlayCamera?.(currentCamera);
              }
            } catch (err) {
              if (!layer.id.startsWith("layer:environment:")) {
                console.warn(`[TwinShell] mountLayer failed for ${layer.id}:`, err);
              }
            }
          }
        };
        // 2a. Environment layers mount first — they are in-memory only (no network).
        const envLayers = orderedMountedMapLayers.filter((l) => l.id.startsWith("layer:environment:"));
        const nonEnvLayers = orderedMountedMapLayers.filter((l) => !l.id.startsWith("layer:environment:"));
        for (const layer of envLayers) {
          await mountLayer(layer);
        }
        // Apply initial visibility for environment layers immediately
        for (const layer of envLayers) {
          if (generation !== layerSyncGenerationRef.current) return;
          if (typeof host.setLayerVisible === "function") {
            const shouldBeVisible = visibleIds.has(layer.id);
            if (shouldBeVisible) {
              await host.setLayerVisible(layer.id, true);
            }
          }
        }
        const { basemap: basemapLayersFiltered, scene: sceneLayersFiltered } =
          partitionPlateauMountPhases(nonEnvLayers.length > 0 ? nonEnvLayers : []);
        const actualBasemapLayers = basemapLayersFiltered.length > 0 ? basemapLayersFiltered : basemapLayers.filter((l) => !l.id.startsWith("layer:environment:"));
        const actualSceneLayers = sceneLayersFiltered.length > 0 ? sceneLayersFiltered : sceneLayers.filter((l) => !l.id.startsWith("layer:environment:"));
        const pauseBetweenStages = () =>
          new Promise<void>((resolve) => {
            requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
          });
        const waitForBasemapReady = async () => {
          if (basemapLayers.length === 0) return;
          const deadline = Date.now() + 2500;
          while (Date.now() < deadline) {
            if (generation !== layerSyncGenerationRef.current) return;
            const ready = basemapLayers.every((layer) => {
              const health = giro3dAdapter?.getLayerHealth?.(layer.id);
              return health === 'ready';
            });
            if (ready) return;
            await new Promise<void>((resolve) => window.setTimeout(resolve, 80));
          }
        };
        for (const layer of actualBasemapLayers) {
          await mountLayer(layer);
        }
        // Giro3D only — Canvas/MapLibre must not wait on 3D basemap health.
        if (engineRef.current === "giro3d") {
          await waitForBasemapReady();
          await pauseBetweenStages();
        }
        const { roads, ground, buildings, other } = partitionSceneMountStages(actualSceneLayers);
        // Canvas soft-VM: mount scene rasters immediately (streamed tiles only).
        // Skip staged pauses that delay first paint for no GPU benefit.
        const canvasFast = engineRef.current === "openlayers";
        for (const layer of roads) {
          await mountLayer(layer);
        }
        if (!canvasFast && roads.length > 0) await pauseBetweenStages();
        for (const layer of ground) {
          await mountLayer(layer);
        }
        if (!canvasFast && ground.length > 0) await pauseBetweenStages();
        for (const layer of buildings) {
          await mountLayer(layer);
        }
        if (!canvasFast && buildings.length > 0) await pauseBetweenStages();
        for (const layer of other) {
          await mountLayer(layer);
        }
        // 3. Reconcile live visibility in-place from the *current* store, not
        // the closed-over `visibleIds` from effect start — an older generation
        // must not revive a layer the user just hid.
        const liveVisible = useViewerStore.getState().visibleLayerIds;
        for (const layer of orderedMountedMapLayers) {
          if (generation !== layerSyncGenerationRef.current) return;
          if (typeof host.setLayerVisible === "function") {
            await host.setLayerVisible(layer.id, liveVisible.has(layer.id));
          }
        }
      } catch (err) {
        console.warn("[TwinShell] layer sync failed", err);
      }
    })();
  }, [
    layerSyncKey,
    mapHostGeneration,
    catalog,
    orderedMountedMapLayers,
    presetVisibleLayerIds,
    visibleIds,
    giro3dAdapter,
    environmentEffectsAllowed,
  ]);

  // Auto-fly after initial mount. Prefer the first *visible* flyable layer;
  // when nothing is visible (all on-demand hidden), frame the union of every
  // registered layer bbox so the camera never sits at null island [0,0].
  // Canvas / MapLibre twins (vector-only Kerala, etc.) must fly too — this
  // used to require Giro3D and left those projects on the Dubai default view.
  useEffect(() => {
    if (initialFlyDoneRef.current) return;
    if (!catalog.length) return;

    const visibleTarget = catalog.find((layer) => {
      if (!visibleIds.has(layer.id)) return false;
      if (layer.kind === "raster" && layer.id.includes("basemap")) return false;
      return isFlyableGeographicBbox(layer.bbox);
    });

    const bbox =
      visibleTarget?.bbox ??
      unionFlyableGeographicBboxes(catalog) ??
      null;
    const flyLayerId = visibleTarget?.id ?? "catalog-union";

    if (!bbox) return;

    const flyWithActiveEngine = (extent: readonly number[]): boolean => {
      const giro = giro3dAdapter as
        | (typeof giro3dAdapter & {
            flyToBbox?: (next: readonly number[], crs?: string) => boolean;
          })
        | null;
      if (giro && typeof giro.flyToBbox === "function") {
        return Boolean(giro.flyToBbox(extent, "EPSG:4326"));
      }
      const canvas = mapContainer.current
        ? getCanvasMapHostHandle(mapContainer.current)
        : null;
      if (canvas?.flyToBbox?.(extent, "EPSG:4326")) return true;
      const map = mapContainer.current
        ? getMapLibreHostHandle(mapContainer.current)
        : null;
      if (map && typeof map.fitBounds === "function") {
        try {
          map.fitBounds(
            [
              [extent[0], extent[1]],
              [extent[2], extent[3]],
            ],
            { padding: 48, duration: 0 },
          );
          return true;
        } catch {
          return false;
        }
      }
      return false;
    };

    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    const tryFly = () => {
      if (cancelled || initialFlyDoneRef.current) return;
      attempts += 1;
      const ok = flyWithActiveEngine(bbox);
      if (ok) {
        initialFlyDoneRef.current = true;
        console.info(
          `[TwinShell] initial flyToBbox succeeded for ${flyLayerId}`,
          bbox,
        );
      } else if (attempts < 10) {
        timer = setTimeout(tryFly, 300);
      } else {
        console.info(
          `[TwinShell] initial flyToBbox gave up for ${flyLayerId}`,
          bbox,
        );
      }
    };
    tryFly();
    return () => {
      cancelled = true;
      if (timer != null) clearTimeout(timer);
    };
  }, [catalog, visibleIds, giro3dAdapter, mapHostGeneration]);

  // ─── WP-11: unified keyboard shortcuts ────────────────────────────────────
  // Wire the cross-shell shortcut keymap on the Twin route. Closes the
  // WP-2 verdict §10 gap that "TwinShell does not bind S/P/A/M". The
  // hook listens at the document level (skipping form-field targets)
  // and dispatches a `viewer:shortcut` `CustomEvent` for every
  // registered shortcut whose `event` field is set; we translate
  // those events into MapLibre fly-to / pan / zoom actions so the
  // Twin shell honours the same M / A / P / S / F / L bindings as
  // AppShell.
  const setMapTool = useViewerStore((s) => s.setMapTool);
  useUnifiedKeyboardShortcuts("twin");
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onShortcut = (event: Event) => {
      const detail = (event as CustomEvent<ViewerShortcutDetail>).detail;
      if (!detail) return;
      const map = mapContainer.current
        ? getMapLibreHostHandle(mapContainer.current)
        : null;
      switch (detail.action) {
        case "tool-pan":
          setMapTool("pan");
          break;
        case "tool-measure":
          setMapTool("measure");
          break;
        case "tool-area":
          setMapTool("area");
          break;
        case "tool-select":
          setMapTool("select");
          break;
        case "zoom-in":
          if (map) map.zoomIn?.();
          break;
        case "zoom-out":
          if (map) map.zoomOut?.();
          break;
        case "fit-to-visible": {
          // Fit to the union of all visible layers' bbox.
          const visible = catalog.filter((layer) => visibleIds.has(layer.id));
          for (const layer of visible) {
            if (hasExplicitGeospatialBounds(layer)) {
              onFlyToLayer(layer);
              break;
            }
          }
          break;
        }
        case "focus-layers": {
          const root = document.getElementById("layers");
          (root ?? document.querySelector('[aria-label="Unified scene tree"]'))
            ?.querySelector<HTMLElement>("input,button,[tabindex]")
            ?.focus();
          break;
        }
        case "lor-home": {
          handleResetView();
          break;
        }
        default:
          break;
      }
    };
    document.addEventListener(VIEWER_SHORTCUT_EVENT, onShortcut);
    return () => {
      document.removeEventListener(VIEWER_SHORTCUT_EVENT, onShortcut);
    };
  }, [catalog, handleResetView, onFlyToLayer, setMapTool, visibleIds]);

  const legacyToolbarFlag = useLegacyToolbarFlag();
  const layoutTier = useLayoutTier();
  const isEmbeddedEstate = shellMode === 'embedded-estate';
  const showHeader = !isEmbeddedEstate;
  const showLeftRail = !isEmbeddedEstate && (layoutTier === 'full' || layoutTier === 'compact');
  const showRightRail = !isEmbeddedEstate && layoutTier !== 'mobile';
  const leftRailCollapsed = layoutTier === 'compact';
  const [, setLeftRailOpen] = useState(true);

  const leftDock = useDockablePanel({
    storageKey: "viewer.dockable.left-rail",
    defaultSlot: "left-rail",
    // Scene Hierarchy tree needs more room to show node labels without
    // truncation. 380px default; user can drag wider via DragHandle.
    defaultWidth: 380,
    defaultHeight: 600,
    railWidth: 380,
  });
  const rightDock = useDockablePanel({
    storageKey: "viewer.dockable.right-rail",
    defaultSlot: "right-rail",
    defaultWidth: tokens.layout.rightPanel,
    defaultHeight: 600,
    railWidth: tokens.layout.rightPanel,
  });
  const statusDock = useDockablePanel({
    storageKey: "viewer.dockable.status-card",
    defaultSlot: "floating",
    defaultWidth: 280,
    defaultHeight: 300,
    railWidth: 280,
  });

  // Rail collapse toggles — independent of the dockable drag state. When
  // collapsed, the grid column shrinks to 0 and a small chevron tab stays
  // visible on the edge so the user can bring the rail back.
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);

  // Drag-resize state for the left and right panel widths.
  const [leftPanelWidth, setLeftPanelWidth] = useState<number>(tokens.layout.rightPanel);
  const [rightPanelWidth, setRightPanelWidth] = useState<number>(tokens.layout.rightPanel);
  const leftResizeDragRef = useRef<{ startX: number; startW: number } | null>(null);
  const rightResizeDragRef = useRef<{ startX: number; startW: number } | null>(null);
  const onLeftResizePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    leftResizeDragRef.current = { startX: e.clientX, startW: leftPanelWidth };
    const onMove = (ev: PointerEvent) => {
      if (!leftResizeDragRef.current) return;
      const delta = ev.clientX - leftResizeDragRef.current.startX;
      const next = Math.min(Math.max(leftResizeDragRef.current.startW + delta, 200), window.innerWidth * 0.6);
      setLeftPanelWidth(next);
    };
    const onUp = () => {
      leftResizeDragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, [leftPanelWidth]);
  const onRightResizePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    rightResizeDragRef.current = { startX: e.clientX, startW: rightPanelWidth };
    const onMove = (ev: PointerEvent) => {
      if (!rightResizeDragRef.current) return;
      const delta = ev.clientX - rightResizeDragRef.current.startX;
      const next = Math.min(Math.max(rightResizeDragRef.current.startW - delta, 200), window.innerWidth * 0.6);
      setRightPanelWidth(next);
    };
    const onUp = () => {
      rightResizeDragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, [rightPanelWidth]);
  const leftIsFloating = leftDock.state.slot === "floating";
  const rightIsFloating = rightDock.state.slot === "floating";
  const effectiveLeftVisible = showLeftRail && !leftCollapsed && !leftIsFloating;
  const effectiveRightVisible = showRightRail && !rightCollapsed && !rightIsFloating;
  const cursorCoordsForDock = useMemo(
    () =>
      cursorCoords
        ? { lon: cursorCoords[0], lat: cursorCoords[1] }
        : null,
    [cursorCoords],
  );

  // Wave-5 / A10 — point-cloud mode switch. Visible only when the scene
  // package exposes both a Combined-scene path (Giro3D / COPC) and a
  // Point-cloud-only path (Potree / Potree-v2 octree) for the same
  // source group. When either side is missing the toggle hides itself
  // (G8 reversibility — existing single-engine UI is untouched).
  const availableModes = useMemo(
    () => availableModesFor(scenePackage ?? null),
    [scenePackage],
  );
  const currentMode: SceneMode =
    engine === "potree" ? "point-cloud-only" : "combined-scene";
  const localEngineering = useMemo(
    () =>
      catalog.some(
        (layer) =>
          layer.kind === "pointcloud" &&
          layerCoordinateMode(layer) !== "georeferenced",
      ),
    [catalog],
  );
  const [engineSwapPending, setEngineSwapPending] = useState(false);
  // Clear the transient "switching" cue once the ViewerHost has
  // re-mounted on the destination engine — `mapHostGeneration` bumps
  // after a successful mount.
  useEffect(() => {
    if (!engineSwapPending) return;
    setEngineSwapPending(false);
  }, [mapHostGeneration, engineSwapPending]);

  // 2D map ↔ 3D terrain — shown for terrain-slot layers AND for DEM/DTM
  // rasters (converter output often lands as `kind: 'raster'`, which is what
  // the Raster ToolCard opens). Plain imagery / vector scenes never see this
  // control, and Potree / Canvas sessions stay untouched.
  const showTerrainViewSwitch = useMemo(
    () => shouldShowTerrainViewSwitch(engine, catalog),
    [engine, catalog],
  );
  const onTerrainViewChange = useCallback(
    (next: TerrainViewMode) => {
      if (engineSwapPending) return;
      if (next === engine) return;
      setEngineSwapPending(true);
      onEngineChange(next);
    },
    [engine, engineSwapPending, onEngineChange],
  );

  // Preserve view state across the swap. Captured on click, replayed
  // through the existing adapter setters once the new host signals
  // readiness via `mapHostGeneration`. Camera and selection are owned
  // by the viewer store, so they survive the engine swap automatically;
  // this snapshot is the explicit contract for the supervisor audit.
  const preservedStateRef = useRef<ReturnType<typeof preserveState> | null>(
    null,
  );
  const onSceneModeChange = useCallback(
    (mode: SceneMode) => {
      if (mode === currentMode) return;
      const preserved = preserveState(currentMode, mode, {
        camera: useViewerStore.getState().camera ?? null,
        selectionIds:
          sceneSelection.kind === "layer" && sceneSelection.layerId
            ? [sceneSelection.layerId]
            : [],
        classificationFilterIds: (() => {
          const fromStore = useViewerStore.getState().pointCloudClassificationFilter;
          if (fromStore.size > 0) return Array.from(fromStore);
          const fromAdapter =
            giro3dAdapter?.getPointCloudClassificationFilter?.()
            ?? potreeAdapter?.getPointCloudClassificationFilter?.();
          return fromAdapter ? Array.from(fromAdapter) : [];
        })(),
        pointCloudQuality:
          (giro3dAdapter ?? potreeAdapter)?.getPointCloudQuality?.() ?? null,
      });
      preservedStateRef.current = preserved;
      const selection = nextEngineForMode(
        scenePackage ?? null,
        mode,
        potreeStatus,
      );
      const nextEngine: EngineId =
        selection.engine === "potree" ? "potree" : "giro3d";
      if (nextEngine === engine) return;
      setEngineSwapPending(true);
      onEngineChange(nextEngine);
    },
    [
      currentMode,
      engine,
      giro3dAdapter,
      onEngineChange,
      potreeAdapter,
      potreeStatus,
      scenePackage,
      sceneSelection,
    ],
  );
  // After the new host mounts, replay the persisted point-cloud
  // quality + classification filter through the new adapter so the
  // operator's settings survive the swap. Camera + selection live in
  // the Zustand store; the engine adapters already read them on mount.
  useEffect(() => {
    if (engineSwapPending) return;
    const preserved = preservedStateRef.current;
    if (!preserved) return;
    preservedStateRef.current = null;
    const adapter = giro3dAdapter ?? potreeAdapter;
    if (!adapter) return;
    if (preserved.classificationFilterIds.length > 0) {
      adapter.setPointCloudClassificationFilter?.(
        new Set(preserved.classificationFilterIds),
      );
    }
    if (preserved.pointCloudQuality) {
      try {
        adapter.setPointCloudQuality?.(preserved.pointCloudQuality);
      } catch {
        /* adapter may not accept partial — best-effort replay only */
      }
    }
  }, [engineSwapPending, giro3dAdapter, potreeAdapter]);

  const estateReturnUrl = useMemo(() => {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const explicitReturn = params.get("returnTo");
    if (explicitReturn?.startsWith("/admin/estate")) return explicitReturn;
    if (params.get("launch") !== "estate") return null;
    const tenantId = params.get("tenantId");
    if (!tenantId) return "/admin/estate";
    const adminParams = new URLSearchParams({ tenantId });
    const assetId = params.get("assetId");
    if (assetId) adminParams.set("assetId", assetId);
    return `/admin/estate?${adminParams.toString()}`;
  }, []);

  return (
    <div
      data-testid="twin-shell"
      data-shell-mode={shellMode}
      style={{
        display: "grid",
        gridTemplateColumns: (() => {
          const leftW = effectiveLeftVisible
            ? (leftRailCollapsed ? 48 : leftPanelWidth)
            : 0;
          const rightW = effectiveRightVisible ? rightPanelWidth : 0;
          if (layoutTier === 'mobile') return '1fr';
          return `${leftW}px 1fr ${rightW}px`;
        })(),
        gridTemplateRows: showHeader ? `${tokens.layout.bottomBarHeight}px 1fr` : "1fr",
        height: isEmbeddedEstate ? "100%" : "100vh",
        minHeight: 0,
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
      }}
    >
      {/* Header — spans both columns */}
      {showHeader ? (
      <header
        style={{
          gridColumn: "1 / -1",
          gridRow: "1",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: tokens.space[3],
          padding: `0 ${tokens.space[4]}px`,
          borderBottom: tokens.border.thin,
          background: tokens.color.surface[1],
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: tokens.space[3],
            minWidth: 0,
          }}
        >
          <strong style={{ fontSize: tokens.font.size.lg }}>
            GeoSoft GeoViewer
          </strong>
          <span
            style={{
              color: tokens.color.ink.secondary,
              fontSize: tokens.font.size.sm,
            }}
          >
            Project:
          </span>
          <span
            style={{
              fontSize: tokens.font.size.md,
              fontWeight: tokens.font.weight.medium,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 220,
            }}
            title={gtwinId !== projectLabel ? `${projectLabel} (${gtwinId})` : gtwinId}
          >
            {projectLabel === gtwinId
              ? `${gtwinId.slice(0, 10)}…`
              : projectLabel}
          </span>
          <SyncStatusBadge />
        </div>
        <nav
          style={{
            display: "flex",
            alignItems: "center",
            gap: tokens.space[3],
            flexShrink: 0,
          }}
        >
          <TwinGlobalSearch
            sceneTree={sceneTree}
            layers={catalog}
            slotMap={slotMap}
            onSelectNode={selectSceneNodeForTools}
            onFlyToLayer={onFlyToLayer}
          />
          <BookmarkLocationControl />
          <button
            type="button"
            onClick={handleShareView}
            title="Copy this project URL to clipboard"
            style={{
              minHeight: tokens.density.touchMin,
              padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
              border: tokens.border.thin,
              borderRadius: tokens.radius.input,
              background: tokens.color.surface[0],
              color: tokens.color.ink.primary,
              cursor: "pointer",
              fontSize: tokens.font.size.sm,
            }}
          >
            Share link
          </button>
          <ExportGtwinAction
            gtwinId={gtwinId}
            projectLabel={projectLabel}
            packageAdapter={gtwinClient.package}
            mode="thin"
          />
          {estateReturnUrl ? (
            <Link
              to={estateReturnUrl}
              style={{
                color: tokens.color.accent.primary,
                fontSize: tokens.font.size.sm,
                textDecoration: "none",
                fontWeight: tokens.font.weight.medium,
              }}
            >
              Back to hierarchy →
            </Link>
          ) : null}
          <Link
            to={`/studio/t/${gtwinId}`}
            style={{
              color: tokens.color.accent.primary,
              fontSize: tokens.font.size.sm,
              textDecoration: "none",
            }}
          >
            Structure Studio →
          </Link>
          <Link
            to="/projects"
            style={{
              color: tokens.color.accent.primary,
              fontSize: tokens.font.size.sm,
              textDecoration: "none",
            }}
          >
            Open another project →
          </Link>
        </nav>
      </header>
      ) : null}

      {/* Left rail — project-only layer list (no drawers, no SAMPLE_LAYERS).
          WP-15 — `aria-label` so axe's `landmark-unique` rule doesn't
          collide with the right-rail `<aside>` below; both default to
          the implicit `complementary` role.
          Responsive: hidden at 'narrow' / 'mobile', collapsed to 48 px
          icon column at 'compact', full width at 'full'. */}
      {/* Left rail collapse/expand tab */}
      {showLeftRail && !leftIsFloating && (
        <button
          type="button"
          aria-label={leftCollapsed ? "Expand layers panel" : "Collapse layers panel"}
          onClick={() => setLeftCollapsed((v) => !v)}
          style={{
            position: "fixed",
            top: "50%",
            left: effectiveLeftVisible ? (leftRailCollapsed ? 44 : leftPanelWidth - 4) : 0,
            transform: "translateY(-50%)",
            zIndex: tokens.zIndex.dock + 1,
            width: 20,
            height: 48,
            borderRadius: `0 ${tokens.radius.sm}px ${tokens.radius.sm}px 0`,
            border: tokens.border.thin,
            borderLeft: "none",
            background: tokens.color.surface[2],
            color: tokens.color.ink.secondary,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
            transition: `left ${tokens.motion.duration.normal}ms ${tokens.motion.easing.standard}`,
          }}
        >
          <Icon name={leftCollapsed ? "chevron_right" : "arrow_back"} size={16} decorative />
        </button>
      )}

      {/* Left rail panel */}
      {(effectiveLeftVisible || leftIsFloating) && (
      <aside
        aria-label="Project layers and scene tree"
        data-dockable-panel="left-rail"
        style={{
          ...(leftIsFloating
            ? {
                ...leftDock.panelStyle,
                background: tokens.color.surface[1],
                border: tokens.border.thin,
                borderRadius: tokens.radius.md,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
              }
            : {
                position: "relative",
                gridColumn: "1",
                gridRow: "2",
                borderRight: tokens.border.thin,
                background: tokens.color.surface[1],
                overflow: leftRailCollapsed ? "hidden" : "auto",
                paddingTop: leftRailCollapsed ? 0 : tokens.space[1],
                scrollPaddingTop: tokens.space[5],
                width: leftRailCollapsed ? 48 : undefined,
                minWidth: leftRailCollapsed ? undefined : 200,
                maxWidth: "50vw",
                display: "flex",
                flexDirection: "column",
              }),
        }}
      >
        {/* Drag-resize handle — right edge of left panel */}
        {!leftIsFloating && !leftRailCollapsed && (
          <div
            aria-hidden
            onPointerDown={onLeftResizePointerDown}
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 6,
              height: "100%",
              cursor: "col-resize",
              zIndex: tokens.zIndex.dock + 2,
              background: "transparent",
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = (tokens.color.accent?.primary ?? tokens.color.ink.secondary) + "44"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = "transparent"; }}
          />
        )}
        <DragHandle
          onPointerDown={leftDock.onDragHandlePointerDown}
          onDoubleClick={leftDock.snapHome}
          minimized={leftDock.state.minimized}
          onToggleMinimized={leftDock.toggleMinimized}
          label="Layers"
          data-testid="drag-handle-left"
        />
        {leftDock.state.minimized ? null : (
        <>
        {sceneTree ? (
          <>
            {hasExternalRefs ? (
              <PromoteRefsBanner gtwinId={gtwinId} />
            ) : null}
            {unreachableLayers.length > 0 && !relinkDismissed ? (
              <RelinkLayerDialog
                layers={unreachableLayers}
                gtwinId={gtwinId}
                onRelink={handleRelink}
                onDismiss={() => setRelinkDismissed(true)}
              />
            ) : null}
            {unsupportedFormatLayers.length > 0 ? (
              <div
                role="status"
                data-testid="unsupported-format-notices"
                style={{
                  padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
                  borderBottom: tokens.border.thin,
                  background: tokens.color.surface[2],
                  display: "flex",
                  flexDirection: "column",
                  gap: tokens.space[2],
                }}
              >
                {unsupportedFormatLayers.map((layer) => {
                  const reason = String(layer.style?.viewerUnsupportedReason ?? "");
                  return (
                    <div
                      key={layer.id}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: tokens.space[2],
                      }}
                    >
                      <LayerHealthDot
                        health={layerHealth[layer.id] ?? "unsupported"}
                        detail={reason}
                      />
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: tokens.font.size.xs,
                            fontWeight: tokens.font.weight.medium,
                            color: tokens.color.ink.primary,
                          }}
                        >
                          {layer.label ?? layer.id}
                        </div>
                        <p
                          data-testid={`layer-unsupported-${layer.id}`}
                          style={{
                            margin: 0,
                            fontSize: tokens.font.size.xs,
                            color: tokens.color.ink.secondary,
                            lineHeight: 1.35,
                          }}
                        >
                          {reason}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
            {/* Phase 6.5 — feature-flagged Scene Tree V2 takes precedence when enabled */}
            <SceneTreeDropArea
              gtwinId={gtwinId}
              currentUser="unknown" // TODO(currentUser): thread real session identity here
              onPinSuccess={onSceneItemAdded}
            >
              <SceneTreeV2Container
                tree={sceneTree}
                gtwinId={gtwinId}
                selectedNodeId={sceneSelection.indexNodeId ?? null}
                onSelect={handleV2Selection}
                objectIndex={sceneObjectIndex ?? undefined}
                hierarchyUrl={sceneHierarchyUrl ?? undefined}
                sceneHierarchies={sceneHierarchies}
                onPickContextReady={(ctx, triggerPick) => {
                  // Block 2, Task E — TwinShell holds the current PickContext
                  // and the container's geometry-pick entry point so the
                  // engine pick path and tree→engine highlight can use them.
                  pickContextRef.current = ctx;
                  setPickContext(ctx);
                  triggerPickRef.current = triggerPick;
                }}
                onBlockVisibilityChange={(layerId, blockCode, visible) => {
                  hostRef.current?.setBlockVisible(layerId, blockCode, visible);
                }}
                onBlockOpacityChange={(layerId, blockCode, opacity) => {
                  hostRef.current?.setBlockOpacity(layerId, blockCode, opacity);
                }}
                onLayerVisibilityChange={(layerId, visible) => {
                  console.log(`[TwinShell] V2 layer visibility ${layerId} → ${visible}`);
                  onSetVisibleAndQueue(layerId, visible);
                }}
                onLayerOpacityChange={(layerId, opacity) => {
                  console.log(`[TwinShell] V2 layer opacity ${layerId} → ${opacity}`);
                  onSetOpacity(layerId, opacity);
                }}
              />
            </SceneTreeDropArea>
            {engine !== "openlayers"
            && environmentEffectsAllowed
            && catalog.some((layer) => isEnvironmentLayer(layer)) ? (
              <EnvironmentEffectsPanel
                layers={catalog}
                visibleIds={visibleIds}
                onSetVisible={onSetVisibleAndQueue}
                onSetManyVisible={onSetManyVisibleAndQueue}
              />
            ) : null}
            {!sceneHierarchyV2Enabled && !sceneObjectIndex && !sceneHierarchyUrl && sceneHierarchies.length === 0 && (
            <SceneTreePanel
              tree={enrichedSceneTree ?? sceneTree}
              notices={sceneNotices}
              layers={catalog}
              visibleIds={visibleIds}
              layerOpacity={layerOpacity}
              selectedNodeId={selectedSceneNode?.id}
              activePanoramaStationId={panoramaBubbleView.activeStationId}
              onSelectNode={selectSceneNodeForTools}
              onSetVisible={onSetVisibleAndQueue}
              onSetOpacity={onSetOpacity}
              onFlyTo={onFlyToLayer}
              onRemoveLayer={persistentRemoveLayer}
              onAddLayer={() => setAddLayerOpen(true)}
              compact={!legacyToolbarFlag}
              layerHealth={layerHealth}
              slotMap={slotMap}
              onZoomToLayer={onZoomToLayer}
              rasterPaint={rasterPaint}
              onSetRasterPaint={onSetRasterPaint}
              onSetManyVisible={onSetManyVisibleAndQueue}
              onSoloLayer={onSoloLayerAndQueue}
              onMoveLayer={onMoveLayer}
              onSetPointCloudAppearance={onSetPointCloudAppearance}
              onSetPointCloudClassificationFilter={onSetPointCloudClassificationForLayer}
              onSetVectorStyle={onSetVectorStyle}
              onSetVectorLabels={onSetVectorLabelsForLayer}
              onOpenPanorama={onOpenPanorama}
              compactPresetSwitcher={!legacyToolbarFlag ? (
                <ScenePresetSwitcher
                  gtwinId={gtwinId}
                  presets={presets}
                  active={activePreset}
                />
              ) : null}
              compactSavedViewsControl={!legacyToolbarFlag ? (
                <SavedViewsControl
                  container={mapContainer.current}
                  scope={gtwinId}
                  inline
                />
              ) : null}
            />
            )}
            {/* Legacy technical 3D layer list. Normal gTwin navigation uses the
                semantic scene tree above; keep this only for developer fallback
                screens so users do not see two competing trees. */}
            {legacyToolbarFlag && isThreeDPresent ? (
              <ThreeDObjectTree
                nodes={threeDNodes}
                selectedLayerId={selection?.layerId}
                onSelectNode={handleSelectThreeDNode}
                onToggleVisibility={handleToggleThreeDNodeVisibility}
              />
            ) : null}
            {legacyToolbarFlag && selection && selectedThreeDLayer ? (
              <div style={{ padding: tokens.space[2] }}>
                <ThreeDSelectionDetail
                  selection={selection}
                  selectedLayer={selectedThreeDLayer}
                />
              </div>
            ) : null}
          </>
        ) : null}
        {legacyToolbarFlag ? (
          <>
            <SimulationReadinessPanel
              report={readinessReport}
              sceneTree={sceneTree}
              onSelectNode={selectSceneNodeForTools}
            />
            <ProjectLayerList
              layers={catalog}
              slotMap={slotMap}
              visibleIds={visibleIds}
              onSetVisible={onSetVisibleAndQueue}
              onRemove={(layerId: string) => persistentRemoveLayer(
                {} as import("./scenePackage").UnifiedSceneTreeNode,
                catalog.find((l) => l.id === layerId) ?? null,
              )}
              onFlyTo={onFlyToLayer}
              onSetOpacity={onSetOpacity}
              onMove={onMoveLayer}
              onIsolate={onIsolateLayer}
              onShowDetails={(layer) => {
                const node = nodeByLayerId.get(layer.id);
                if (node) selectSceneNodeForTools(node);
              }}
              layerOpacity={layerOpacity}
              onAddLayerClick={() => setAddLayerOpen(true)}
              onSetRasterPaint={onSetRasterPaint}
              rasterPaint={rasterPaint}
              layerCrs={layerCrs}
            />
            <TwinSavedViewsPanel gtwinId={gtwinId} />
          </>
        ) : null}
        </>
        )}
      </aside>
      )}

      {/* Hamburger toggle for collapsed/hidden left rail */}
      {!showLeftRail && (
        <button
          type="button"
          aria-label="Open layers panel"
          onClick={() => setLeftRailOpen((v) => !v)}
          style={{
            position: "fixed",
            top: tokens.layout.bottomBarHeight + tokens.space[2],
            left: tokens.space[2],
            zIndex: tokens.zIndex.dock,
            width: 40,
            height: 40,
            borderRadius: tokens.radius.md,
            border: tokens.border.thin,
            background: tokens.color.surface[1],
            color: tokens.color.ink.primary,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: tokens.elevation[2],
          }}
        >
          <Icon name="menu" size={20} decorative />
        </button>
      )}

      {/* Map canvas */}
      <main
        style={{
          gridColumn: "2",
          gridRow: showHeader ? "2" : "1",
          position: "relative",
          background: tokens.color.surface[0],
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <MapLayoutWorkspace
          view={layoutWorkspaceView}
          onViewChange={setLayoutWorkspaceView}
          frameLayers={mapLayers}
          frameEngine={layoutFrameEngine}
          seedCamera={layoutSeedCamera}
          legendLayers={layoutLegendLayers}
          visibleLayerIds={visibleIds}
          mapPane={
            <div
              data-testid="map-pane-surface"
              style={{ width: "100%", height: "100%", position: "relative" }}
            >
        {/* WP-14 — real-backend / mock-mode status banner. Mounted inside
            `<main>` so it overlays the top of the map canvas without
            stealing space from the rails. The banner subscribes to
            `useSyncStatusStore.realBackendError` for real-mode failures
            and renders the dismissible "Mock SDK active" advisory in
            mock mode (default). */}
        {!isEmbeddedEstate ? (
        <div
          style={{
            position: "absolute",
            top: tokens.space[3],
            left: "50%",
            transform: "translateX(-50%)",
            width: `min(${tokens.layout.standaloneStateWide}px, calc(100% - ${tokens.space[5] * 2}px))`,
            zIndex: tokens.zIndex.panel,
            pointerEvents: "auto",
          }}
        >
          <RealBackendBanner mode={gtwinClient.sceneItemsMode} />
        </div>
        ) : null}
        {/* Wave-4 / A8 — engine fallback advisory. Mounted directly below
            the real-backend banner so the action row stays inside the
            map overlay region. Uses the existing `<Banner />` primitive
            (severity="warning") plus its built-in accent action — no new
            visual primitive, no raw hex / spacing / font / icon emoji. */}
        {!isEmbeddedEstate && fallbackProposal ? (
          <div
            style={{
              position: "absolute",
              top: tokens.space[6],
              left: "50%",
              transform: "translateX(-50%)",
              width: `min(${tokens.layout.standaloneStateWide}px, calc(100% - ${tokens.space[5] * 2}px))`,
              zIndex: tokens.zIndex.panel,
              pointerEvents: "auto",
            }}
            data-testid="engine-fallback-banner"
          >
            <Banner
              severity="warning"
              narrationKey="engine-routing-fallback"
              title="Switch engine to view this point cloud"
              body={fallbackProposal.message}
              errorCode="CV_E_COPC_UNSUPPORTED"
              actions={[
                {
                  label: "Open in Potree",
                  onClick: () => {
                    onEngineChange("potree");
                  },
                },
              ]}
            />
          </div>
        ) : null}
        {layerAlignmentRejections.length > 0 ? (
          <div
            style={{
              position: "absolute",
              top: tokens.space[3],
              right: tokens.space[3],
              left: tokens.space[3],
              // Operations alert: must never hide behind floating dock widgets
              // (Status, camera chip). Centered + above every dock layer.
              zIndex: tokens.zIndex.dock + 10,
              maxWidth: 640,
              marginLeft: "auto",
              marginRight: "auto",
              pointerEvents: "auto",
              boxShadow: tokens.elevation[3] ?? tokens.elevation[2],
            }}
          >
            <LayerAlignmentAlertBanner rejections={layerAlignmentRejections} />
          </div>
        ) : null}
        {catalog.some((layer) => layerCoordinateMode(layer) !== "georeferenced") ? (
          <div
            role="alert"
            style={{
              position: "absolute",
              top: layerAlignmentRejections.length > 0 ? 120 : tokens.space[3],
              left: tokens.space[3],
              zIndex: tokens.zIndex.panel,
              padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
              border: tokens.border.thin,
              borderRadius: tokens.radius.md,
              background: tokens.color.surface[1],
              color: tokens.color.semantic.warning,
              fontSize: tokens.font.size.sm,
              boxShadow: tokens.elevation[2],
            }}
          >
            Local engineering coordinates / not georeferenced.
          </div>
        ) : null}
        {!legacyToolbarFlag && catalog.length > 0 && mapLayers.length === 0 ? (
          <div
            role="status"
            style={{
              position: "absolute",
              left: tokens.space[3],
              bottom: tokens.space[3],
              zIndex: tokens.zIndex.panel,
              padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
              borderRadius: tokens.radius.pill,
              border: tokens.border.thin,
              background: tokens.color.surface[1],
              color: tokens.color.ink.secondary,
              fontSize: tokens.font.size.xs,
              boxShadow: tokens.elevation[2],
              pointerEvents: "none",
            }}
          >
            {presetFilteredLayers.length === 0
              ? "No layers in this scene preset"
              : "All layers hidden"}
          </div>
        ) : null}
        {legacyToolbarFlag && catalog.length > 0 && mapLayers.length === 0 ? (
          <div
            role="status"
            style={{
              position: "absolute",
              inset: tokens.space[4],
              zIndex: tokens.zIndex.panel,
              padding: tokens.space[4],
              borderRadius: tokens.radius.md,
              border: tokens.border.thin,
              background: tokens.color.surface[1],
              color: tokens.color.ink.secondary,
              fontSize: tokens.font.size.sm,
              maxWidth: tokens.layout.authCard,
              pointerEvents: "none",
            }}
          >
            {presetFilteredLayers.length === 0 ? (
              <>
                <strong style={{ color: tokens.color.ink.primary }}>
                  No layers match this scene preset.
                </strong>
                <p
                  style={{
                    margin: `${tokens.space[2]}px 0 0`,
                    marginBottom: 0,
                  }}
                >
                  This preset hides geospatial basemaps and imagery. Choose{" "}
                  <strong>Combined</strong> or <strong>Map only</strong> below.
                </p>
              </>
            ) : (
              <>
                <strong style={{ color: tokens.color.ink.primary }}>
                  All layers are hidden.
                </strong>
                <p
                  style={{
                    margin: `${tokens.space[2]}px 0 0`,
                    marginBottom: 0,
                  }}
                >
                  Use <strong>Show</strong> in the project layer list on the
                  left.
                </p>
              </>
            )}
          </div>
        ) : null}
        {/* {engine === "openlayers" ? (
          <div
            role="status"
            style={{
              position: "absolute",
              top: tokens.space[3],
              left: tokens.space[3],
              zIndex: tokens.zIndex.panel,
              maxWidth: 420,
              padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
              borderRadius: tokens.radius.md,
              border: tokens.border.thin,
              background: tokens.color.surface[1],
              color: tokens.color.ink.primary,
              fontSize: tokens.font.size.xs,
              boxShadow: tokens.elevation[2],
              pointerEvents: "none",
            }}
          >
            Canvas 2D mode (no GPU / WebGL). Basemap + COG tiles still render;
            3D engines are unavailable on this VM.
          </div>
        ) : null} */}
        <div ref={mapContainer} style={{ width: "100%", height: "100%" }} />
        {engineMountError ? (
          <div
            role="alert"
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: tokens.space[6],
              background: "rgba(15, 23, 20, 0.82)",
              color: "#F5F7F6",
              textAlign: "center",
              pointerEvents: "none",
            }}
          >
            <div style={{ maxWidth: 480 }}>
              <strong style={{ display: "block", marginBottom: tokens.space[2] }}>
                Map did not start
              </strong>
              <p style={{ margin: 0, opacity: 0.9 }}>{engineMountError}</p>
              <p style={{ margin: `${tokens.space[3]}px 0 0`, fontSize: 12, opacity: 0.75 }}>
                Open DevTools → Console and look for{" "}
                <code>[TwinShell]</code>, <code>[MapLibreAdapter]</code>, or{" "}
                <code>[TwinRoute]</code> errors.
              </p>
            </div>
          </div>
        ) : null}
        {isPanoramaTourEnabled() &&
        engine === "giro3d" &&
        giro3dAdapter &&
        !panoramaBubbleView.layerId
          ? catalog
              .filter(
                (layer) =>
                  isPanoramaBubbleLayer(layer) && visibleIds.has(layer.id),
              )
              .map((layer) => {
                const manifest = panoramaStationManifestsByLayerId[layer.id];
                if (!manifest || manifest.stations.length === 0) return null;
                const instance = giro3dAdapter.getInstance();
                if (!instance) return null;
                return (
                  <Suspense fallback={null} key={`panorama-markers-${layer.id}`}>
                    <StationMarkers
                      stations={manifest.stations}
                      activeStationId={null}
                      onStationClick={(stationId) =>
                        onPanoramaStationMarkerClick(layer.id, stationId)
                      }
                      giro3dInstance={instance}
                    />
                  </Suspense>
                );
              })
          : null}
        {isPanoramaTourEnabled() && activePanoramaLayer?.endpoints.stations ? (
          <div
            data-testid="panorama-bubble-canvas-overlay"
            style={{
              position: "absolute",
              inset: 0,
              zIndex: tokens.zIndex.dock + 5,
              pointerEvents: "auto",
            }}
          >
            <Suspense fallback={null}>
            <PanoramaBubbleView
              stationsUrl={activePanoramaLayer.endpoints.stations}
              activeStationId={panoramaBubbleView.activeStationId}
              initialYawRad={panoramaInitialYawRad ?? undefined}
              title={activePanoramaLayer.label ?? activePanoramaLayer.id}
              onClose={() => {
                // Camera continuity: restore the 3D camera snapshot so the
                // user lands back in the exact view they entered from. We
                // intentionally clear the saved state AFTER restoring so a
                // subsequent re-entry re-captures the (now-restored) yaw
                // rather than the bubble's drifted look direction.
                const saved = savedCameraStateRef.current;
                if (saved) {
                  const instance = giro3dAdapter?.getInstance() ?? null;
                  restoreGiro3DCameraState(instance, saved);
                }
                savedCameraStateRef.current = null;
                setPanoramaInitialYawRad(null);
                setPanoramaBubbleView({ layerId: null, activeStationId: null });
              }}
              onStationChange={(stationId) => {
                const layerId = panoramaBubbleView.layerId;
                if (!layerId) return;
                setPanoramaBubbleView({ activeStationId: stationId });
                setSceneSelection(
                  selectPanoramaBubbleStation(layerId, stationId, {
                    source: "engine-pick",
                  }),
                );
              }}
              onSurfacePick={(worldSurvey) => {
                onPanoramaSurfacePick(worldSurvey);
              }}
              className="panorama-bubble-view"
            />
            </Suspense>
          </div>
        ) : null}
        {engine === "giro3d" && visibleThreeDModelLayers.length === 0 ? (
          <EmptyScene3DState />
        ) : null}
        {/* Default project mode keeps the canvas clear: layer-family tools
            belong in the right rail. The old floating engine selector,
            analysis strip, and viewer settings remain available for
            debugging via `?legacy-toolbar=1`. */}
        {legacyToolbarFlag ? (
          <>
            <AnalysisToolbar
              engine={engine}
              onOpenSettings={() => setSettingsOpen(true)}
            />
            {settingsOpen ? (
              <ViewerSettingsPanel
                engine={engine}
                onClose={() => setSettingsOpen(false)}
              />
            ) : null}
          </>
        ) : null}
        {/* WP-16 — `<SemanticPropertyCard />` overlaps the right-rail
            `<ToolCardDock />` (both anchor top-right). Per masterplan §6 +
            §7, the dock is the canonical surface for layer-context tools;
            this floating card stays available behind `?legacy-toolbar=1`
            for the developer fallback path while a future WP folds its
            content into the dock's per-layer cards. */}
        {legacyToolbarFlag ? (
          <SemanticPropertyCard
            node={selectedSceneNode}
            layers={catalog}
            onFlyToLayer={onFlyToLayer}
            onIsolateLayer={onIsolateLayer}
            onClose={() => setSelectedSceneNode(null)}
          />
        ) : null}
        { (engine === "maplibre" || engine === "deckgl" || engine === "openlayers" || engine === "giro3d") ? (
          <BasemapSwitcher
            container={mapContainer.current}
            onBasemapChange={(id) => { void giro3dAdapter?.setBasemap?.(id); }}
          />
        ) : null}
        {legacyToolbarFlag && (engine === "maplibre" || engine === "deckgl") ? (
          <SavedViewsControl
            container={mapContainer.current}
            scope={gtwinId}
          />
        ) : null}

        {/* WP-16 — `<Giro3DToolbar />` (~82 KB legacy 3D toolbar) is
            replaced for the gTwin-first workflow by the right-rail
            `<ToolCardDock />` cards
            (`<ThreeDModelToolCard />` + `<SectionAndClipToolCard />` +
            `<PointCloudToolCard />`, WP-5 / WP-6). Stays available behind
            `?legacy-toolbar=1` for the developer fallback path
            (`/demo/raster-cog`, raw 3D demos). */}
        {legacyToolbarFlag && engine === "giro3d" ? (
          <Giro3DToolbar
            adapter={giro3dAdapter}
            sceneTree={sceneTree}
            selectedSceneNode={selectedSceneNode}
            onSceneNodeSelected={setSelectedSceneNode}
          />
        ) : null}

        {/* 3D-specific controls for cesium/three engines (legacy/debug path). */}
        {legacyToolbarFlag && (engine === "cesium" || engine === "three") ? (
          <ThreeDControls
            engine={engine}
            host={hostRef.current}
            layers={catalog}
          />
        ) : null}

        {/* WP-6 wiring — full 3D model overlays mount when the project
            carries a renderable BIM / 3D Tiles / glTF layer, regardless
            of engine. `AppShell` only enables these for the legacy
            cesium engine; under the Twin route Giro3D powers the same
            scene, so we activate the toolset whenever an applicable
            layer exists in the project. */}
        {isThreeDPresent ? (
          <>
            {/* Universal scene-wide tools — the section/clip/render-mode
                controls are now in the right-rail `SectionAndClipToolCard`.
                Keep the floating AnalysisStrip behind the legacy flag so
                automation tests retain access. */}
            {legacyToolbarFlag ? (
              <AnalysisStrip adapter={analysisStripAdapter} />
            ) : null}
            {/* `<ThreeDToolbar>` and `<ThreeDCompass>` were rendered as
                top-right floating overlays. With the redesigned layout the
                same camera-mode + reset-view controls now live in the
                canvas-top `<CanvasToolbarStrip>` / left-rail card actions,
                so the legacy floating cluster duplicated those tools and
                overlapped the `<LorBreadcrumb>` + `<PlateauZoomGateChip>`.
                Gate the legacy cluster behind `?legacy-toolbar=1` so
                automation tests still have access while the new layout
                stays clean. */}
            {legacyToolbarFlag ? (
              <>
                <ThreeDToolbar
                  activeMode={threeDCameraMode}
                  onModeChange={setThreeDCameraMode}
                  onResetView={handleResetView}
                  onLookAtSelected={handleResetView}
                  disableLookAtSelected={!selection}
                />
                <ThreeDCompass
                  headingDeg={camera?.hpr?.[0] ?? mapBearingDeg ?? 0}
                  tiltDeg={camera?.hpr?.[1] ?? 0}
                  onResetNorth={handleResetNorth}
                />
              </>
            ) : null}
            {legacyToolbarFlag ? (
              <ThreeDControlsPanel
                hasSelection={Boolean(selection)}
                isolated={threeDControlState.isolated}
                hidden={threeDControlState.hidden}
                ghosted={threeDControlState.ghosted}
                sectionPlaneEnabled={threeDControlState.sectionPlaneEnabled}
                clipBoxEnabled={threeDControlState.clipBoxEnabled}
                exploded={threeDExplodeFactor}
                lightingEnabled={threeDControlState.lightingEnabled}
                shadowsEnabled={threeDControlState.shadowsEnabled}
                sunHour={threeDControlState.sunHour}
                sectionOffset={threeDControlState.sectionOffset}
                clipOffset={threeDControlState.clipOffset}
                materialState={threeDControlState.materialState}
                canvasTone={effectiveThreeDCanvasTone}
                canvasToneDisabled={!toneAvailable}
                onToggleIsolate={handleControlsToggleIsolate}
                onToggleHidden={handleControlsToggleHidden}
                onToggleGhosted={handleControlsToggleGhosted}
                onToggleSectionPlane={handleControlsToggleSection}
                onToggleClipBox={handleControlsToggleClipBox}
                onChangeExploded={setThreeDExplodeFactor}
                onToggleLighting={() =>
                  setThreeDControlState((previous) => ({
                    ...previous,
                    lightingEnabled: !previous.lightingEnabled,
                  }))
                }
                onToggleShadows={() =>
                  setThreeDControlState((previous) => ({
                    ...previous,
                    shadowsEnabled: !previous.shadowsEnabled,
                  }))
                }
                onChangeSunHour={(value) =>
                  setThreeDControlState((previous) => ({
                    ...previous,
                    sunHour: value,
                  }))
                }
                onChangeSectionOffset={handleControlsChangeSectionOffset}
                onChangeClipOffset={handleControlsChangeClipOffset}
                onToggleCanvasTone={() =>
                  setThreeDCanvasTone(
                    effectiveThreeDCanvasTone === "neutral-dark"
                      ? "light"
                      : "neutral-dark",
                  )
                }
              />
            ) : null}
            <MissingGeometryDiagnostics
              missingCount={missingGeometryCount}
              onOpenRemap={() => setAddLayerOpen(true)}
            />
            {/* LoR4+ Floor Selector — left edge of viewport, vertically centered */}
            {engine === "giro3d" && effectiveLoR >= 4 && scopeStoreys.length > 0 && (
              <FloorSelector
                activeLoR={effectiveLoR}
                storeys={scopeStoreys}
                activeStorey={activeStorey}
                onSelectStorey={setActiveStorey}
              />
            )}
            {!isEmbeddedEstate && engine === "giro3d" && giro3dAdapter ? (
              <aside
                aria-label="Scene status card"
                data-dockable-panel="status-card"
                style={{
                  position: "absolute",
                  top: tokens.space[3],
                  right: tokens.space[3],
                  zIndex: tokens.zIndex.panel,
                  width: tokens.width.popoverSm,
                  maxWidth: `calc(100vw - ${tokens.space[6]}px)`,
                  background: `${tokens.color.surface[1]}cc`,
                  backdropFilter: `blur(${tokens.space[2]}px)`,
                  border: tokens.border.thin,
                  borderRadius: tokens.radius.md,
                  boxShadow: tokens.elevation[1],
                  display: "flex",
                  flexDirection: "column",
                  pointerEvents: "auto",
                  overflow: "hidden",
                  ...(statusDock.state.slot === "floating" && statusDock.state.position
                    ? statusDock.panelStyle
                    : {}),
                }}
              >
                <DragHandle
                  onPointerDown={statusDock.onDragHandlePointerDown}
                  onDoubleClick={statusDock.snapHome}
                  minimized={statusDock.state.minimized}
                  onToggleMinimized={statusDock.toggleMinimized}
                  label="Status"
                  data-testid="drag-handle-status"
                />
                {statusDock.state.minimized ? null : (
                <div style={{ padding: tokens.space[2], display: "flex", flexDirection: "column", gap: tokens.space[2] }}>
                  {/* Row 1: Navigation breadcrumb + scope controls */}
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: tokens.space[1],
                    }}
                  >
                    <LorBreadcrumb
                      state={{ ...lorState, breadcrumb: extendedBreadcrumb }}
                      onHome={handleResetView}
                    />
                    <DisciplineToggle
                      activeLoR={effectiveLoR}
                      activeDisciplines={activeDisciplines}
                      onToggle={handleDisciplineToggle}
                    />
                    <LoRScopeButtons
                      currentLoR={lorState.level}
                      onMicroscope={handleMicroscope}
                      onTelescope={handleTelescope}
                      canDrillInterior={
                        !activeScopeState && findFirstDrillableLoR3(catalog) !== null
                      }
                    />
                  </div>

                  {/* Row 2: Camera distance */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: tokens.space[2],
                      fontSize: tokens.font.size.xs,
                      color: tokens.color.ink.secondary,
                    }}
                  >
                    <span style={{ fontWeight: tokens.font.weight.medium, color: tokens.color.ink.primary }}>
                      Camera
                    </span>
                    <span style={{ fontFamily: tokens.font.family.mono }}>
                      {plateauCameraDistanceM != null
                        ? plateauCameraDistanceM >= 1000
                          ? `${(plateauCameraDistanceM / 1000).toFixed(1)} km`
                          : `${Math.round(plateauCameraDistanceM)} m`
                        : "—"}
                    </span>
                  </div>

                  {/* Row 3: Tile streaming progress (hidden when idle) */}
                  <TileLoadChip adapter={giro3dAdapter} />

                  {/* Row 4: Street detail / zoom gate status */}
                  <PlateauZoomGateChip
                    distanceM={plateauCameraDistanceM}
                    streetDetailActive={plateauStreetDetail}
                    stableMountEnabled={PLATEAU_FEATURES.stableMountPolicy}
                    deferredCount={deferredDetailLayers.length}
                    mountedDetailCount={mountedDetailLayerCount}
                    layerTileProgress={plateauLayerTileProgress}
                  />

                  {/* Row 5: Deferred detail layer count */}
                  <PlateauDetailDeferredChip
                    deferredCount={deferredDetailLayers.length}
                    streetDetailActive={plateauStreetDetail}
                  />
                </div>
                )}
              </aside>
            ) : null}
          </>
        ) : null}

        {/* Measurement visual overlay (points, lines, cursor) */}
        {legacyToolbarFlag ? (
          <MeasurementOverlay
            container={mapContainer.current}
            points={measureVertices}
          />
        ) : null}

        {/* Pixel inspector: live Spectral hover + click sample for the
            selected raster. `mapHostEl` tracks mount generation so the
            inspector re-attaches after engine swaps (ref alone does not
            re-render). */}
        <PixelInspector
          container={mapHostEl}
          hoverLonLat={cursorCoords}
          cogUrl={pixelInspectorLayer?.cogUrl ?? null}
          layerLabel={pixelInspectorLayer?.label}
          elevationLike={pixelInspectorLayer?.elevationLike}
        />

        {/* Draggable plugin dock — legacy/debug path; right rail owns project tools. */}
        {legacyToolbarFlag && engine !== "giro3d" ? <PluginDock /> : null}

        {/* Bottom status strip: cursor coords, zoom, CRS, tile health. */}
        {!isEmbeddedEstate ? (
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: tokens.zIndex.panel,
            padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
            background: tokens.color.overlay.scrim,
            color: tokens.color.ink.primary,
            backdropFilter: `blur(${tokens.space[1]}px)`,
            display: "flex",
            alignItems: "center",
            gap: tokens.space[3],
            fontSize: tokens.font.size.sm,
          }}
        >
          <span
            style={{
              fontFamily: tokens.font.family.mono,
              fontSize: tokens.font.size.xs,
            }}
          >
            {cursorCoords
              ? `${cursorCoords[0].toFixed(6)}, ${cursorCoords[1].toFixed(6)}`
              : "—, —"}
          </span>
          <span
            style={{
              fontSize: tokens.font.size.xs,
              color: tokens.color.ink.secondary,
            }}
          >
            Zoom {camera?.zoom?.toFixed(1) ?? "—"}
          </span>
          <span
            style={{
              fontSize: tokens.font.size.xs,
              color: tokens.color.ink.secondary,
            }}
          >
            {catalog[0] ? `${catalog[0].crs} · ${layerCoordinateMode(catalog[0])}` : "EPSG:4326"}
          </span>
          {isThreeDPresent ? (
            <ThreeDPerformanceChip
              snapshot={threeDPerformance}
              pointCloudMode={catalog.some((layer) => layer.kind === "pointcloud")}
            />
          ) : null}
        </div>
        ) : null}

        <MissingCrsDialog
          open={missingCrsLayer !== null}
          layerLabel={missingCrsLayer?.label ?? missingCrsLayer?.id ?? ''}
          onRectify={handleMissingCrsRectify}
          onFakeCoordinates={handleMissingCrsFakeCoordinates}
          onDismiss={handleMissingCrsDismiss}
        />
        {addLayerOpen ? (
          <AddLayerToGTwinDialog
            gtwinId={gtwinId}
            onAdd={async (id, input) => {
              markDirty();
              markSaving();
              try {
                const item = await gtwinClient.scene.items.add(id, input);
                return item;
              } catch (err) {
                markFailed(err instanceof Error ? err.message : String(err));
                recordRealBackendError(err);
                throw err;
              }
            }}
            onSuccess={(item, input) => onSceneItemAdded(item, input.slot)}
            onClose={() => setAddLayerOpen(false)}
          />
        ) : null}
        {layerPropertiesLayer && layerPropertiesDiagnostics ? (
          // The panel is a self-contained floating frame (draggable + resizable);
          // it owns its own fixed positioning, so it is rendered directly.
          <LayerPropertiesPanel
            diagnostics={layerPropertiesDiagnostics}
            layer={layerPropertiesLayer}
            onClose={closeLayerProperties}
            coordinateOverride={layerPropertiesOverride}
            onApplyCoordinateOverride={async (patch) => {
              let vectorSessionFeaturesUrl: string | null = null;
              if (layerPropertiesLayer.kind === 'vector' && patch.sourceCrs) {
                const response = await fetch('/api/v1/vector-crs/sessions', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    layer_id: layerPropertiesLayer.id,
                    source_crs: patch.sourceCrs,
                  }),
                });
                if (!response.ok) {
                  throw new Error(`Vector CRS session failed (HTTP ${response.status})`);
                }
                const session = (await response.json()) as {
                  endpoints?: { features?: string };
                };
                vectorSessionFeaturesUrl = session.endpoints?.features ?? null;
              }
              const effectivePatch = { ...patch, vectorSessionFeaturesUrl };
              setLayerCoordinateOverride(layerPropertiesLayer.id, effectivePatch);
              const host = hostRef.current;
              if (!host || !visibleIds.has(layerPropertiesLayer.id)) return;
              try {
                await host.removeLayer(layerPropertiesLayer.id);
                const current = useViewerStore.getState().catalog.find(
                  (candidate) => candidate.id === layerPropertiesLayer.id,
                );
                const override = useViewerStore.getState().layerCoordinateOverrides[layerPropertiesLayer.id];
                if (!current) return;
                const effectiveLayer = effectiveLayerForSession(current, override);
                await host.addLayer(effectiveLayer);
              } catch (error) {
                console.warn('[TwinShell] coordinate override remount failed:', error);
              }
            }}
            onClearCoordinateOverride={async () => {
              clearLayerCoordinateOverride(layerPropertiesLayer.id);
              const host = hostRef.current;
              if (!host || !visibleIds.has(layerPropertiesLayer.id)) return;
              try {
                await host.removeLayer(layerPropertiesLayer.id);
                await host.addLayer(layerPropertiesLayer);
              } catch (error) {
                console.warn('[TwinShell] coordinate override reset failed:', error);
              }
            }}
            onPmtilesStyleChange={(patch) => {
              const adapter = giro3dAdapterRef.current as any;
              if (typeof adapter?.setNativeVectorStyle === 'function') {
                adapter.setNativeVectorStyle(layerPropertiesLayer.id, patch);
              }
            }}
          />
        ) : null}
            </div>
          }
        />
      </main>

      {/* Right rail — WP-12 ToolCardDock. Always present so the dock
          can show its empty state when nothing is selected; the
          dispatcher mounts at most one Card at a time per the
          single-drawer rule.
          WP-15 — `aria-label` so axe's `landmark-unique` rule doesn't
          collide with the left-rail `<aside>` above (both default to
          the implicit `complementary` role). */}
      {/* Right rail collapse/expand tab */}
      {showRightRail && !rightIsFloating && (
        <button
          type="button"
          aria-label={rightCollapsed ? "Expand tools panel" : "Collapse tools panel"}
          onClick={() => setRightCollapsed((v) => !v)}
          style={{
            position: "fixed",
            top: "50%",
            right: effectiveRightVisible ? (rightPanelWidth - 4) : 0,
            transform: "translateY(-50%)",
            zIndex: tokens.zIndex.dock + 1,
            width: 20,
            height: 48,
            borderRadius: `${tokens.radius.sm}px 0 0 ${tokens.radius.sm}px`,
            border: tokens.border.thin,
            borderRight: "none",
            background: tokens.color.surface[2],
            color: tokens.color.ink.secondary,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
            transition: `right ${tokens.motion.duration.normal}ms ${tokens.motion.easing.standard}`,
          }}
        >
          <Icon name={rightCollapsed ? "arrow_back" : "chevron_right"} size={16} decorative />
        </button>
      )}

      {/* Right rail panel */}
      {(effectiveRightVisible || rightIsFloating) && (
      <aside
        aria-label="Layer family tools"
        data-dockable-panel="right-rail"
        style={{
          ...(rightIsFloating
            ? {
                ...rightDock.panelStyle,
                background: tokens.color.surface[1],
                border: tokens.border.thin,
                borderRadius: tokens.radius.md,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }
            : {
                position: "relative",
                gridColumn: layoutTier === 'mobile' ? "1" : "3",
                gridRow: "2",
                borderLeft: tokens.border.thin,
                background: tokens.color.surface[1],
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                minWidth: 200,
              }),
        }}
      >
        {!rightIsFloating ? (
          <div
            aria-hidden
            data-testid="right-rail-resize-handle"
            onPointerDown={onRightResizePointerDown}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: 6,
              height: "100%",
              cursor: "col-resize",
              zIndex: tokens.zIndex.dock + 2,
              background: "transparent",
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = (tokens.color.accent?.primary ?? tokens.color.ink.secondary) + "44"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = "transparent"; }}
          />
        ) : null}
        <DragHandle
          onPointerDown={rightDock.onDragHandlePointerDown}
          onDoubleClick={rightDock.snapHome}
          minimized={rightDock.state.minimized}
          onToggleMinimized={rightDock.toggleMinimized}
          label="Tools"
          data-testid="drag-handle-right"
        />
        {rightDock.state.minimized ? null : (
        <>
        {showTerrainViewSwitch ? (
          <div
            style={{
              padding: tokens.space[3],
              borderBottom: tokens.border.thin,
              background: tokens.color.surface[1],
            }}
            data-testid="terrain-view-switch-rail"
          >
            <TerrainViewSwitch
              current={engine}
              onChange={onTerrainViewChange}
              disabled={engineSwapPending}
            />
          </div>
        ) : null}
        {availableModes.length === 2 ? (
          <div
            style={{
              padding: tokens.space[3],
              borderBottom: tokens.border.thin,
              background: tokens.color.surface[1],
            }}
            data-testid="point-cloud-mode-switch-rail"
          >
            <PointCloudModeSwitch
              availableModes={availableModes}
              current={currentMode}
              onChange={onSceneModeChange}
              disabled={engineSwapPending}
              localEngineering={localEngineering}
            />
          </div>
        ) : null}
        {/* Q4+Q5+M10: Selection property panel for PLATEAU building attributes */}
        <SelectionPropertyPanel
          selection={propertyPanelSelection}
          selectionCount={propertyPanelSelection ? 1 : selectionCount}
          onClose={handleCloseSelectionPanel}
        />
        <ToolCardDock
          container={mapContainer.current}
          layers={catalog}
          coordinate={cursorCoordsForDock}
          scaleDenominator={mapScaleDenominator}
          bearingDeg={mapBearingDeg}
          onResetView={handleResetView}
          onResetNorth={handleResetNorth}
          onOpenGeocoder={handleOpenGeocoder}
          onShareView={handleShareView}
          pointCloudAdapter={giro3dAdapter}
          giro3dAdapter={giro3dAdapter}
          threeDModelAdapter={giro3dAdapter?.getThreeDModelAdapter() ?? null}
          engine={engine}
          onTerrainViewChange={onTerrainViewChange}
          onGeoreferenceCommit={persistGeoreferenceLayer}
          terrainViewSwitching={engineSwapPending}
        />
        </>
        )}
      </aside>
      )}
    </div>
  );
}

// ─── 3D Controls Panel ─────────────────────────────────────────────────────

interface ThreeDControlsProps {
  engine: EngineId;
  host: ViewerHost | null;
  layers: Layer[];
}

function ThreeDControls({ engine, host, layers }: ThreeDControlsProps) {
  const [sunEnabled, setSunEnabled] = useState(true);
  const [cameraMode, setCameraMode] = useState<"orbit" | "firstPerson">(
    "orbit",
  );

  const threeDLayers = layers.filter(
    (l) =>
      l.kind === "3dtiles" ||
      l.kind === "bim" ||
      l.kind === "terrain" ||
      l.kind === "pointcloud",
  );

  const totalInfo = useMemo(() => {
    let verts = 0;
    let faces = 0;
    for (const l of threeDLayers) {
      const adapters = (host as any)?._adapters ?? [];
      for (const adapter of adapters) {
        if (typeof adapter.getModelInfo === "function") {
          const info = adapter.getModelInfo(l.id);
          if (info) {
            verts += info.vertexCount;
            faces += info.faceCount;
          }
        }
      }
    }
    return { verts, faces };
  }, [threeDLayers, host]);

  return (
    <div
      style={{
        position: "absolute",
        top: tokens.space[3],
        right: tokens.space[3],
        zIndex: tokens.zIndex.panel,
        background: tokens.color.surface[1],
        border: tokens.border.thin,
        borderRadius: tokens.radius.md,
        padding: tokens.space[3],
        minWidth: tokens.layout.popoverXs,
        fontSize: tokens.font.size.sm,
        color: tokens.color.ink.primary,
        backdropFilter: `blur(${tokens.space[2]}px)`,
      }}
    >
      <strong
        style={{
          fontSize: tokens.font.size.sm,
          marginBottom: tokens.space[2],
          display: "block",
        }}
      >
        3D Controls ({engine})
      </strong>

      {/* Camera mode toggle */}
      <div style={{ marginBottom: tokens.space[2] }}>
        <span
          style={{
            color: tokens.color.ink.secondary,
            fontSize: tokens.font.size.xs,
          }}
        >
          Camera:
        </span>
        <div
          style={{
            display: "flex",
            gap: tokens.space[1],
            marginTop: tokens.space[1],
          }}
        >
          <button
            type="button"
            onClick={() => setCameraMode("orbit")}
            style={{
              minHeight: tokens.density.touchMin,
              padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
              border: tokens.border.thin,
              borderRadius: tokens.radius.input,
              background:
                cameraMode === "orbit"
                  ? tokens.color.accent.primary
                  : tokens.color.surface[0],
              color:
                cameraMode === "orbit"
                  ? tokens.color.accent.on
                  : tokens.color.ink.primary,
              cursor: "pointer",
              fontSize: tokens.font.size.xs,
            }}
          >
            Orbit
          </button>
          <button
            type="button"
            onClick={() => setCameraMode("firstPerson")}
            style={{
              minHeight: tokens.density.touchMin,
              padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
              border: tokens.border.thin,
              borderRadius: tokens.radius.input,
              background:
                cameraMode === "firstPerson"
                  ? tokens.color.accent.primary
                  : tokens.color.surface[0],
              color:
                cameraMode === "firstPerson"
                  ? tokens.color.accent.on
                  : tokens.color.ink.primary,
              cursor: "pointer",
              fontSize: tokens.font.size.xs,
            }}
          >
            First Person
          </button>
        </div>
      </div>

      {/* Sun/shadow toggle (Cesium only) */}
      {engine === "cesium" ? (
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: tokens.space[2],
            marginBottom: tokens.space[2],
            fontSize: tokens.font.size.xs,
            color: tokens.color.ink.secondary,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={sunEnabled}
            onChange={(e) => setSunEnabled(e.currentTarget.checked)}
          />
          Sun/Shadows
        </label>
      ) : null}

      {/* Model info summary */}
      {threeDLayers.length > 0 ? (
        <div
          style={{
            marginTop: tokens.space[2],
            padding: tokens.space[2],
            background: tokens.color.surface[0],
            borderRadius: tokens.radius.input,
            fontSize: tokens.font.size.xs,
            color: tokens.color.ink.secondary,
          }}
        >
          <div>{threeDLayers.length} 3D layer(s)</div>
          {totalInfo.verts > 0 ? (
            <div>
              {totalInfo.verts.toLocaleString()} vertices,{" "}
              {totalInfo.faces.toLocaleString()} faces
            </div>
          ) : null}
          <div style={{ marginTop: tokens.space[1] }}>
            Kinds: {[...new Set(threeDLayers.map((l) => l.kind))].join(", ")}
          </div>
        </div>
      ) : (
        <div
          style={{
            fontSize: tokens.font.size.xs,
            color: tokens.color.ink.muted,
          }}
        >
          No 3D layers loaded
        </div>
      )}
    </div>
  );
}

// ─── Add Layer Dialog ──────────────────────────────────────────────────────
//
// The legacy `AddLayerDialog` (in-memory `devPreview` writer) was removed in
// WP-1. The Twin route now uses `<AddLayerToGTwinDialog>` from
// `components/gtwin/`, which routes through `gtwinClient.scene.items.add(...)`
// so durable project state is the only path supported on `/t/:id`. The
// developer fallback survives only on `/demo/*` routes per masterplan §5.5.

const KIND_BADGE_VARIANTS: Record<string, ChipVariant> = {
  raster: "info",
  vector: "success",
  "3dtiles": "warning",
  bim: "tertiary",
  terrain: "neutral",
  pointcloud: "info",
};

const KIND_BADGE_ICONS: Record<string, IconName> = {
  raster: "terrain",
  vector: "polyline",
  "3dtiles": "view_in_ar",
  bim: "view_in_ar",
  terrain: "terrain",
  pointcloud: "blur_on",
};

function LayerKindBadge({ kind }: { kind: string }) {
  return (
    <Chip
      label={kind}
      icon={KIND_BADGE_ICONS[kind] ?? "layers"}
      variant={KIND_BADGE_VARIANTS[kind] ?? "neutral"}
      aria-label={`${kind} layer type`}
    />
  );
}

interface ProjectLayerListProps {
  layers: Layer[];
  slotMap: Record<string, string>;
  visibleIds: Set<string>;
  onSetVisible: (id: string, visible: boolean) => void;
  onRemove: (id: string) => void;
  onFlyTo: (layer: Layer) => void;
  onSetOpacity: (id: string, opacity: number) => void;
  onMove: (id: string, direction: -1 | 1) => void;
  onIsolate: (layer: Layer) => void;
  onShowDetails: (layer: Layer) => void;
  layerOpacity: Record<string, number>;
  onAddLayerClick: () => void;
  onSetRasterPaint: (id: string, prop: string, value: number) => void;
  rasterPaint: Record<string, Record<string, number>>;
  layerCrs: Record<string, Extract<import('@geosoft/viewer-sdk').ViewerEvent, { type: 'crsCorrectionApplied' }>>;
}

function ProjectLayerList({
  layers,
  slotMap,
  visibleIds,
  onSetVisible,
  onRemove,
  onFlyTo,
  onSetOpacity,
  onMove,
  onIsolate,
  onShowDetails,
  layerOpacity,
  onAddLayerClick,
  onSetRasterPaint,
  rasterPaint,
  layerCrs,
}: ProjectLayerListProps) {
  const [query, setQuery] = useState("");
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const groups = useMemo(
    () => buildCatalogGroups(layers, slotMap, query),
    [layers, query, slotMap],
  );
  const toggleGroup = (id: string) => {
    setCollapsedGroupIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const copyLayerLink = (layer: Layer) => {
    if (typeof window === "undefined" || typeof navigator === "undefined")
      return;
    const url = new URL(window.location.href);
    url.hash = `${url.hash.replace(/#$/, "")}${url.hash ? "&" : "#"}layer=${encodeURIComponent(layer.id)}`;
    void navigator.clipboard?.writeText(url.toString());
  };
  const copyLayerSource = (layer: Layer) => {
    if (typeof navigator === "undefined") return;
    const source =
      Object.values(layer.endpoints).find(
        (value): value is string => typeof value === "string",
      ) ?? layer.id;
    void navigator.clipboard?.writeText(source);
  };
  return (
    <section style={{ padding: tokens.space[3] }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: tokens.space[3],
        }}
      >
        <h2
          style={{
            margin: tokens.space[0],
            fontSize: tokens.font.size.lg,
            fontWeight: tokens.font.weight.semibold,
          }}
        >
          Project layers
        </h2>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: tokens.space[2],
          }}
        >
          <span
            style={{
              color: tokens.color.ink.secondary,
              fontSize: tokens.font.size.sm,
            }}
          >
            {layers.length}
          </span>
          <button
            type="button"
            onClick={onAddLayerClick}
            style={{
              minHeight: tokens.density.touchMin,
              padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
              border: tokens.border.thin,
              borderRadius: tokens.radius.input,
              background: tokens.color.accent.subtle,
              color: tokens.color.accent.primary,
              cursor: "pointer",
              fontSize: tokens.font.size.sm,
              fontWeight: tokens.font.weight.medium,
              display: "inline-flex",
              alignItems: "center",
              gap: tokens.space[1],
            }}
          >
            <Icon name="add" size={20} decorative />
            Add layer
          </button>
        </div>
      </div>

      <label
        style={{
          display: "grid",
          gap: tokens.space[1],
          marginBottom: tokens.space[3],
        }}
      >
        <span
          style={{
            color: tokens.color.ink.secondary,
            fontSize: tokens.font.size.xs,
          }}
        >
          Filter content catalog
        </span>
        <input
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          placeholder="Layer, slot, kind, source URL"
          style={{
            minHeight: tokens.density.touchMin,
            border: tokens.border.thin,
            borderRadius: tokens.radius.input,
            background: tokens.color.surface[0],
            color: tokens.color.ink.primary,
            paddingInline: tokens.space[2],
            fontSize: tokens.font.size.sm,
          }}
        />
      </label>

      {layers.length === 0 ? (
        <p
          style={{
            color: tokens.color.ink.secondary,
            fontSize: tokens.font.size.sm,
          }}
        >
          No layers in this project. Open another project or import a .gtwin
          file.
        </p>
      ) : groups.length === 0 ? (
        <p
          style={{
            color: tokens.color.ink.secondary,
            fontSize: tokens.font.size.sm,
          }}
        >
          No layers match this content catalog filter.
        </p>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: tokens.space[2],
          }}
        >
          {groups.map((group) => {
            const collapsed = collapsedGroupIds.has(group.id);
            return (
              <section
                key={group.id}
                style={{
                  border: tokens.border.thin,
                  borderRadius: tokens.radius.md,
                  background: tokens.color.surface[0],
                  overflow: "hidden",
                }}
              >
                <button
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  aria-expanded={!collapsed}
                  style={{
                    width: "100%",
                    minHeight: tokens.density.touchMin,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: tokens.space[2],
                    padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
                    border: 0,
                    borderBottom: collapsed ? 0 : tokens.border.thin,
                    background: tokens.color.surface[2],
                    color: tokens.color.ink.primary,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: tokens.space[2],
                      minWidth: 0,
                    }}
                  >
                    <Icon
                      name={collapsed ? "chevron_right" : "keyboard_arrow_up"}
                      size={20}
                      decorative
                    />
                    <span style={{ minWidth: 0 }}>
                      <strong
                        style={{
                          display: "block",
                          fontSize: tokens.font.size.sm,
                        }}
                      >
                        {group.label}
                      </strong>
                      <span
                        style={{
                          color: tokens.color.ink.secondary,
                          fontSize: tokens.font.size.xs,
                        }}
                      >
                        {group.layers.length} layer
                        {group.layers.length === 1 ? "" : "s"}
                      </span>
                    </span>
                  </span>
                </button>
                {!collapsed ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: tokens.space[2],
                      padding: tokens.space[2],
                    }}
                  >
                    {group.layers.map((layer) => {
                      const visible = visibleIds.has(layer.id);
                      const slot = slotMap[layer.id] ?? "project";
                      const opacity = layerOpacity[layer.id] ?? 1;
                      const idx = layers.findIndex(
                        (candidate) => candidate.id === layer.id,
                      );
                      const isFirst = idx === 0;
                      const isLast = idx === layers.length - 1;
                      return (
                        <article
                          key={layer.id}
                          style={{
                            padding: tokens.space[3],
                            border: tokens.border.thin,
                            borderRadius: tokens.radius.md,
                            background: tokens.color.surface[1],
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: tokens.space[2],
                            }}
                          >
                            <div style={{ minWidth: 0 }}>
                              <div
                                style={{
                                  fontSize: tokens.font.size.sm,
                                  fontWeight: tokens.font.weight.medium,
                                  color: tokens.color.ink.primary,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={layer.label ?? layer.id}
                              >
                                {layer.label ?? layer.id}
                              </div>
                              <div
                                style={{
                                  marginTop: tokens.space[1],
                                  fontSize: tokens.font.size.xs,
                                  color: tokens.color.ink.secondary,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: tokens.space[1],
                                  flexWrap: "wrap",
                                }}
                              >
                                <LayerKindBadge kind={layer.kind} />
                                <span>
                                  {slot} · {layer.crs}
                                </span>
                                <LayerCrsChip alignment={layerCrs[layer.id] ?? null} />
                                {layer.style?.readiness ? (
                                  <span>readiness available</span>
                                ) : null}
                              </div>
                            </div>
                            <Chip
                              label={visible ? "Shown" : "Hidden"}
                              icon={visible ? "visibility" : "visibility_off"}
                              variant={visible ? "success" : "neutral"}
                              aria-label={
                                visible
                                  ? `${layer.label ?? layer.id} is shown`
                                  : `${layer.label ?? layer.id} is hidden`
                              }
                            />
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: tokens.space[2],
                              marginTop: tokens.space[3],
                              flexWrap: "wrap",
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => onSetVisible(layer.id, !visible)}
                              style={{
                                minHeight: tokens.density.touchMin,
                                padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
                                border: tokens.border.thin,
                                borderRadius: tokens.radius.input,
                                background: tokens.color.surface[1],
                                color: tokens.color.ink.primary,
                                cursor: "pointer",
                              }}
                            >
                              {visible ? "Hide" : "Show"}
                            </button>
                            <button
                              type="button"
                              onClick={() => onFlyTo(layer)}
                              style={{
                                minHeight: tokens.density.touchMin,
                                padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
                                border: tokens.border.thin,
                                borderRadius: tokens.radius.input,
                                background: tokens.color.surface[1],
                                color: tokens.color.ink.primary,
                                cursor: "pointer",
                              }}
                            >
                              Fly to
                            </button>
                            <button
                              type="button"
                              onClick={() => onIsolate(layer)}
                              style={{
                                minHeight: tokens.density.touchMin,
                                padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
                                border: tokens.border.thin,
                                borderRadius: tokens.radius.input,
                                background: tokens.color.surface[2],
                                color: tokens.color.ink.primary,
                                cursor: "pointer",
                              }}
                            >
                              Isolate
                            </button>
                            <button
                              type="button"
                              onClick={() => onShowDetails(layer)}
                              style={{
                                minHeight: tokens.density.touchMin,
                                padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
                                border: tokens.border.thin,
                                borderRadius: tokens.radius.input,
                                background: tokens.color.surface[2],
                                color: tokens.color.ink.primary,
                                cursor: "pointer",
                              }}
                            >
                              Details
                            </button>
                            <button
                              type="button"
                              onClick={() => onMove(layer.id, -1)}
                              disabled={isFirst}
                              title="Move up (render under)"
                              style={{
                                minHeight: tokens.density.touchMin,
                                padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                                border: tokens.border.thin,
                                borderRadius: tokens.radius.input,
                                background: tokens.color.surface[1],
                                color: isFirst
                                  ? tokens.color.ink.muted
                                  : tokens.color.ink.primary,
                                cursor: isFirst ? "not-allowed" : "pointer",
                              }}
                            >
                              Up
                            </button>
                            <button
                              type="button"
                              onClick={() => onMove(layer.id, 1)}
                              disabled={isLast}
                              title="Move down (render over)"
                              style={{
                                minHeight: tokens.density.touchMin,
                                padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                                border: tokens.border.thin,
                                borderRadius: tokens.radius.input,
                                background: tokens.color.surface[1],
                                color: isLast
                                  ? tokens.color.ink.muted
                                  : tokens.color.ink.primary,
                                cursor: isLast ? "not-allowed" : "pointer",
                              }}
                            >
                              Down
                            </button>
                            <button
                              type="button"
                              onClick={() => onRemove(layer.id)}
                              style={{
                                minHeight: tokens.density.touchMin,
                                padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
                                border: tokens.border.thin,
                                borderRadius: tokens.radius.input,
                                background: tokens.color.surface[1],
                                color: tokens.color.semantic.danger,
                                cursor: "pointer",
                              }}
                            >
                              Remove
                            </button>
                            <button
                              type="button"
                              onClick={() => copyLayerLink(layer)}
                              style={{
                                minHeight: tokens.density.touchMin,
                                padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
                                border: tokens.border.thin,
                                borderRadius: tokens.radius.input,
                                background: tokens.color.surface[2],
                                color: tokens.color.ink.primary,
                                cursor: "pointer",
                              }}
                            >
                              Copy link
                            </button>
                            <button
                              type="button"
                              onClick={() => copyLayerSource(layer)}
                              style={{
                                minHeight: tokens.density.touchMin,
                                padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
                                border: tokens.border.thin,
                                borderRadius: tokens.radius.input,
                                background: tokens.color.surface[2],
                                color: tokens.color.ink.primary,
                                cursor: "pointer",
                              }}
                            >
                              Show source
                            </button>
                          </div>
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: tokens.space[2],
                              marginTop: tokens.space[3],
                              fontSize: tokens.font.size.xs,
                              color: tokens.color.ink.secondary,
                            }}
                          >
                            <span style={{ minWidth: tokens.space[7] }}>Opacity</span>
                            <input
                              type="range"
                              min={0}
                              max={1}
                              step={0.05}
                              value={opacity}
                              onChange={(e) =>
                                onSetOpacity(
                                  layer.id,
                                  Number(e.currentTarget.value),
                                )
                              }
                              style={{ flex: 1 }}
                              aria-label={`Opacity for layer ${layer.label ?? layer.id}`}
                            />
                            <span
                              style={{
                                minWidth: tokens.space[6],
                                textAlign: "right",
                                fontFamily: tokens.font.family.mono,
                              }}
                            >
                              {Math.round(opacity * 100)}%
                            </span>
                          </label>
                          {layer.kind === "raster" ? (
                            <>
                              {[
                                {
                                  prop: "raster-brightness-max",
                                  label: "Brightness",
                                  min: 0,
                                  max: 1,
                                  step: 0.05,
                                  def: 1,
                                },
                                {
                                  prop: "raster-contrast",
                                  label: "Contrast",
                                  min: -1,
                                  max: 1,
                                  step: 0.05,
                                  def: 0,
                                },
                                {
                                  prop: "raster-saturation",
                                  label: "Saturation",
                                  min: -1,
                                  max: 1,
                                  step: 0.05,
                                  def: 0,
                                },
                              ].map((ctrl) => {
                                const val =
                                  rasterPaint[layer.id]?.[ctrl.prop] ??
                                  ctrl.def;
                                return (
                                  <label
                                    key={ctrl.prop}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: tokens.space[2],
                                      marginTop: tokens.space[1],
                                      fontSize: tokens.font.size.xs,
                                      color: tokens.color.ink.secondary,
                                    }}
                                  >
                                    <span style={{ minWidth: tokens.space[7] }}>
                                      {ctrl.label}
                                    </span>
                                    <input
                                      type="range"
                                      min={ctrl.min}
                                      max={ctrl.max}
                                      step={ctrl.step}
                                      value={val}
                                      onChange={(e) =>
                                        onSetRasterPaint(
                                          layer.id,
                                          ctrl.prop,
                                          Number(e.currentTarget.value),
                                        )
                                      }
                                      style={{ flex: 1 }}
                                      aria-label={`${ctrl.label} for ${layer.label ?? layer.id}`}
                                    />
                                    <span
                                      style={{
                                        minWidth: tokens.space[6],
                                        textAlign: "right",
                                        fontFamily: tokens.font.family.mono,
                                      }}
                                    >
                                      {ctrl.prop === "raster-brightness-max"
                                        ? `${Math.round(val * 100)}%`
                                        : val.toFixed(2)}
                                    </span>
                                  </label>
                                );
                              })}
                            </>
                          ) : null}
                        </article>
                      );
                    })}
                  </div>
                ) : null}
              </section>
            );
          })}
        </div>
      )}
    </section>
  );
}

function PromoteRefsBanner({ gtwinId }: { readonly gtwinId: string }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (done) return null;

  const handlePromote = async () => {
    setBusy(true);
    try {
      const apiBase = (import.meta.env?.VITE_GTWIN_API_URL as string | undefined) || `${window.location.origin}/gtwin-api/`;
      const resp = await fetch(
        `${apiBase.replace(/\/+$/, '')}/api/v1/gtwins/${gtwinId}/scene/promote-refs`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } },
      );
      if (resp.ok) {
        setDone(true);
        window.location.reload();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="status"
      style={{
        margin: tokens.space[2],
        padding: tokens.space[3],
        borderRadius: tokens.radius.md,
        background: tokens.color.surface[2],
        border: tokens.border.thin,
        fontSize: tokens.font.size.xs,
        color: tokens.color.ink.secondary,
      }}
    >
      <p style={{ margin: 0 }}>
        This project loads data from external converter refs. Migrate to self-contained mode for reliable offline access.
      </p>
      <button
        type="button"
        onClick={handlePromote}
        disabled={busy}
        style={{
          marginTop: tokens.space[2],
          padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
          borderRadius: tokens.radius.input,
          border: tokens.border.thin,
          background: tokens.color.accent.primary,
          color: tokens.color.accent.on,
          cursor: busy ? 'wait' : 'pointer',
          fontSize: tokens.font.size.xs,
        }}
      >
        {busy ? 'Migrating...' : 'Migrate now'}
      </button>
    </div>
  );
}
